/**
 * "Who has access..." — unified access management dialog.
 *
 * Closes #22; the existing RoleAccessDialog / TeamAccessDialog /
 * MemberAccessDialog stay alongside until this is battle-tested.
 *
 * Phases (all complete):
 *   1. Read-only view.
 *   2. X-to-remove + Add picker.
 *   3. (THIS COMMIT) Native HTML5 drag-and-drop — drag a tag to a sidebar
 *      tab to reassign permission, drag to a trash drop target (visible
 *      only mid-drag) to remove. Mid-drag hover over a sidebar tab
 *      previews that tab's contents. Inherited tags can drag → become
 *      explicit at the new permission. On a file, the "No access" tab
 *      rejects drops (cursor: not-allowed).
 *
 * Tab note: the issue lists 4 tabs (admin / readwrite / read / hidden) but the
 * permission system has 4 levels including `readcopy` (Read-only — allows download/copy). Showing
 * 4 tabs would orphan any explicit `readcopy` row. We render 5 tabs to avoid
 * data loss; if the spec really wants 4, that's a UX merge decision (e.g.
 * fold `readcopy` into the "Read-only" tab) and would need data backfill so
 * it's deferred to a separate decision.
 */
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X, Loader2, Folder, File, Plus, Trash2, Undo2, Shield, ShieldAlert, UsersRound, GripVertical, Crown, SquarePen, Eye, EyeOff, Share2, ChevronDown, Check, Ban, Network, AlertTriangle, MoreHorizontal, Globe, Lock, Home, Mail, type LucideIcon } from 'lucide-react'
import { useTheme, getThemeClass, Tooltip, MiniappIcon, PDAppIcon, type GlyphIcon } from '@papan/core-ui/ui'
import { useIsDesktop, EmptyState } from '@papan/core-ui/ui'
import { useToast, useConfirm } from '@papan/core-ui/ui'
import { request, isSessionEndedError, errorText } from '@papan/api-client'
import { onSessionEnd } from '@papan/core-ui/ui/sessionEnded'
import { useRealtime } from '@papan/realtime/client'
import { OutsiderTab, AccessSettingsPopover } from '@papan/org-connections/ui'
import { disambiguateParentLabels, isMiniappFolder, appDisplayName, parseMiniappIcon, parseMiniappIconRev, createAccessBurst, type MiniappIconSpec } from '@papan/types'
import { useProjects } from './home/data'
import { LinkRow, InboundResharesSection, ViaShortcutSection, QrOverlay, avatarBg, initialFromEmail, nameFromEmail, linkAudience, AUDIENCE_LABEL, AUDIENCE_HINT, type LinkAudience } from '@papan/share-links/ui'
import { revokeShareLink, ownerRevokeShareLink, type ShareLinkWithUrl, type InboundReshareLink, type ViaShortcutLink } from '@papan/share-links/client'
import { AddPicker } from './WhoHasAccessDialog.AddPicker.js'
import { NarrowingReview, type Narrowing } from './WhoHasAccessDialog.NarrowingReview.js'
import type {
  AccessTag,
  MemberInfo,
  Permission,
  TagType,
  TeamInfo,
} from './WhoHasAccessDialog.types.js'
import { ROLE_LABELS } from './WhoHasAccessDialog.types.js'

interface WhoHasAccessDialogProps {
  folderId: string
  folderName: string
  folderPath?: string
  tenantId: string
  isFolder?: boolean
  /** Caller's own effective permission on the folder — passed to the
   *  Outsiders → Share flow so the permission picker can't offer a level
   *  above what the caller holds. */
  myPermission?: string
  /** True when this folder is a received cross-tenant `.shared` — its
   *  effective access is capped at the source grant (min(src_cap, placement)),
   *  so internal grants above `myPermission` are clamped. Gates the "capped"
   *  note; never set for native folders, where no such ceiling exists. */
  isReceivedShare?: boolean
  /** True when the row is part of a cross-tenant `.shared` mount —
   *  the artifact itself (folder OR file-target) or any descendant
   *  inside the mount. Folders inside the mount already get Manage &
   *  Edit via `isFolder=true`; this prop widens that to FILES inside
   *  a share too, since the whole subtree shares one management
   *  context. Native folders/files outside any mount leave this
   *  false — same-tenant file rows continue to hide admin. */
  inShareContext?: boolean
  /** Personal tenants have no roles/teams — hide the "People & teams" tab and
   *  default to Outsiders (who else can reach this: other drives it was shared
   *  to). The remaining tabs (Links, Outsiders, Mini App & AI) still
   *  apply: a personal drive can share links, share to another drive, and grant apps. */
  hidePeopleTeams?: boolean
  /** Open straight to a specific top tab (e.g. 'outsider' from the Shared-by-me
   *  table, whose recipients ARE outsider drives). Defaults to People & teams
   *  (or Outsiders when hidePeopleTeams). */
  initialViewMode?: 'people' | 'outsider' | 'share-links' | 'miniapp'
  onClose: () => void
}

// ─── Permission catalogue ────────────────────────────────────────────────────

const PERM_LABELS: Record<Permission, string> = {
  hidden: 'No access',
  readcopy: 'Read-only',
  readwrite: 'Read & Write',
  admin: 'Manage & Edit',
}

/** Key into the server's `inherited` map for a principal — role ids are bare,
 *  teams and users are prefixed. Shared by the 3-dot menu's "Inherit (X)" row
 *  and the drag target, which must agree on what a tag would fall back to. */
function inheritedKeyFor(tag: AccessTag): string {
  if (tag.type === 'role') return tag.targetId
  if (tag.type === 'team') return `group:${tag.targetId}`
  return `user:${tag.targetId}`
}

/** What this principal would be left with if its explicit grant went away —
 *  `null` when nothing inherits, i.e. removal really does mean removal. */
function inheritedPermissionFor(
  tag: AccessTag,
  inheritedRaw: Record<string, string>,
): Permission | null {
  if (tag.inherited) return tag.permission
  return (inheritedRaw[inheritedKeyFor(tag)] as Permission | undefined) ?? null
}

const PERM_DESCRIPTIONS: Record<Permission, string> = {
  admin: 'Lets people rename, delete, and edit contents — including granting access to others. Only the people listed under Manage can change access here.',
  readwrite: 'Lets people rename, delete, and edit contents. Cannot grant access to others.',
  readcopy: 'Lets people view and download a copy. Cannot edit.',
  hidden: 'People can see this folder but not the files and subfolders inside it, and they can\'t change who has access here — including their own access. If this folder/file itself sits inside another \'No Access\' folder, it is invisible.',
}

/** Label for the admin tier inside a received `.shared`. The TAP admin
 *  can always manage access for TAP members, but content actions are
 *  clamped by the source-org ceiling — so "Manage & Edit" overstates
 *  power when the cap is read-only. Mirror the ceiling in the label
 *  so the user reads the real power. Outside `.shared` or when the
 *  cap is unknown, return the canonical "Manage & Edit". */
function adminLabelFor(shareCap: Permission | null, isReceivedShare: boolean): string {
  if (!isReceivedShare || !shareCap) return PERM_LABELS.admin
  if (shareCap === 'readcopy') return 'Manage & Read'
  if (shareCap === 'readwrite') return PERM_LABELS.admin
  return PERM_LABELS.admin
}

/** Description for the admin tier. Inside a received `.shared` we surface
 *  the source ceiling so the TAP admin understands the cap — content
 *  actions above the ceiling are silently impossible without the source
 *  org granting more. */
function adminDescriptionFor(shareCap: Permission | null, isReceivedShare: boolean): string {
  if (!isReceivedShare || !shareCap) return PERM_DESCRIPTIONS.admin
  if (shareCap === 'readcopy') {
    return "Lets people manage who has access. The source organization only granted Read-only on this folder, so members can view and download — they can't edit until the source grants more access."
  }
  if (shareCap === 'readwrite') {
    return "Lets people manage who has access. The source organization granted Read & Write on this folder, so members can rename, delete, and edit — but can't go above that without the source granting more access."
  }
  return PERM_DESCRIPTIONS.admin
}

function labelFor(p: Permission | 'all', shareCap: Permission | null, isReceivedShare: boolean): string {
  if (p === 'all') return 'All Permissions'
  if (p === 'admin') return adminLabelFor(shareCap, isReceivedShare)
  return PERM_LABELS[p]
}

/** Icon + description for the synthetic "all" tab. Kept inline here so the
 *  per-permission icon/label maps stay strictly typed to `Permission`. */
const ALL_DESCRIPTION = 'Every grant on this folder, grouped by permission level. Add at any tier here, or use a card’s ⋯ menu to change or remove it.'

const PERM_ICONS: Record<Permission, LucideIcon> = {
  admin: Crown,         // ownership / management authority
  readwrite: SquarePen, // edit capability
  readcopy: Eye,        // read-only label (still allows download/copy)
  hidden: EyeOff,       // not visible
}

/** Tab order — top of sidebar (most permissive) to bottom. */
const TAB_ORDER: Permission[] = ['admin', 'readwrite', 'readcopy', 'hidden']

/** Files now offer the same tiers as folders, incl. `admin` (Manage & Edit) —
 *  admin on a file is grantable directly (it used to be folder-only).
 *
 *  NOTE TO REVIEWERS: `'hidden'` ("No access") IS in this list on purpose, and
 *  that does NOT mean No-access is settable on a file. "No access" is
 *  FOLDER-ONLY by design (it works by overlaying an empty folder to hide its
 *  contents — there is no equivalent for a file). The `'hidden'` tab exists so
 *  the dialog can DISPLAY who *inherits* No-access from a parent folder;
 *  SETTING it on a file is blocked by `isDropTabAllowed` / `isDropAllowedAt`
 *  (both reject `'hidden' && !isFolder`) and the "Add" button is suppressed on
 *  this tab for files. So: display = yes, set = no. Removing `'hidden'` here
 *  would break the inherited-No-access display — it is not a hardening. */
const FILE_TABS: Permission[] = ['admin', 'readwrite', 'readcopy', 'hidden']

/** "People & teams" / "Outsiders" / "Share links" view switch — a
 *  horizontal row that sits above the RIGHT pane. Phase 23 added the
 *  share-links view so owners can audit link principals alongside other
 *  grants. The share-links column data comes from
 *  `/api/share-links/principals?fileId=`. */
type ViewMode = 'people' | 'outsider' | 'share-links' | 'miniapp'

const VIEW_MODES: Array<{ key: ViewMode; icon: GlyphIcon; label: string }> = [
  { key: 'people', icon: UsersRound, label: 'People & teams' },
  { key: 'miniapp', icon: PDAppIcon, label: 'PDApp & AI' },
  { key: 'outsider', icon: Network, label: 'Outsiders' },
  { key: 'share-links', icon: Share2, label: 'Links' },
]

/**
 * Phone: the four view modes as ONE row with nothing hidden.
 *
 * This replaced a dropdown that opened a bottom sheet, which replaced a
 * scrolling tab strip. Each step was solving a real problem and the last one
 * over-corrected:
 *
 *   · The STRIP could not fit four labels in 390px. "People & teams" wrapped
 *     to three lines and "Links" was clipped off the right edge with no
 *     affordance saying it existed.
 *   · The SHEET fixed the fitting problem by hiding three of the four. That is
 *     a lot of ceremony for switching a view — a slide animation and a panel
 *     covering the content you are navigating within, opened from a control at
 *     the opposite end of the screen — and it made Outsiders and Links
 *     invisible until you thought to tap.
 *   · This shows all four AT ONCE and still fits, because only the ACTIVE one
 *     needs its label: the other three are their icons. One tap to switch, no
 *     menu, no animation, nothing hidden.
 *
 * The label is the accessible name on every button, so an icon-only tab is
 * still announced as "Outsiders" rather than as a graphic. That matters more
 * here than usual: three of the four are icon-only at any moment.
 *
 * `flex-1 min-w-0` + truncate on the active item and a fixed width on the
 * rest, so a narrow phone shortens the LABEL rather than pushing a tab off the
 * edge — which is the failure the strip had.
 */
function ViewModeTabs({ viewMode, onChange, hidePeople }: {
  viewMode: ViewMode
  onChange: (m: ViewMode) => void
  hidePeople: boolean
}) {
  const modes = VIEW_MODES.filter(m => !(hidePeople && m.key === 'people'))
  return (
    // Track DARKER than the dialog, active segment LIGHTER — the usual "the
    // selected one is raised" reading. Built the other way round first (a
    // lighter track with a darker active pill) it looked like a hole punched
    // in the strip rather than a selection.
    <div className="flex items-center gap-1 rounded-full themed-bg-primary p-1">
      {modes.map(({ key, icon: Icon, label }) => {
        const active = key === viewMode
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-label={label}
            aria-pressed={active}
            /* ANIMATED, and the animation is why the geometry is written this
               way rather than the obvious way.

               The obvious way is `flex-1` on the active tab and a fixed width
               on the rest, which is what this was: correct at rest and a hard
               reflow in between, because `flex: 1` <-> `w-11` is a change of
               flex-basis from a length to `auto` and there is nothing for CSS
               to interpolate. Every tab therefore keeps the SAME basis and
               differs only in `flex-grow`, which IS animatable — so the active
               pill expands and its neighbours contract as one continuous
               motion instead of snapping.

               The label needs the same treatment for the same reason:
               mounting it only when active pops it into place mid-slide. It is
               always rendered and animates its own `max-width` from 0, so it
               unfurls with the pill that carries it. `overflow-hidden` is what
               makes a max-width of 0 actually hide it. */
            style={{ flexGrow: active ? 1 : 0, flexBasis: '2.25rem' }}
            className={`flex items-center justify-center h-9 rounded-full overflow-hidden min-w-0
              transition-[flex-grow,background-color,color] duration-300 motion-reduce:transition-none
              [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
              active ? 'themed-bg-tertiary themed-text font-medium' : 'themed-text-muted'
            }`}
          >
            <Icon size={16} className="shrink-0" />
            <span
              className={`overflow-hidden whitespace-nowrap text-sm transition-all duration-300
                motion-reduce:transition-none [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
                active ? 'ml-2 max-w-[12rem] opacity-100' : 'ml-0 max-w-0 opacity-0'
              }`}
            >
              {label}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function ViewModeSwitch({ viewMode, onChange, hidePeople = false, thumbRef }: {
  viewMode: ViewMode
  onChange: (m: ViewMode) => void
  /** Personal tenants have no roles/teams — hide the "People & teams" tab. */
  hidePeople?: boolean
  /**
   * Where the indicator was when this strip last existed, owned by the DIALOG.
   *
   * On desktop this component is rendered INSIDE each view's branch, so
   * switching to Outsiders or Links swaps the branch and mounts a NEW
   * instance — with no previous position, and therefore nothing to slide
   * from. Measured, the indicator unmounted and reappeared already at its
   * destination; only People <-> PDApp animated, because those two share a
   * branch and keep the instance alive.
   *
   * A ref on the dialog outlives every branch swap, so a fresh strip starts
   * where the old one ended and the slide survives the remount. Cheaper and
   * far less risky than hoisting the strip out of the branches, which is where
   * it has to sit to line up with the pane beside the sidebar.
   */
  thumbRef?: React.MutableRefObject<{ left: number; width: number } | null>
}) {
  const modes = VIEW_MODES.filter(m => !(hidePeople && m.key === 'people'))

  /**
   * Sliding active indicator, MEASURED rather than computed.
   *
   * The AddPicker's tab underline gets away with pure arithmetic — every tab
   * there is `flex-1`, so the active one is at `index × 100%` of `1/n` and
   * nothing has to be read from the DOM. These tabs are content-width and
   * their labels differ ("Links" against "People & teams"), so there is no
   * expression for where the third one starts. Hence refs and `offsetLeft`.
   *
   * A PASSIVE effect, deliberately, not `useLayoutEffect`. A layout effect
   * runs before paint, so on a remount the inherited position would never
   * reach the screen and the transition would have no start value — the same
   * race that broke the phone sheets. Measuring after paint means the old
   * position is real, and the move to the new one animates.
   *
   * `ready` suppresses the transition for that first measurement only.
   * Without it the indicator SLIDES in from the left edge each time the dialog
   * opens, animating a change the reader never made.
   */
  const stripRef = useRef<HTMLDivElement>(null)
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(() => thumbRef?.current ?? null)
  // Inheriting a position means we can animate from it immediately; starting
  // cold means the first appearance must NOT animate, or the indicator slides
  // in from the left edge on every open.
  const ready = useRef(!!thumbRef?.current)

  useEffect(() => {
    const strip = stripRef.current
    if (!strip) return
    const measure = () => {
      const el = strip.querySelector<HTMLElement>(':scope > [data-active="true"]')
      if (!el) { setThumb(null); return }
      const next = { left: el.offsetLeft, width: el.offsetWidth }
      // A `display:none` strip measures 0/0. This component renders on phones
      // too (it is only `hidden md:flex`), so without this the phone pass
      // wrote {0,0} into the DIALOG-owned ref — poisoning the position a later
      // desktop-width render inherits, and making the indicator slide in from
      // the left edge at zero width on a phone-to-desktop resize.
      if (next.width === 0) return
      if (thumbRef) thumbRef.current = next
      // Bail when the geometry is unchanged. `measure` runs from a
      // ResizeObserver, so it fires far more often than the value moves, and a
      // fresh object identity each time is a re-render each time.
      setThumb(prev => (prev && prev.left === next.left && prev.width === next.width) ? prev : next)
    }
    measure()
    // Re-measure on resize AND on font load: the labels are text, so their
    // widths change when the webfont swaps in, and an indicator measured
    // against the fallback metrics sits visibly off its tab afterwards.
    // Observe the strip and the TABS — never the indicator. It is a child of
    // `strip`, its width is what this effect sets, and it transitions over
    // 300ms: observing it meant every animation frame re-entered `measure`,
    // re-rendered the strip ~18 times per switch, and risked the
    // "ResizeObserver loop completed with undelivered notifications" warning.
    const ro = new ResizeObserver(measure)
    ro.observe(strip)
    for (const child of Array.from(strip.children)) {
      if (child instanceof HTMLElement && child.dataset.active !== undefined) ro.observe(child)
    }
    return () => ro.disconnect()
  }, [viewMode, hidePeople, thumbRef])

  useEffect(() => {
    if (thumb) { const id = requestAnimationFrame(() => { ready.current = true }); return () => cancelAnimationFrame(id) }
    ready.current = false
  }, [thumb])

  return (
    <>
      {/* DESKTOP ONLY. The phone's scope control is rendered ONCE by the
          dialog itself, above every view — see the note at that call site.
          This one stays here because on desktop it must line up with the pane,
          to the RIGHT of the permission sidebar, which only the branch knows
          the geometry of. */}
      <div ref={stripRef} className="relative hidden md:flex items-center gap-1 mb-2 shrink-0">
        {/* The moving background. A SEPARATE element rather than a background
            on the active tab, because a background cannot travel between two
            elements — which is why the old version could only ever cut. */}
        {thumb && (
          <span
            aria-hidden
            className={`pointer-events-none absolute inset-y-0 rounded-md themed-bg-tertiary ${
              ready.current
                ? 'transition-[transform,width] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'
                : ''
            }`}
            style={{ transform: `translateX(${thumb.left}px)`, width: thumb.width }}
          />
        )}
        {modes.map(({ key, icon: Icon, label }) => (
          <button
            key={key}
            type="button"
            data-active={viewMode === key}
            aria-pressed={viewMode === key}
            onClick={() => onChange(key)}
            /* `font-medium` on EVERY tab, not just the active one. Bold text is
               wider, so switching used to change the tab's own width — and an
               indicator measured against a width that changes in the same
               frame animates toward a target that has already moved, while the
               tabs beside it shift. Uniform weight keeps the geometry still
               and lets colour carry the selection. */
            className={`relative z-10 shrink-0 whitespace-nowrap px-3 py-1 text-sm font-medium rounded-md flex items-center gap-1.5 transition-colors ${
              viewMode === key ? 'themed-text' : 'themed-text-muted hover:themed-text'
            }`}
          >
            <Icon size={14} className="shrink-0" /> {label}
          </button>
        ))}
      </div>
    </>
  )
}

// ─── Type colour palette ────────────────────────────────────────────────────
//
// Carried on the per-row TypeIcon (lucide Shield/UsersRound/User). The row
// itself is theme-neutral so a many-row list reads as a uniform table; the
// icon is the type signal.
//   role   → amber  Shield        (matches role-based permission affordances)
//   team   → blue   UsersRound    (matches TeamAccessDialog)
//   member → muted  User          (neutral; members are the most numerous)


// ─── Tag model ───────────────────────────────────────────────────────────────

interface PermissionsResponse {
  /** One of the drive's own folders (Library, Projects…): sharing the folder itself is
   *  off by default for everyone but the owner (owner ruling 2026-10-02). */
  structuralFolder?: string
  permissions: Array<{
    id: string
    targetType: 'user' | 'role' | 'group'
    targetId: string
    permission: Permission
  }>
  inherited?: Record<string, string>  // 'group:<id>' | '<roleName>' → permission
  inheritedFromPath?: string           // optional, where inheritance came from
  /** Never carries `'app'` — the server drops those rows (an app does not
   *  navigate the drive, so no folder is a corridor to it). */
  viaDescendant?: Array<{
    targetType: 'user' | 'role' | 'group'
    targetId: string
    permission: string
    descendantId: string
    descendantPath: string
  }>
  /** The caller holds `manage_folder_permission` at READ only: the server has
   *  already withheld every `hidden` ("No access") row, so the dialog drops
   *  that tab rather than rendering it permanently empty, and offers no edits. */
  accessMapReadOnly?: boolean
  /** Issue #71: server tells the dialog the folder's current Restricted state
   *  inline so the toggle header can render without a separate fetch. */
  restricted?: boolean
  /** Issue #106: optimistic-concurrency token for `restricted`. Echoed
   *  back on PATCH so the server can reject a stale write (409) instead
   *  of silently clobbering a concurrent admin's change. */
  restrictedVersion?: number
  /** Per-principal `allow_sharing` (mini-app share capability) keyed by
   *  `${type}:${id}` where type is `member` | `team` | `role`. Seeded
   *  into `sharingByTag` so every pill renders its real toggle state on
   *  open. See docs/MINI_APP_SECURITY.md and the
   *  `resolveAllowSharingByMember` helper. */
  allowSharing?: Record<string, boolean>
  /** Phase 4: for `.shared` mounts (root or any file/folder inside),
   *  the permission ceiling ORG granted via the share. Every tier above
   *  this gets the amber warning + tooltip — granting them is
   *  permitted but ineffective at access time. */
  shareCap?: Permission
  /** App-principal grants — a `.miniapp` folder granted access to this
   *  file/folder. Surfaced in the "Mini App & AI" view. `name` has the
   *  `.miniapp` suffix stripped; `path` is the app folder's full path. */
  apps?: AppGrant[]
  /** App grants inherited from an ancestor folder — read-only here. */
  inheritedApps?: AppGrant[]
  /** The inherited lists as they will be once this folder's Restricted flag is
   *  flipped — computed by the same resolver, so a toggle shows the new list at
   *  once; the refetch after the PATCH confirms it. Native folders only. */
  whenRestrictedToggled?: {
    inherited: Record<string, string>
    inheritedApps?: AppGrant[]
    /** Who transits this folder via a descendant grant once the flag is flipped. */
    viaDescendant?: PermissionsResponse['viaDescendant']
  }
}

/** One app-principal (mini-app) grant on this file/folder. `permId` is absent
 *  for inherited grants (they live on an ancestor and aren't editable here). */
interface AppGrant {
  permId?: string
  appId: string
  permission: Permission
  allowSharing: boolean
  name: string
  path: string
  limitBy?: string
  /** The app's own icon spec + cache rev (parsed server-side). */
  icon?: MiniappIconSpec | null
  iconRev?: string | null
}

/** Hallway viewer — exists because of a descendant grant, not editable here. */
interface ViaDescendantTag {
  type: TagType
  targetId: string
  label: string
  /** Permission level on the descendant — drives which tab the hallway pill shows under. */
  permission: Permission
  descendantPath: string
  /** Team granted on the descendant but not visible to the caller. */
  redacted?: boolean
}

const REDACTED_LABEL = 'Redacted'

// ─── Component ───────────────────────────────────────────────────────────────

export function WhoHasAccessDialog({
  folderId,
  folderName,
  folderPath,
  tenantId,
  isFolder = true,
  myPermission,
  isReceivedShare = false,
  inShareContext = false,
  hidePeopleTeams = false,
  initialViewMode,
  onClose,
}: WhoHasAccessDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()
  const confirm = useConfirm()

  // Tab set: files and folders both offer every tier now, including Manage &
  // Edit (admin) — admin on a file is grantable directly. FILE_TABS == TAB_ORDER.
  /** Read-level caller: the access map is disclosed, editing is not, and the
   *  No-access tab is absent because the server withheld those rows. */
  const [accessMapReadOnly, setAccessMapReadOnly] = useState(false)
  /** Why the access list could not be loaded, rendered INSIDE the dialog.
   *  A toast is the wrong home for this one: the refusal leaves the dialog
   *  looking like an empty access list — i.e. "nobody has access" — and the
   *  toast that would have explained it is gone seconds later, so the wrong
   *  reading is the one that persists. */
  const [loadError, setLoadError] = useState<string | null>(null)
  // A read-level caller never receives `hidden` rows, so the tab would sit
  // there empty and imply "nobody is denied" — which is precisely the fact
  // being withheld. Drop it instead.
  const allTabs = (isFolder || inShareContext) ? TAB_ORDER : FILE_TABS
  const tabs = accessMapReadOnly ? allTabs.filter(t => t !== 'hidden') : allTabs
  // 'all' is a synthetic tab — shows every grant grouped by level. The
  // per-permission tabs (admin/readwrite/readcopy/hidden) remain the
  // place where users edit (add/drag/remove); 'all' is read-only audit.
  type ActiveTab = Permission | 'all'
  const sidebarTabs: ActiveTab[] = ['all', ...tabs]
  const isDesktop = useIsDesktop()
  /** Where the desktop scope indicator sits, held HERE so it survives the
   *  branch swap that remounts the strip. See `ViewModeSwitch`'s `thumbRef`. */
  const viewTabThumb = useRef<{ left: number; width: number } | null>(null)
  const tierStripRef = useRef<HTMLDivElement>(null)
  const [activeTabRaw, setActiveTabRaw] = useState<ActiveTab>('all')
  /** The tier axis is navigable on BOTH sizes, in different shapes.
   *
   *  It was removed from the phone for a while, on the reasoning that the
   *  `all` view already groups by tier with headers and counts so the tabs
   *  only filtered a self-describing list. That reasoning still holds as far
   *  as it goes, and it was not what the screen needed: scrolling to find a
   *  tier is worse than tapping one when you already know which you want, and
   *  the strip carries the COUNTS, which are the answer to "how many people
   *  can edit this" without reading anything.
   *
   *  What was actually wrong was never the second axis, it was that both axes
   *  wore the same clothes: a dropdown pill sat flush against tab chips in one
   *  row, separated by a hairline, so the eye read six peers of which one
   *  behaved differently. They are two rows now, and deliberately unalike —
   *  the scope is a filled segmented control of icons, the tier is a plain
   *  scrolling strip of text chips with counts. Same information, no longer
   *  confusable. */
  const activeTab: ActiveTab = activeTabRaw
  const setActiveTab = setActiveTabRaw
  /** Keep the ACTIVE tier chip visible in the phone strip: it can start
   *  outside the viewport (No access is fifth). Keyed on the SELECTION so it
   *  runs when that changes, never on an unrelated re-render. */
  useEffect(() => {
    tierStripRef.current
      ?.querySelector<HTMLElement>(`[data-tier="${activeTab}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [activeTab])
  /** The pane's recessed-well treatment is DESKTOP ONLY — on a phone the screen
   *  is the pane, and a bordered radius inside a full-bleed dialog was three
   *  boxes deep.
   *
   *  Applied from JS rather than as `md:themed-bg-primary md:border md:themed-border`.
   *  `.themed-*` are hand-written classes in `apps/web/src/index.css`, NOT Tailwind
   *  utilities, so Tailwind never generates an `md:` variant for them and the
   *  prefixed form is silently DEAD. `md:border` alone does apply, which left the
   *  pane with a 1px border falling back to `currentColor` — i.e. `--text-primary`,
   *  a near-white rim — and no background at any width. It typechecks, it looks
   *  like a responsive class, and it is the only place in the repo that tried it. */
  const paneChrome = isDesktop ? 'themed-bg-primary border themed-border rounded-2xl' : ''
  // Clamp when the No-access tab disappears underneath the selection. The
  // sidebar renders outside the loading gate, so the tab is live during the
  // initial fetch — while `accessMapReadOnly` is still false — and the silent
  // permVersion refetch can also flip it mid-session (an admin demoted to
  // read). Landing on a removed tab renders an EMPTY No-access list, which
  // reads as "nobody is denied": the exact misreading dropping the tab exists
  // to prevent, arrived at by a different route.
  useEffect(() => {
    if (accessMapReadOnly && activeTab === 'hidden') setActiveTab('all')
  }, [accessMapReadOnly, activeTab])
  // Step 9b: top-level mode switch. 'people' = existing per-tenant
  // sidebar (permission tabs); 'outsider' = connected-orgs view that
  // re-uses orgConnectionsApi (step 9a) — the building block of
  // external share. Folder-scoped filtering is deferred to 9b proper.
  const [viewMode, setViewMode] = useState<'people' | 'outsider' | 'share-links' | 'miniapp'>(initialViewMode ?? (hidePeopleTeams ? 'outsider' : 'people'))
  // Decision 10 / Phase 37: dual-pivot tab inside the Share-links view.
  // Lifted here (was local to ShareLinkPrincipalsPane) so the left
  // sidebar can render it as a permission-style tab list and the right
  // pane just renders the selected slice.
  const [shareLinksSubTab, setShareLinksSubTab] = useState<ShareLinksSubTab>('links')
  // Sub-filters scoped to each parent tab. 'all' is the unfiltered
  // default; the indented sidebar items toggle to a slice.
  const [linksFilterRaw, setLinksFilter] = useState<'all' | LinkAudience>('all')
  /** PHONE: the audience/category FILTERS are pinned to 'all'; the sub-tab is
   *  not. Same rule the tier axis follows in the People view, cut in a
   *  different place because the two axes here are not the same kind of thing:
   *
   *    Links vs Recipients  changes WHAT you are looking at (two different
   *                         lists), so it survives as a two-way control.
   *    the audience pills   merely narrow one of those lists, and every row
   *                         already carries its own audience chip — so on a
   *                         390px strip they cost a whole band of chrome to
   *                         restate what the list says about itself.
   *
   *  Ten pills across two axes is what the sidebar becomes when it is laid on
   *  its side, and none of them fit. */
  const linksFilter: 'all' | LinkAudience = isDesktop ? linksFilterRaw : 'all'
  // Recipient buckets: a recipient lands in a bucket if at least one of
  // their contributions matches the bucket's category.
  //   blocked:   permission='block' (overrides kind — the recipient is
  //              explicitly denied on the link)
  //   edit:      kind='quick_view' + permission='edit'
  //   app:       kind='app' (always View by design)
  //   view-live: kind='quick_view' + permission='view' + liveMode
  //   view-save: kind='quick_view' + permission='view' + !liveMode
  // A recipient with mixed contributions appears in every matching
  // bucket, so the counts can sum to more than `aggregates.length`.
  const [recipientsFilterRaw, setRecipientsFilter] = useState<'all' | 'edit' | 'app' | 'view-live' | 'view-save' | 'blocked'>('all')
  /** Pinned to 'all' on a phone — see `linksFilter`. */
  const recipientsFilter = isDesktop ? recipientsFilterRaw : 'all'
  type ContribCategory = 'edit' | 'app' | 'view-live' | 'view-save' | 'blocked'
  const categoryOf = (c: RecipientAggregate['contributions'][number]): ContribCategory => {
    if (c.permission === 'block') return 'blocked'
    if (c.kind === 'app') return 'app'
    if (c.permission === 'edit') return 'edit'
    return c.liveMode ? 'view-live' : 'view-save'
  }
  // Fetch lifted so the sidebar can show counts on each tab without a
  // second round trip. Only fires when the user actually opens the
  // share-links view — otherwise the call is skipped.
  const shareLinkPrincipals = useShareLinkPrincipals(folderId, viewMode === 'share-links')
  const recipientAggregates = useMemo(
    () => aggregateByRecipient(shareLinkPrincipals.rows),
    [shareLinkPrincipals.rows],
  )
  // Audience tallies, keyed on the SAME derivation the row chip renders
  // (`linkAudience`). The old `'*' in grants` split had two faults: it counted
  // a `'*': block` deny as Public, and it had no Private bucket at all — every
  // link that named nobody was tallied as Restricted, so the count disagreed
  // with the badge on each of those rows. External reshares classify
  // identically, so they fold into the same buckets rather than only into All.
  const audienceCounts = useMemo(() => {
    const counts: Record<LinkAudience, number> = { public: 0, restricted: 0, private: 0 }
    for (const r of [...shareLinkPrincipals.rows, ...shareLinkPrincipals.inboundReshares]) {
      counts[linkAudience(r.recipientGrants, r.strictAudience)]++
    }
    return counts
  }, [shareLinkPrincipals.rows, shareLinkPrincipals.inboundReshares])
  // Per-bucket recipient counts (a recipient with mixed contributions
  // is counted in every matching bucket).
  const recipientsBucketCounts = useMemo(() => {
    const counts = { edit: 0, app: 0, 'view-live': 0, 'view-save': 0, blocked: 0 } as Record<ContribCategory, number>
    for (const agg of recipientAggregates) {
      const seen = new Set<ContribCategory>()
      for (const c of agg.contributions) seen.add(categoryOf(c))
      for (const cat of seen) counts[cat]++
    }
    return counts
  }, [recipientAggregates])
  // Filtered rows / aggregates that the right pane actually renders.
  const visibleRows = useMemo(() => {
    if (linksFilter === 'all') return shareLinkPrincipals.rows
    return shareLinkPrincipals.rows.filter((r) => linkAudience(r.recipientGrants, r.strictAudience) === linksFilter)
  }, [shareLinkPrincipals.rows, linksFilter])
  const visibleInboundReshares = useMemo(() => {
    if (linksFilter === 'all') return shareLinkPrincipals.inboundReshares
    return shareLinkPrincipals.inboundReshares.filter((r) => linkAudience(r.recipientGrants, r.strictAudience) === linksFilter)
  }, [shareLinkPrincipals.inboundReshares, linksFilter])
  const visibleAggregates = useMemo(() => {
    if (recipientsFilter === 'all') return recipientAggregates
    return recipientAggregates.filter((a) => a.contributions.some((c) => categoryOf(c) === recipientsFilter))
  }, [recipientAggregates, recipientsFilter])
  // LinkRow plumbing for the audit view. EditPanel mounts on a single
  // row at a time; busyId disables the others while revoke is in flight.
  const [shareLinkEditingId, setShareLinkEditingId] = useState<string | null>(null)
  const [shareLinkBusyId, setShareLinkBusyId] = useState<string | null>(null)
  // Highlight one row + scroll it into view in the All Links list — set
  // when the user clicks a link badge inside an All Recipients card.
  const [shareLinkFlashId, setShareLinkFlashId] = useState<string | null>(null)
  // URL of the link whose QR is being shown in the centered QR modal (null = closed).
  const [qrModalUrl, setQrModalUrl] = useState<string | null>(null)
  const focusLinkInAllLinks = useCallback((linkId: string) => {
    setShareLinksSubTab('links')
    // Snap the filter back to 'all' so the targeted link is definitely
    // in scope no matter which sub-filter the user came from.
    setLinksFilter('all')
    setShareLinkFlashId(linkId)
    setTimeout(() => {
      setShareLinkFlashId((cur) => (cur === linkId ? null : cur))
    }, 2000)
  }, [])
  const handleShareLinkPatched = useCallback((updated: ShareLinkWithUrl) => {
    shareLinkPrincipals.updateRow(updated.id, updated as Partial<ShareLinkPrincipalRow>)
  }, [shareLinkPrincipals])
  const handleShareLinkCopy = useCallback((url: string) => {
    void navigator.clipboard.writeText(url)
      .then(() => toast.success('Link copied to clipboard'))
      .catch(() => toast.info(`Link: ${url}`))
  }, [toast])
  const handleShareLinkRevoke = useCallback(async (row: ShareLinkPrincipalRow) => {
    const ok = await confirm({
      title: 'Revoke this link?',
      message: (
        <span>
          <code>{row.url}</code> will stop working immediately. The token is
          permanently burned — even a future link on the same file gets a fresh,
          unrelated URL. This can't be undone.
        </span>
      ),
      type: 'danger',
      confirmText: 'Revoke',
    })
    if (!ok) return
    setShareLinkBusyId(row.id)
    try {
      await revokeShareLink(row.id)
      shareLinkPrincipals.removeRow(row.id)
      setShareLinkEditingId((cur) => (cur === row.id ? null : cur))
      toast.success('Link revoked')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke')
    } finally {
      setShareLinkBusyId(null)
    }
  }, [confirm, shareLinkPrincipals, toast])

  // Revoke a link that reaches this folder THROUGH A SHORTCUT — judged from this folder
  // (the server checks Manage here and that the link really reaches it via a shortcut).
  const handleViaShortcutRevoke = useCallback(async (l: ViaShortcutLink) => {
    const who = l.creator ? (l.creator.name || l.creator.email) : 'someone no longer here'
    const ok = await confirm({
      title: 'Revoke this link?',
      message: (
        <span>
          A link <strong>{who}</strong> made on another folder reaches this {isFolder ? 'folder' : 'file'} through a
          shortcut. Revoking it stops the whole link working — for everyone it was shared with,
          not only here.
        </span>
      ),
      type: 'danger',
      confirmText: 'Revoke link',
    })
    if (!ok) return
    setShareLinkBusyId(l.id)
    try {
      await revokeShareLink(l.id, { viaFolderId: folderId })
      shareLinkPrincipals.removeViaShortcut(l.id)
      toast.success('Link revoked')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke')
    } finally {
      setShareLinkBusyId(null)
    }
  }, [confirm, shareLinkPrincipals, toast, folderId, isFolder])

  // Owner-revoke of an EXTERNAL reshare link on this content. The resharer's
  // link stops serving; a recipient who already added it to their Drive keeps
  // their separate owner→recipient grant (revocable in the People view).
  const handleInboundReshareRevoke = useCallback(async (r: InboundReshareLink) => {
    const who = r.reshareFrom.name || r.reshareFrom.email || r.reshareFrom.tenantName
    const ok = await confirm({
      title: 'Revoke this reshare?',
      message: (
        <span>
          The link <strong>{who}</strong> created to re-share your{' '}
          {r.reshareFrom.targetFileName ? <code>{r.reshareFrom.targetFileName}</code> : 'content'} will stop working.
          Anyone who already added it to their Drive keeps the access you granted — revoke that
          separately under People.
        </span>
      ),
      type: 'danger',
      confirmText: 'Revoke reshare',
    })
    if (!ok) return
    setShareLinkBusyId(r.id)
    try {
      await ownerRevokeShareLink(r.id)
      shareLinkPrincipals.removeInboundReshare(r.id)
      toast.success('Reshare revoked')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke')
    } finally {
      setShareLinkBusyId(null)
    }
  }, [confirm, shareLinkPrincipals, toast])

  const [tags, setTags] = useState<AccessTag[]>([])
  /** Raw `inherited` map from the permissions API: keys are role names
   *  (no prefix), `group:<teamId>`, or `user:<email>`. Used by the All
   *  Permissions 3-dot menu to know each principal's would-be-inherited
   *  level even when an explicit grant currently shadows it. */
  const [inheritedRaw, setInheritedRaw] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)
  /** IDs of tags that just appeared — used for the CSS flash highlight. */
  const [recentlyAdded, setRecentlyAdded] = useState<Set<string>>(new Set())
  /** Issue #71: folder's Restricted flag. Seeded from the server's GET
   *  response so the toggle initial state matches reality, then flipped
   *  optimistically by `handleToggleRestricted`. */
  const [restricted, setRestricted] = useState(false)
  /** Issue #106: the Restricted switch must be inert while its own state
   *  is known-stale. `permFetching` is true whenever a permissions
   *  (re)fetch is in flight — the initial load AND the silent
   *  org:access_changed refetch (and, after a DriveView remount, the
   *  fresh initial load). `restrictedSaving` covers the PATCH round-trip.
   *  In those windows `restricted` may not reflect the server, so a
   *  click computing `next = !restricted` would write the wrong absolute
   *  boolean to an access-control flag. Starts true: a fetch is in
   *  flight from mount. */
  const [permFetching, setPermFetching] = useState(true)
  /** An `org:access_changed` arrived and its refetch hasn't started yet (#1259 holds it up to an interval): the
   *  switch is as stale as during the fetch itself (#106), so it locks from the event, not from the fetch.
   *  Accepted cost (owner, 2026-10-22): the event is tenant-wide (no folder), so while someone elsewhere changes
   *  access every second or two the switch is unlocked only between a refetch landing and the next event (refetches
   *  are ≥5 s apart then). Chosen over a click from an outdated who-keeps-access list, which the server's 409 does
   *  not catch unless Restricted itself moved. */
  const [accessChangeQueued, setAccessChangeQueued] = useState(false)
  const [restrictedSaving, setRestrictedSaving] = useState(false)
  /** A Restricted toggle is in flight and the list on screen is not yet the
   *  server's answer: `previewed` = it shows the server-computed preview (likely
   *  right, being confirmed); otherwise it is the OLD list (stale). */
  const [listPending, setListPending] = useState<null | { previewed: boolean; retrying?: boolean; stalled?: boolean; offline?: boolean }>(null)
  /** Mirror of `listPending` for the fetch effect's callbacks. */
  const listPendingRef = useRef(listPending)
  listPendingRef.current = listPending
  /** What the Restricted control says while a change is unconfirmed — shown beside
   *  the switch itself (where the user just clicked), in a slot that is always
   *  reserved, so nothing moves when it appears. */
  const restrictedStatus: null | { busy: boolean; text: string } = !listPending ? null
    : listPending.stalled ? { busy: false, text: 'Not saved — reopen' }
    : listPending.offline ? { busy: false, text: 'Offline — will save' }
    : listPending.retrying ? { busy: true, text: 'Retrying…' }
    : { busy: true, text: listPending.previewed ? 'Saving…' : 'Updating…' }
  /** While the list is pending and the server can't be reached, look again on a
   *  timer — never leave an unconfirmed list on screen as if it were final. */
  const reconcileRetry = useRef<{ timer: ReturnType<typeof setTimeout> | null; attempts: number }>({ timer: null, attempts: 0 })
  useEffect(() => () => { if (reconcileRetry.current.timer) clearTimeout(reconcileRetry.current.timer) }, [])
  // Back online with an unconfirmed (or unsaved) Restricted change: look at once.
  useEffect(() => {
    const onOnline = () => {
      if (!listPendingRef.current) return
      reconcileRetry.current.attempts = 0
      setPermVersion(v => v + 1)
    }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [])
  /** A Restricted PATCH is in flight. A fetch that lands meanwhile (another
   *  admin's `org:access_changed`) predates our write: applying it would flip the
   *  previewed list — and the switch — back to the old state until our own
   *  refetch lands. Such a result is dropped; the refetch after the PATCH (or the
   *  rollback on failure) is what the dialog shows. */
  const restrictedPatchInFlight = useRef(false)
  /** Change ids this dialog sent with its own Restricted PATCHes. The server
   *  stamps the id on the `org:access_changed` it publishes, so the echo of our
   *  own write is skipped (its reply already carried the confirmed list) while
   *  every other admin's change still refetches. */
  const ownChangeIds = useRef(new Set<string>())
  /** Another change arrived while our save was in flight: refetch once after it. */
  const refetchAfterSave = useRef(false)
  /** The folder on screen now — a save's reply is applied only to the folder it was for. */
  const currentFolderId = useRef(folderId)
  currentFolderId.current = folderId
  /** A Restricted change that got no answer (offline). It is SAVED once the server
   *  is reachable again — but only if nobody changed the flag meanwhile: the first
   *  answer after reconnecting decides (still at `baseVersion` and not yet `next`
   *  → re-send; already `next` → it landed; moved on → someone else's change wins). */
  const pendingRestrictedWrite = useRef<null | { next: boolean; baseVersion: number; folderId: string }>(null)
  // #1316 review round 1: an unsaved Restricted change belongs to the session it was made in — never re-sent after it
  // ended (the re-send would go out as the next identity)
  useEffect(() => onSessionEnd(() => { pendingRestrictedWrite.current = null }), [])
  /** Shared by the sidebar pill and the phone's settings row. Hoisted because
   *  it is the half that MATTERS — a stale `restricted` makes `!restricted`
   *  write the wrong absolute value (issue #106) — and two copies of a
   *  three-way guard is how one of them loses a term. */
  // Locked for as long as a Restricted change is unconfirmed (`listPending`), not only
  // while a request is in flight: the background checks while offline must not blink the
  // switch enabled/disabled every few seconds, or let a click in between start another change.
  const restrictedLocked = permFetching || accessChangeQueued || restrictedSaving || accessMapReadOnly || !!listPending
  /** Issue #106: the optimistic-concurrency token last read from the
   *  server. Sent back on PATCH so a concurrent change is rejected
   *  (409 → reconcile) rather than silently clobbered. */
  const [restrictedVersion, setRestrictedVersion] = useState(0)
  /** Tabs currently mid-flash because the user just toggled Restricted.
   *  Drives the `.animate-restricted-tab-flash` CSS class on the sidebar
   *  buttons. Cleared after the animation duration. */
  const [flashingTabs, setFlashingTabs] = useState<Set<Permission>>(new Set())
  /** All teams + members in the org, fetched once at mount; used by the picker. */
  const [allTeams, setAllTeams] = useState<TeamInfo[]>([])
  const [allMembers, setAllMembers] = useState<MemberInfo[]>([])
  /** Phase 4: source share cap (when this folder lives inside a
   *  `.shared` mount). Permissions above this tier get the amber
   *  warning. Null on native (non-mount) folders. */
  const [shareCap, setShareCap] = useState<Permission | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  /** #787 §5 — grants inside this folder still narrowing a just-promoted Manager. */
  const [narrowingReview, setNarrowingReview] = useState<{ granteeLabel: string; narrowings: Narrowing[] } | null>(null)
  /** The "+ Add" button the open picker belongs to. Captured from the click
   *  rather than held in a ref: the All-permissions view renders one Add per
   *  section inside a `.map()`, where a per-item ref needs a component split. */
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null)
  // Which tier the "Add mini app" picker is granting at (null = closed). App
  // principals get their own picker (browse to a `.miniapp` folder) since the
  // people AddPicker lists members/teams/roles.
  const [appPickerFor, setAppPickerFor] = useState<Permission | null>(null)
  /**
   * Drag state. We mirror the native DnD payload in React state so the UI can
   * react (show trash target, preview tab on hover) — DataTransfer alone
   * isn't readable during dragover, only on drop, so reactive UI needs the
   * mirror. `null` when no drag is in progress.
   */
  const [draggingTag, setDraggingTag] = useState<AccessTag | null>(null)
  /**
   * Sidebar tab the cursor is currently over during a drag — drives the ring
   * visual. Kept SEPARATE from `activeTab` because mutating activeTab mid-drag
   * unmounts the source <span>, and `dragend` events from detached DOM nodes
   * don't bubble to window — leaving the trash overlay and ring stuck after
   * release. Active tab only updates after a successful drop.
   */
  const [dragOverTab, setDragOverTab] = useState<Permission | null>(null)
  /** True while the cursor is over the trash drop target during a drag. */
  const [trashHover, setTrashHover] = useState(false)
  /**
   * Per-pill "Allow sharing" toggle state — scaffold-only local UI state
   * (no backend yet, see MINI_APP_SECURITY.md). Default is "allowed" so
   * `undefined === true` for fresh rows. Keyed by tagKey so the value
   * survives drag-to-tab (the same principal at a new permission level
   * keeps its sharing state).
   */
  const [sharingByTag, setSharingByTag] = useState<Map<string, boolean>>(new Map())
  /** One of the drive's own folders: its person / team / role cards show the value in force. */
  const [structuralSharing, setStructuralSharing] = useState(false)
  /** tagKey of the pill whose sharing-toggle dropdown is open, or null. */
  const [sharingDropdownFor, setSharingDropdownFor] = useState<string | null>(null)
  /** Which per-tab pill has its 3-dot menu open. The All Permissions view keeps
   *  its own copy of this — the two lists never render at the same time. */
  const [tagMenuFor, setTagMenuFor] = useState<string | null>(null)
  /** True while the cursor is over the user-list drop zone during a drag.
   *  The user-list zone wraps the explicit-pills row and is functionally
   *  equivalent to dropping on the active sidebar tab — it's an "add to
   *  current perm" target that mirrors the trash zone visually. */
  const [userListHover, setUserListHover] = useState(false)
  /** Hallway viewers — read-only, sourced from server's viaDescendant payload. */
  const [viaDescendant, setViaDescendant] = useState<ViaDescendantTag[]>([])
  /** Bumped on every `org:access_changed` event. Re-runs the fetch effect so
   *  the dialog reflects grants made by other admins (or by this admin via
   *  another tab) without requiring the user to close + reopen. */
  const [permVersion, setPermVersion] = useState(0)
  const { onOrgEvent } = useRealtime()
  /** Becomes true after the first successful fetch settles. Gates the
   *  newly-added `flash` (pop animation + ring) so existing tags rendered
   *  on initial open don't get the highlight — only tags that arrive
   *  AFTER the dialog has settled (i.e., from `handleAdd`) should pop. */
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  /** All in-flight 1.5s "remove from recentlyAdded" timers. Tracked so we
   *  can clear them on unmount — otherwise a user closing the dialog
   *  mid-flash leaves a callback that calls setState on an unmounted
   *  component. React 18 currently swallows the no-op gracefully, but
   *  the cleanup is the right shape regardless. */
  const flashTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  useEffect(() => () => {
    flashTimers.current.forEach(id => clearTimeout(id))
    flashTimers.current.clear()
  }, [])

  // ── Fetch permissions, teams, members ─────────────────────────────────────

  // The last server answer, so a Restricted toggle can show the server-computed
  // preview (`whenRestrictedToggled`) instantly and roll back to it on failure.
  const lastFetchRef = useRef<{
    permData: PermissionsResponse
    teamData: { teams: TeamInfo[] }
    memberData: { members: MemberInfo[]; invites?: MemberInfo[] }
  } | null>(null)

  /** Turn one server answer into the dialog's state (tags, hallway, flags). */
  const applyFetched = (
    permData: PermissionsResponse,
    teamData: { teams: TeamInfo[] },
    memberData: { members: MemberInfo[]; invites?: MemberInfo[] },
  ): void => {
    setAllTeams(teamData.teams)
    setAllMembers([
      ...memberData.members.map(m => ({ ...m, status: m.status ?? ('active' as const) })),
      // DECLINED invites are excluded: re-inviting clears that state first,
      // so offering them would stage a grant against an email that cannot
      // currently accept. Same filter the team editor applies.
      // Emails lowercased on the way in. The server stores grant targets
      // normalised, so a tag's `targetId` comes back lowercase; leaving an
      // invite's original casing here makes every `email === targetId`
      // lookup miss — no "Invited" chip, no display name, wrong avatar
      // initial, and the "already added" check fails so a granted invitee
      // is offered again.
      ...(memberData.invites ?? [])
        .filter(inv => inv.status !== 'declined')
        .map(inv => ({ ...inv, email: inv.email.toLowerCase(), status: 'invited' as const })),
    ])
    const teamById = new Map(teamData.teams.map(t => [t.id, t.name]))
    const collected: AccessTag[] = []

    // Explicit rows. Team grants for teams not in `teamById` mean the
    // caller can't see the team (privacy filter elsewhere). Show
    // "Redacted" so the row still surfaces — the caller is the folder
    // admin and needs to know a grant exists at this level — without
    // leaking the team's name.
    for (const p of permData.permissions) {
      const type: TagType = p.targetType === 'role' ? 'role' : p.targetType === 'group' ? 'team' : 'member'
      const teamName = type === 'team' ? teamById.get(p.targetId) : undefined
      const isRedacted = type === 'team' && teamName === undefined
      const label =
        type === 'role' ? `role:${ROLE_LABELS[p.targetId] ?? p.targetId}` :
        type === 'team' ? (isRedacted ? REDACTED_LABEL : `team:${teamName}`) :
        p.targetId  // member: just the email
      collected.push({
        type, targetId: p.targetId, label,
        permission: p.permission,
        inherited: false,
        permId: p.id,
        ...(isRedacted ? { redacted: true } : {}),
      })
    }

    // Inherited rows (role, team, and user). User-specific inheritance
    // covers the share-primitive case where a member grant on a
    // `.shared` mount artifact must surface on every virtual child
    // inside that mount.
    const inheritedFromPath = permData.inheritedFromPath
    // Stash the raw map for the All Permissions menu (the per-tag
    // loop below filters explicit-shadowed entries out, but the
    // menu wants to know the inheritance level regardless).
    setAccessMapReadOnly(!!permData.accessMapReadOnly)
    setInheritedRaw(permData.inherited ?? {})
    for (const [key, perm] of Object.entries(permData.inherited ?? {})) {
      if (key.startsWith('group:')) {
        const teamId = key.slice(6)
        // Skip if there's already an explicit row for this team — explicit wins.
        if (collected.some(t => t.type === 'team' && t.targetId === teamId)) continue
        const teamName = teamById.get(teamId)
        const isRedacted = teamName === undefined
        collected.push({
          type: 'team', targetId: teamId,
          label: isRedacted ? REDACTED_LABEL : `team:${teamName}`,
          permission: perm as Permission,
          inherited: true,
          inheritedFromPath,
          ...(isRedacted ? { redacted: true } : {}),
        })
      } else if (key.startsWith('user:')) {
        const email = key.slice(5)
        // Explicit wins (same as team/role).
        if (collected.some(t => t.type === 'member' && t.targetId === email)) continue
        collected.push({
          type: 'member', targetId: email,
          label: email,
          permission: perm as Permission,
          inherited: true,
          inheritedFromPath,
        })
      } else {
        // Role inheritance: key is the role name, no prefix
        const roleName = key
        if (collected.some(t => t.type === 'role' && t.targetId === roleName)) continue
        collected.push({
          type: 'role', targetId: roleName,
          label: `role:${ROLE_LABELS[roleName] ?? roleName}`,
          permission: perm as Permission,
          inherited: true,
          inheritedFromPath,
        })
      }
    }

    // Members: also check the org-wide member list — for any member that isn't
    // already in `collected` AND has an explicit user row, no-op (we already added
    // them above). The issue's "what's already optimised" note: only show members
    // with relevant access; we don't enumerate the whole org. So we skip members
    // who only inherit via role/team — those are represented by the role/team tag.

    // App-principal grants flow through the SAME tag machinery as
    // people (rendered in the shared tier sidebar + container, filtered
    // by viewMode). Precompute the disambiguating parent label over the
    // whole set so the subline stays short. `targetId` = the .miniapp
    // folder id, used to route mutations through the mini-app access API.
    const explicitApps = (permData.apps ?? []).map(a => ({ ...a, inherited: false }))
    const inheritedAppRows = (permData.inheritedApps ?? []).map(a => ({ ...a, inherited: true }))
    const allApps = [...explicitApps, ...inheritedAppRows]
    const appLabels = disambiguateParentLabels(allApps.map(a => a.path))
    allApps.forEach((a, idx) => {
      collected.push({
        type: 'app',
        targetId: a.appId,
        label: a.name,
        permission: a.permission,
        inherited: a.inherited,
        permId: a.inherited ? undefined : a.permId,
        appPath: appLabels[idx],
        appFullPath: a.path,
        appIcon: a.icon ?? null,
        appIconRev: a.iconRev ?? null,
      })
    })

    setTags(collected)

    // Hallway viewers from descendant grants — server has already filtered
    // out anyone in `permissions` or `inherited`, so the list is purely
    // additional rows visible only because of a child grant. The
    // descendant permission drives which tab the pill renders under.
    const hallway: ViaDescendantTag[] = []
    for (const v of permData.viaDescendant ?? []) {
      // The `member` fallback is safe here only because the server never
      // sends an `'app'` row: its label is the bare `targetId`, which is an
      // email for a person but a `.pdapp` folder id for an app — and that
      // is exactly how a grant once rendered as `WAGQXWZvGg3dbwLfRGgc`.
      const type: TagType = v.targetType === 'role' ? 'role' : v.targetType === 'group' ? 'team' : 'member'
      const teamName = type === 'team' ? teamById.get(v.targetId) : undefined
      const isRedacted = type === 'team' && teamName === undefined
      const label =
        type === 'role' ? `role:${ROLE_LABELS[v.targetId] ?? v.targetId}` :
        type === 'team' ? (isRedacted ? REDACTED_LABEL : `team:${teamName}`) :
        v.targetId
      hallway.push({
        type,
        targetId: v.targetId,
        label,
        permission: v.permission as Permission,
        descendantPath: v.descendantPath,
        ...(isRedacted ? { redacted: true } : {}),
      })
    }
    setViaDescendant(hallway)
    setRestricted(permData.restricted ?? false)
    setRestrictedVersion(permData.restrictedVersion ?? 0)
    setShareCap(permData.shareCap ?? null)

    // Seed sharingByTag from the server's allow_sharing map.
    // Response key shape: `${type}:${id}` (member:email, team:id,
    // role:name); local key shape: tagKey() = `${type}:${id}:e|i`.
    // Re-seed on every fetch so refetches triggered by
    // org:access_changed correctly reflect just-mutated values.
    setStructuralSharing(!!permData.structuralFolder)
    if (permData.allowSharing) {
      const next = new Map<string, boolean>()
      for (const t of collected) {
        const lookup = `${t.type}:${t.targetId}`
        const value = permData.allowSharing[lookup]
        if (value !== undefined) {
          next.set(tagKey(t), value)
        } else if (permData.structuralFolder && t.type !== 'app') {
          // A card the server's map has no key for (a pending invitee) reads the
          // folder's default, not the dialog's "Allow" fallback.
          next.set(tagKey(t), false)
        }
      }
      setSharingByTag(next)
    }
  }

  const [reloadAfterDrop, setReloadAfterDrop] = useState(0)
  useEffect(() => {
    let cancelled = false
    // Loading flag only on the initial load — subsequent refetches triggered
    // by `permVersion` should silently swap the data without flashing the
    // "Loading..." placeholder over the user's already-rendered grants.
    const isInitial = permVersion === 0
    if (isInitial) setLoading(true)
    // #106: every fetch — initial OR the silent permVersion refetch —
    // is a window where `restricted` doesn't yet reflect the server.
    setPermFetching(true)
    setAccessChangeQueued(false)   // the fetch now carries the lock (same render: no unlocked gap)
    setLoadError(null)
    // Did the server answer (data, or an authoritative refusal)?
    let answered = false
    // #1316: an answer dropped because its session ended — no answer, no failure: asked again (`reloadAfterDrop`)
    let dropped = false

    Promise.all([
      request<PermissionsResponse>(`/api/folders/${folderId}/permissions`),
      // Non-org-admins only see teams they're related to (privacy filter
      // server-side). Folder admin alone doesn't widen team visibility —
      // they can only grant teams they're personally related to.
      request<{ teams: TeamInfo[] }>(`/api/orgs/${tenantId}/teams`).catch(() => ({ teams: [] as TeamInfo[] })),
      // The endpoint answers `{ members, invites }`. BOTH are grant targets:
      // `POST /api/folders/:id/permissions` accepts an email with a pending
      // invite, precisely so an admin can stage a new hire's access before they
      // accept (issue #112) — the same reason Settings → Members and the team
      // editor list invitees. This dialog used to read `members` alone and
      // silently drop `invites`, so a pending invitee was unreachable from the
      // folder page while being editable in Settings.
      request<{ members: MemberInfo[]; invites?: MemberInfo[] }>(`/api/orgs/${tenantId}/members`)
        .catch(() => ({ members: [] as MemberInfo[], invites: [] as MemberInfo[] })),
    ])
      .then(([permData, teamData, memberData]) => {
        if (cancelled) return
        if (restrictedPatchInFlight.current) return
        answered = true
        const pw = pendingRestrictedWrite.current
        if (pw && pw.folderId === folderId) {
          if (!!permData.restricted === pw.next) {
            // It landed before the connection dropped: nothing to re-send.
            pendingRestrictedWrite.current = null
          } else if ((permData.restrictedVersion ?? 0) === pw.baseVersion) {
            // Still unsaved and nobody else changed it: keep the change on screen and send it now.
            const base = { permData, teamData, memberData }
            const shown = previewOf(permData, pw.next)
            lastFetchRef.current = shown ? { ...base, permData: shown } : base
            if (shown) applyFetched(shown, teamData, memberData)
            void sendRestricted(pw.next, pw.baseVersion, base, !!shown)
            return
          } else {
            // Someone else changed Restricted while we were offline: theirs stands.
            pendingRestrictedWrite.current = null
            toast.error('Restricted was changed by someone else while you were offline — showing the current value')
          }
        }
        lastFetchRef.current = { permData, teamData, memberData }
        applyFetched(permData, teamData, memberData)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (isSessionEndedError(err)) { dropped = true; return }
        // 403 is not a failure — it is the answer. `manage_folder_permission`
        // resolved below `read` for this caller, so say that rather than
        // "Failed to load", which reads as a bug and invites a retry that can
        // never succeed.
        const refused = (err as { status?: number } | null)?.status === 403
        if (refused) {
          // A refusal is AUTHORITATIVE, and on a silent `permVersion` refetch it
          // arrives with a full access map already on screen from the last
          // successful load — so the banner alone left the caller reading, and
          // still editing, the very list it said they may not see (each edit
          // then 403-toasting). Clear the disclosure and flip to read-only,
          // which the existing wiring turns into an inert pane, a dropped
          // No-access tab and a disabled Restricted toggle.
          setTags([])
          setViaDescendant([])
          setInheritedRaw({})
          setSharingByTag(new Map())
          setAccessMapReadOnly(true)
          setLoadError("You don't have permission to see who has access to this folder.")
          toast.error("You don't have permission to see who has access")
          pendingRestrictedWrite.current = null
          answered = true
          return
        }
        // Reconciling an unconfirmed Restricted change: the pending note says so
        // and a retry is scheduled below — no toast per attempt.
        if (listPendingRef.current) return
        // A transient failure REFRESHING is not a failure to load: the data on
        // screen is seconds old and still true. Claiming "Failed to load the
        // access list" above a fully rendered list is the false half. Only the
        // initial fetch has nothing to fall back on.
        if (isInitial) setLoadError('Failed to load the access list.')
        toast.error(isInitial ? 'Failed to load access list' : "Couldn't refresh the access list")
      })
      .finally(() => {
        if (cancelled) return
        // a dropped load is asked again as the session now here (an initial one stays "Loading…" until it answers)
        if (dropped) { setReloadAfterDrop(n => n + 1); return }
        if (isInitial) setLoading(false)
        setPermFetching(false)
        // The server's answer ends a pending list (unless our own save is still in
        // flight). No answer while pending: keep it marked and look again shortly.
        if (!restrictedPatchInFlight.current) {
          const retry = reconcileRetry.current
          const offline = typeof navigator !== 'undefined' && navigator.onLine === false
          if (answered) {
            retry.attempts = 0
            // A re-send that just started keeps the list pending until it answers.
            if (!pendingRestrictedWrite.current) setListPending(null)
          } else if (listPendingRef.current && offline) {
            // Offline: the browser's `online` event triggers the next look — no timer burn.
            setListPending(p => (p ? { ...p, offline: true, retrying: false } : p))
          } else if (listPendingRef.current && retry.attempts < 20) {
            retry.attempts++
            // An unsaved change keeps saying it will be saved; only a pure confirmation
            // check (nothing waiting to be sent) reads "Retrying…".
            const unsaved = !!pendingRestrictedWrite.current
            setListPending(p => (p ? { ...p, offline: unsaved, retrying: !unsaved } : p))
            if (retry.timer) clearTimeout(retry.timer)
            retry.timer = setTimeout(() => { retry.timer = null; setPermVersion(v => v + 1) }, 3000)
          } else if (listPendingRef.current) {
            setListPending(p => (p ? { ...p, stalled: true } : p))
          }
        }
        setHasLoadedOnce(true)
      })

    return () => { cancelled = true }
  }, [folderId, tenantId, permVersion, reloadAfterDrop]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Realtime: refetch on org:access_changed ──────────────────────────────
  //
  // Server emits `org:access_changed` from the POST/DELETE handlers in
  // features/orgs/api/folder-permissions.ts whenever a grant is set or
  // removed. Bumping permVersion here re-runs the fetch effect so:
  //   - the "Inherit from parent" section reflects parent-folder changes
  //   - hallway viewers reflect descendant grants set elsewhere
  //   - other admins viewing this same folder see each other's edits live
  // The event is tenant-scoped (no folderId in the payload), so we refetch
  // on any access change in this tenant. Refetches are silent (no loading
  // flag) since this admin already has the previous data on screen.
  // At most one refetch per interval (`createAccessBurst`, #1259): an admin's run of team edits
  // is one event per click, and each refetched the whole dialog. Not skipped for a change naming
  // another member — this dialog lists PEOPLE.
  const [accessBurst] = useState(() => createAccessBurst(() => {
    // Mid-save: a fetch now would race our write; ask once the save settles.
    if (restrictedPatchInFlight.current) { refetchAfterSave.current = true; return }
    setPermVersion(v => v + 1)
  }))
  useEffect(() => {
    const off = onOrgEvent((event) => {
      if (event.type === 'org:access_changed') {
        // Our own Restricted write: its reply already put the confirmed list on screen.
        if (event.changeId && ownChangeIds.current.delete(event.changeId)) return
        setAccessChangeQueued(true)
        accessBurst.push()
      }
    })
    return off
  }, [onOrgEvent, accessBurst])
  // Only unmount drops a pending reaction — a re-subscribe (a new `onOrgEvent`) keeps it, or the change it was
  // for would never be read.
  useEffect(() => () => { accessBurst.cancel() }, [accessBurst])

  // ── Escape closes the dialog ──────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Close the sharing-toggle dropdown on outside click. Menu/trigger
  // already stopPropagation their own mousedown, so any mousedown that
  // bubbles to the document is by definition "outside". Deferred
  // attach so the click that opens the dropdown doesn't immediately
  // close it (mirrors AddPicker's pattern). Escape is intentionally
  // delegated to the existing dialog handler — it closes the whole
  // dialog (unmounting the dropdown with it).
  useEffect(() => {
    if (!sharingDropdownFor) return
    const handler = () => setSharingDropdownFor(null)
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handler)
    }
  }, [sharingDropdownFor])

  // ── Drag cleanup safety net ───────────────────────────────────────────────
  //
  // The Tag's React `onDragEnd` is bound to its <span>, but during a drag we
  // call `setActiveTab(perm)` on `onTabDragOver` which unmounts the source
  // span (it lived on the previous tab). When the user releases, the
  // unmounted element never fires React's `onDragEnd`, so `draggingTag` and
  // `trashHover` get stuck → the purple ring on the sidebar tab and the
  // trash overlay both persist after drop. A window-level `dragend` fires
  // regardless of source mount state, so it's the reliable place to clean up.
  useEffect(() => {
    if (!draggingTag) return
    const cleanup = () => {
      setDraggingTag(null)
      setDragOverTab(null)
      setTrashHover(false)
      setUserListHover(false)
    }
    window.addEventListener('dragend', cleanup)
    return () => window.removeEventListener('dragend', cleanup)
  }, [draggingTag])

  // ── Tag list for the active tab ───────────────────────────────────────────
  //
  // Sort: roles → teams → members, alphabetical within each. Explicit before
  // inherited (inherited entries render in their own section, see below).

  // The People and "Mini App & AI" views share this ENTIRE block (sidebar +
  // container + cards + drag/add/remove) — the only difference is which
  // principals show: `miniapp` shows app grants, `people` shows everyone else.
  // The App view has no hallway list, and now genuinely carries none: the
  // server stops emitting app rows for `viaDescendant` at all, because an app
  // does not navigate the drive and so is never transiting a folder to reach a
  // grant inside it. This tab therefore shows exactly the apps granted ON this
  // folder.
  //
  // (App rows DID reach here once, under People & teams, labelled with the raw
  // id of the app's `.pdapp` folder. Dropping them at the source is what fixed
  // it; the client-side filter that briefly moved them into this tab is gone
  // with them.)
  const isAppView = viewMode === 'miniapp'
  const viewTags = useMemo(
    () => tags.filter(t => isAppView ? t.type === 'app' : t.type !== 'app'),
    [tags, isAppView],
  )
  const viewVia = isAppView ? [] : viaDescendant

  const visibleTags = useMemo(() => {
    // 'all' is rendered separately as a per-permission grouped view —
    // the single-tab path is unused, so just return empty slices.
    if (activeTab === 'all') return { explicit: [] as AccessTag[], inherited: [] as AccessTag[] }
    const onTab = viewTags.filter(t => t.permission === activeTab)
    const explicit = onTab.filter(t => !t.inherited)
    // The server sends the inherited list AFTER the Restricted cut — which
    // grants from above survive depends on who holds them (#787 §2) — so it is
    // shown as sent, on every tab.
    const inherited = onTab.filter(t => t.inherited)

    const TYPE_ORDER: Record<TagType, number> = { role: 0, team: 1, member: 2, app: 3 }
    const sorter = (a: AccessTag, b: AccessTag) => {
      if (TYPE_ORDER[a.type] !== TYPE_ORDER[b.type]) return TYPE_ORDER[a.type] - TYPE_ORDER[b.type]
      return a.label.localeCompare(b.label)
    }
    return { explicit: explicit.sort(sorter), inherited: inherited.sort(sorter) }
  }, [viewTags, activeTab])

  /** Sidebar badge count for tab `p`. Always matches what's actually rendered
   *  on the tab: explicit + inherited + hallway. The hallway/descendant
   *  section was previously omitted from this count even though those
   *  cards are visible on the same tab — a discrepancy users could spot
   *  ("count says 1 but I see 2"). */
  const countForTab = (p: ActiveTab): number => {
    if (p === 'all') {
      // Sum each tab's count so the 'all' badge equals what users see
      // when they scan the per-permission groups in the right pane.
      return tabs.reduce((sum, t) => sum + countForTab(t), 0)
    }
    const onTab = viewTags.filter(t => t.permission === p)
    const hallwayOnTab = viewVia.filter(v => v.permission === p).length
    return onTab.length + hallwayOnTab
  }

  // ── Mutation handlers (auto-save, optimistic) ─────────────────────────────

  const handleRemove = async (tag: AccessTag) => {
    if (tag.inherited || !tag.permId) return  // X is only on explicit
    const tk = tagKey(tag)
    // Lazy remove (symmetric with handleAdd): keep the pill visible with
    // a saving-state dim (`savingId` → opacity-50 on the Tag) while the
    // DELETE is in flight, then remove on success or surface the error
    // toast on failure. The earlier optimistic-then-rollback pattern made
    // the pill vanish-then-reappear when the server rejected (hierarchy
    // guard, or issue-#73 self-protection), reading as a jarring blink
    // since the grid reflows in both directions.
    setSavingId(tk)
    try {
      // App principals share the generic permission endpoint (DELETE by permId).
      // The grant is gated on THIS folder's manage; the app's `.chain` (in the
      // app's own folder, which the folder admin can't touch) is managed by the
      // app owner in the mini-app editor.
      await request(`/api/folders/${folderId}/permissions/${tag.permId}`, { method: 'DELETE' })
      setTags(curr => curr.filter(t => tagKey(t) !== tk))
    } catch (err) {
      // request() throws Error(server message), so err.message is the
      // actionable string ("Cannot remove a permission set by a higher-
      // level admin" or "...remove your Manage & Edit access...").
      const msg = err instanceof Error && err.message ? err.message : 'Failed to remove permission'
      toast.error(msg)
      console.error('[WhoHasAccessDialog] failed to remove permission', err)
    } finally {
      setSavingId(null)
    }
  }

  const handleAdd = async (
    type: TagType,
    targetId: string,
    label: string,
    permission: Permission,
    /** A freshly picked app's icon (the picker already holds it). */
    appIcon?: { spec: MiniappIconSpec | null; rev: string | null },
  ) => {
    // If the same target already exists at the same level, no-op.
    const existing = tags.find(t => t.type === type && t.targetId === targetId && !t.inherited)
    if (existing && existing.permission === permission) return

    // App principals grant through the SAME generic endpoint as people —
    // gated on THIS folder's manage (which the dialog already requires), so
    // drag/add/remove behave identically. The `.chain` into the app's own
    // folder is left to the app owner in the mini-app editor (which prompts
    // for it) — the folder admin can't write there.
    const targetType = type === 'role' ? 'role' : type === 'team' ? 'group' : type === 'app' ? 'app' : 'user'
    setPickerOpen(false)
    try {
      const resp = await request<{ ok: boolean; permission: { id: string }; narrowings?: Narrowing[]; mirrored?: Array<{ folderId: string; permission: string }> }>(`/api/folders/${folderId}/permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetType, targetId, permission }),
      })
      if (resp.narrowings && resp.narrowings.length > 0) {
        setNarrowingReview({ granteeLabel: label.replace(/^(role|team):/, ''), narrowings: resp.narrowings })
      }
      // D18: an app's grant is narrowed to YOUR access below this folder (it never reaches more
      // than you). A count, not names — a hidden file's name must not surface here.
      if (type === 'app' && resp.mirrored && resp.mirrored.length > 0) {
        const n = resp.mirrored.length
        toast.info(`${label} was limited to your own access in ${n} place${n === 1 ? '' : 's'} below this folder — it can't reach what you can't.`)
      }
      // Replace any existing explicit row for this target (different perm) with the new one.
      setTags(curr => {
        const filtered = curr.filter(t => !(t.type === type && t.targetId === targetId && !t.inherited))
        // Carry the app's path labels across a retier (a fresh add lacks them
        // until the next refetch, which fills the disambiguated subline).
        const prevApp = curr.find(t => t.type === 'app' && t.targetId === targetId)
        return [
          ...filtered,
          {
            type, targetId, label, permission,
            inherited: false,
            permId: resp.permission?.id,
            ...(type === 'app' ? {
              appPath: prevApp?.appPath, appFullPath: prevApp?.appFullPath,
              appIcon: appIcon ? appIcon.spec : prevApp?.appIcon,
              appIconRev: appIcon ? appIcon.rev : prevApp?.appIconRev,
            } : {}),
          },
        ]
      })
      // Highlight + scroll to the new tag.
      const newKey = `${type}:${targetId}:e`
      setRecentlyAdded(prev => new Set(prev).add(newKey))
      const timerId = setTimeout(() => {
        flashTimers.current.delete(timerId)
        setRecentlyAdded(prev => {
          const next = new Set(prev)
          next.delete(newKey)
          return next
        })
      }, 1500)
      flashTimers.current.add(timerId)
    } catch (err) {
      // Success toasts stay suppressed (per-grant toasts were noisy), but
      // failures need to surface — without optimistic insert the user
      // otherwise sees nothing happen and has no idea why (e.g.
      // "Cannot override a permission set by a higher-level admin" from
      // the server-side authority check). request() throws Error(server
      // message), so passing err.message through gives the actionable text.
      const msg = err instanceof Error && err.message ? err.message : 'Failed to grant permission'
      toast.error(msg)
      console.error('[WhoHasAccessDialog] failed to grant permission', err)
    }
  }

  // ── Allow-sharing toggle ────────────────────────────────────────────────
  //
  // Optimistic flip + write. On error we roll back the local Map and toast; on
  // success the server emits `org:access_changed`, which triggers a refetch that
  // re-seeds the Map from the server payload (idempotent — local state already
  // matches if the write succeeded).
  //
  // #206 — this toggle is FOLDER-scoped: every principal type writes a
  // `folder_permissions` grant on THIS folder (targetType user/group/role/app),
  // so toggling on one folder no longer flips the principal on EVERY folder. A
  // grant on the drive root behaves org-wide; the Settings → Permissions page
  // still writes the org-wide role default, which is the fallback when a folder
  // carries no override.
  const handleSetSharing = async (tag: AccessTag, allowed: boolean) => {
    const k = tagKey(tag)
    const prevValue = sharingByTag.get(k) ?? true
    // On one of the drive's own folders a person / team / role card shows the EFFECTIVE
    // value, so picking it again is not a change — and writing it would store a value
    // where there was none, which then passes down to everything inside.
    // (Not the owner ROLE card: it shows the role's value, not what each owner gets.)
    if (structuralSharing && tag.type !== 'app' && !(tag.type === 'role' && tag.targetId === 'owner') && prevValue === allowed) {
      setSharingDropdownFor(null)
      return
    }
    setSharingByTag(prev => {
      const next = new Map(prev)
      next.set(k, allowed)
      return next
    })
    setSharingDropdownFor(null)
    try {
      if (tag.type === 'app') {
        // App re-share flag lives on the folder_permission row — re-upsert the
        // grant (same tier) with the new allowSharing via the generic endpoint,
        // gated on THIS folder's manage. People route through /capabilities.
        await request(`/api/folders/${folderId}/permissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetType: 'app', targetId: tag.targetId, permission: tag.permission, allowSharing: allowed }),
        })
      } else {
        // #206 — folder-scoped: write the allow_sharing flag onto THIS folder's
        // grant for the principal (nearest-ancestor resolution; a grant on the
        // drive root is org-wide). Re-upsert at the SAME tier so only the flag
        // changes. Replaces the old org-wide PATCH /capabilities, which flipped the
        // toggle on EVERY folder despite the per-folder UI (the #206 bug). The
        // Settings → Permissions role page still writes the org-wide default, which
        // remains the fallback when a folder carries no override.
        // `tag.permission` is a folder-permission tier (hidden/readcopy/readwrite/
        // admin) — never `owner` (the Permission type excludes it), so it upserts
        // as-is at the same tier.
        await request(`/api/folders/${folderId}/permissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targetType: tag.type === 'team' ? 'group' : tag.type === 'role' ? 'role' : 'user',
            targetId: tag.targetId,
            permission: tag.permission,
            allowSharing: allowed,
          }),
        })
      }
    } catch (err) {
      // Rollback the optimistic flip on failure.
      setSharingByTag(prev => {
        const next = new Map(prev)
        next.set(k, prevValue)
        return next
      })
      const msg = err instanceof Error && err.message ? err.message : 'Failed to update sharing capability'
      toast.error(msg)
      console.error('[WhoHasAccessDialog] failed to set allow_sharing', err)
    }
  }

  // ── Restricted toggle (issue #71) ────────────────────────────────────────
  //
  // Flip optimistically, fire PATCH, roll back + toast on error. Mirrors
  // the add/remove pattern. Yellow flash kicks off in the same tick so
  // the user gets visual confirmation even before the server responds.
  // Manage & Edit is NOT in the flashing set — admin grants pass through
  // the restricted boundary unchanged, so that tab visibly doesn't shift.
  const handleToggleRestricted = async () => {
    // #106: refuse to act on a known-stale switch. While a permissions
    // (re)fetch is in flight (incl. the org:access_changed refetch or a
    // post-remount initial load) or a PATCH is pending, `restricted` may
    // not reflect the server — `next = !restricted` would persist the
    // wrong absolute value to an access-control flag. The button is also
    // `disabled` in these states; this is the defense-in-depth guard for
    // a programmatic dispatch that bypasses `disabled`.
    if (restrictedLocked) return
    const next = !restricted
    setRestricted(next)
    const prev = lastFetchRef.current
    const shown = prev ? previewOf(prev.permData, next) : null
    if (prev && shown) {
      lastFetchRef.current = { ...prev, permData: shown }
      applyFetched(shown, prev.teamData, prev.memberData)
    }
    setListPending({ previewed: !!shown })
    const flashTabs = new Set<Permission>(['readwrite', 'readcopy', 'hidden'])
    setFlashingTabs(flashTabs)
    // CSS animation is 900ms; clear at 1000ms so the class is removed
    // after the animation finishes, ready to re-apply on the next toggle.
    const flashTimerId = setTimeout(() => {
      flashTimers.current.delete(flashTimerId)
      setFlashingTabs(new Set())
    }, 1000)
    flashTimers.current.add(flashTimerId)
    await sendRestricted(next, restrictedVersion, prev, !!shown)
  }

  /** The server's answer with this folder's Restricted flag shown as `next` —
   *  its own `whenRestrictedToggled` lists applied. Null when the answer carries
   *  no preview, or already shows `next`. */
  function previewOf(base: PermissionsResponse, next: boolean): PermissionsResponse | null {
    const pv = base.whenRestrictedToggled
    if (!pv || !!base.restricted === next) return null
    return {
      ...base,
      restricted: next,
      inherited: pv.inherited,
      inheritedApps: pv.inheritedApps,
      viaDescendant: pv.viaDescendant ?? base.viaDescendant,
      whenRestrictedToggled: { inherited: base.inherited ?? {}, inheritedApps: base.inheritedApps, viaDescendant: base.viaDescendant },
    }
  }

  /**
   * PATCH the flag to `next` from `expectedVersion` — the click, or the re-send of a
   * change made offline. `prev` is the server answer from before the change (what a
   * refusal puts back); `previewed` says the list on screen already shows `next`.
   */
  async function sendRestricted(
    next: boolean,
    expectedVersion: number,
    prev: { permData: PermissionsResponse; teamData: { teams: TeamInfo[] }; memberData: { members: MemberInfo[]; invites?: MemberInfo[] } } | null,
    previewed: boolean,
  ): Promise<void> {
    const resend = !!pendingRestrictedWrite.current
    /** Put the list back as it was before the change (the switch with it). */
    const restorePrevious = () => {
      setRestricted(!next)
      if (prev && previewed) {
        lastFetchRef.current = prev
        applyFetched(prev.permData, prev.teamData, prev.memberData)
      }
    }
    const changeId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`
    ownChangeIds.current.add(changeId)
    const savedFolderId = folderId
    setListPending({ previewed })
    setRestrictedSaving(true)
    restrictedPatchInFlight.current = true
    try {
      const resp = await request<{ ok: boolean; restricted: boolean; restrictedVersion?: number; access?: PermissionsResponse }>(
        `/api/folders/${savedFolderId}/restricted`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          // #106: echo the version we read so the server can reject a
          // stale write (409) instead of silently clobbering a
          // concurrent admin's change.
          body: JSON.stringify({ restricted: next, expectedVersion, changeId }),
        },
      )
      restrictedPatchInFlight.current = false
      pendingRestrictedWrite.current = null
      const base = lastFetchRef.current ?? prev
      if (resp?.access && base && currentFolderId.current === savedFolderId) {
        // The reply carries the confirmed list after the write — the same answer a
        // refetch would give, one round trip sooner. Whatever the preview showed
        // (even a stale one) is replaced by it here.
        lastFetchRef.current = { ...base, permData: resp.access }
        applyFetched(resp.access, base.teamData, base.memberData)
        setListPending(null)
      } else {
        // Older server, or the confirmed list failed: ask for it. Which inherited
        // grants survive the boundary depends on who holds them (#787 §2) and only
        // the server resolves that.
        if (typeof resp?.restrictedVersion === 'number') setRestrictedVersion(resp.restrictedVersion)
        setPermVersion(v => v + 1)
      }
      if (resend) toast.success('Restricted saved')
    } catch (err) {
      restrictedPatchInFlight.current = false
      ownChangeIds.current.delete(changeId)
      const status = (err as { status?: number } | null)?.status
      if (isSessionEndedError(err)) {
        // #1316 review round 1: the answer was dropped because the session that sent it ended — not "no answer": never
        // kept to re-send (the re-send would go out as the NEXT identity) and nothing said. The list is read again, as
        // the session now here, and shows what is true for it.
        pendingRestrictedWrite.current = null
        setListPending(null)
        setPermVersion(v => v + 1)
      } else if (status === 409) {
        // #106: a concurrent admin (or our own silently-reset dialog)
        // moved the flag under us. Do NOT blind-invert to a guessed
        // value — put the previous list back (the preview is now a guess
        // about a state that didn't happen) and reconcile to server truth via
        // a refetch, which re-seeds `restricted` + `restrictedVersion`; the
        // toggle stays disabled while it is in flight (permFetching).
        pendingRestrictedWrite.current = null
        restorePrevious()
        setListPending({ previewed: false })
        setPermVersion(v => v + 1)
        const msg = err instanceof Error && err.message
          ? err.message
          : 'Restricted changed elsewhere — reloaded the current value'
        toast.error(msg)
        console.warn('[WhoHasAccessDialog] restricted version conflict — reconciled', err)
      } else if (typeof status !== 'number') {
        // No HTTP answer (offline, network drop, timeout): the write may or may not
        // have landed. Keep the change on screen and remember it; the first answer
        // after reconnecting decides — saved, re-sent, or someone else's change wins.
        const wasResend = !!pendingRestrictedWrite.current
        if (!wasResend) {
          toast.error("Couldn't reach the server — Restricted will be saved when the connection is back")
        }
        pendingRestrictedWrite.current = { next, baseVersion: expectedVersion, folderId: savedFolderId }
        // Say so at once — not only when the browser reports itself offline
        // (`navigator.onLine` stays true under DevTools throttling in some browsers,
        // and when the server, not the device, is unreachable).
        setListPending(p => ({ ...(p ?? { previewed }), offline: true, retrying: false }))
        if (wasResend) {
          // The re-send failed too (a flaky link): don't loop straight back — the next
          // look is the paced retry (or the browser's `online` event).
          const retry = reconcileRetry.current
          if (retry.timer) clearTimeout(retry.timer)
          retry.timer = setTimeout(() => { retry.timer = null; setPermVersion(v => v + 1) }, 3000)
        } else {
          setPermVersion(v => v + 1)
        }
        console.warn('[WhoHasAccessDialog] restricted: no answer — will save on reconnect', err)
      } else {
        // The server refused (4xx/5xx): nothing changed — put the list back.
        pendingRestrictedWrite.current = null
        restorePrevious()
        setListPending(null)
        const msg = err instanceof Error && err.message ? err.message : 'Failed to toggle Restricted'
        toast.error(msg)
        console.error('[WhoHasAccessDialog] failed to toggle restricted', err)
      }
    } finally {
      setRestrictedSaving(false)
      // Someone else changed access while we saved: their state may postdate the
      // list we just applied — one fresh look.
      if (refetchAfterSave.current) {
        refetchAfterSave.current = false
        setPermVersion(v => v + 1)
      }
    }
  }

  // ── Drag handlers ──────────────────────────────────────────────────────────
  //
  // Native HTML5 DnD. The UI does three things during a drag:
  //   - Show a trash drop target (top-right of right pane).
  //   - Reflect the active tab to whichever tab the cursor is over (so the
  //     user sees what they're about to drop into). The active-tab change
  //     during drag is purely visual — onDrop reads the target tab from the
  //     drop handler args, not from the React state.
  //   - On file mode, the "No access" tab refuses drops (no preventDefault).

  const onTagDragStart = (tag: AccessTag) => (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move'
    // Some browsers start a drag only with data set. A constant, never the tag's
    // id: whatever the drag crosses can read it (#750). onDrop reads draggingTag.
    e.dataTransfer.setData('text/plain', 'papan-access-tag')
    setDraggingTag(tag)
  }

  const onTagDragEnd = () => {
    setDraggingTag(null)
    setTrashHover(false)
  }

  const isDropTabAllowed = (perm: Permission): boolean => {
    // File: "No access" cannot be set explicitly on a file (only inherited).
    if (perm === 'hidden' && !isFolder) return false
    return true
  }

  const onTabDragOver = (perm: Permission) => (e: React.DragEvent) => {
    if (!draggingTag) return
    if (!isDropTabAllowed(perm)) {
      e.dataTransfer.dropEffect = 'none'
      return  // Don't preventDefault → drop won't fire, cursor shows not-allowed.
    }
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    // Visual feedback only — do NOT mutate activeTab here. Switching activeTab
    // mid-drag unmounts the source <span>, and `dragend` events from detached
    // nodes don't bubble to window, leaving the UI stuck.
    if (dragOverTab !== perm) setDragOverTab(perm)
  }

  const onTabDragLeave = (perm: Permission) => () => {
    // Clear ring when cursor leaves a tab — only if it's still pointing at
    // this perm (avoids race where dragenter on the next tab already updated).
    if (dragOverTab === perm) setDragOverTab(null)
  }

  const onTabDrop = (perm: Permission) => async (e: React.DragEvent) => {
    e.preventDefault()
    if (!draggingTag) return
    if (!isDropTabAllowed(perm)) return
    const dt = draggingTag
    setDraggingTag(null)
    setDragOverTab(null)
    // Same level + same target = no-op
    if (!dt.inherited && dt.permission === perm) return
    // Switch active tab so the user sees the tag land at its new home.
    setActiveTab(perm)
    // Reuse handleAdd: it POSTs upsert and replaces any existing explicit
    // entry for the same target. For inherited drag → make explicit at this
    // level, the same path works (no existing explicit row to replace).
    await handleAdd(dt.type, dt.targetId, dt.label, perm)
  }

  const onTrashDragOver = (e: React.DragEvent) => {
    if (!draggingTag) return
    if (draggingTag.inherited) return  // Inherited entries can't be removed via trash.
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setTrashHover(true)
  }

  const onTrashDragLeave = () => setTrashHover(false)

  const onTrashDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setTrashHover(false)
    if (!draggingTag || draggingTag.inherited) return
    const dt = draggingTag
    setDraggingTag(null)
    await handleRemove(dt)
  }

  // ── User-list drop zone ───────────────────────────────────────────────────
  // Mirror of the trash zone, but on the explicit-pills row. Drops here are
  // equivalent to dropping on the active sidebar tab — visualising the area
  // as a drop target makes the affordance discoverable for users who haven't
  // figured out the sidebar-tab drop yet.
  const onUserListDragOver = (e: React.DragEvent) => {
    if (!draggingTag) return
    if (activeTab === 'all' || !isDropTabAllowed(activeTab)) {
      e.dataTransfer.dropEffect = 'none'
      return
    }
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (!userListHover) setUserListHover(true)
  }

  const onUserListDragLeave = () => setUserListHover(false)

  const onUserListDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setUserListHover(false)
    if (!draggingTag) return
    if (activeTab === 'all' || !isDropTabAllowed(activeTab)) return
    const dt = draggingTag
    setDraggingTag(null)
    setDragOverTab(null)
    // Same level + same target = no-op (matches onTabDrop semantics).
    if (!dt.inherited && dt.permission === activeTab) return
    await handleAdd(dt.type, dt.targetId, dt.label, activeTab)
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return createPortal(
    // Outer = scroll container. When the viewport is too short to fit
    // the modal at its desired height (h-[85vh] with min-h-[600px]),
    // overflow-y-auto lets the page scroll the modal instead of
    // shrinking it. Click on the outer (anywhere but the modal itself)
    // closes the dialog.
    <div className="fixed inset-0 z-[100] overflow-hidden md:overflow-y-auto" onClick={onClose}>
      <div className="relative min-h-full bg-black/50 flex items-center justify-center p-0 md:p-4">
      <div
        // `data-picker-bounds`: AddPicker keeps its popover within THIS box
        // horizontally. Anchored to the viewport alone, a right-hand "+ Add"
        // put most of the panel outside the dialog, floating over the page —
        // correctly positioned and visually detached from what it belongs to.
        data-picker-bounds=""
        className={`relative z-10 rounded-none md:rounded-lg shadow-xl w-full md:max-w-3xl ${themeClass} themed-bg-secondary flex flex-col h-[100svh] md:h-[660px] max-h-[100svh] md:max-h-[88vh]`}
        onClick={(e) => e.stopPropagation()}
      >
        {narrowingReview && (
          <NarrowingReview
            granteeLabel={narrowingReview.granteeLabel}
            narrowings={narrowingReview.narrowings}
            teamName={(id) => allTeams.find(t => t.id === id)?.name}
            onClose={() => setNarrowingReview(null)}
          />
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 pt-4 md:pt-5 pb-3 border-b themed-border">
          <div className="min-w-0">
            {/* Phone: the FOLDER is the title and "Who has access" is the
                eyebrow. Inverted, the screen shouted its own label in 18px
                semibold while the one fact that identifies what you are
                looking at sat muted underneath — and it is the folder name a
                reader needs when several of these look alike. Desktop keeps
                the sentence, where the width makes it read naturally. */}
            <div className="md:hidden min-w-0">
              <p className="text-[10px] uppercase tracking-wider themed-text-muted leading-none mb-1">Who has access</p>
              <h3 className="text-base font-semibold themed-text flex items-center gap-1.5 min-w-0">
                {isFolder
                  ? <Folder size={16} className="text-yellow-500 shrink-0" />
                  : <File size={16} className="themed-text-muted shrink-0" />}
                <span className="truncate">{folderName}</span>
              </h3>
            </div>
            <h3 className="hidden md:flex text-lg font-semibold themed-text items-center gap-1.5 flex-wrap">
              <span>Who has access to</span>
              {folderName && (
                <Tooltip content={folderPath ?? ''} always followCursor>
                  <span className="inline-flex items-center gap-1.5 themed-text-muted font-normal min-w-0">
                    {isFolder
                      ? <Folder size={18} className="text-yellow-500 shrink-0" />
                      : <File size={18} className="themed-text-muted shrink-0" />}
                    <span className="truncate">{folderName}</span>
                  </span>
                </Tooltip>
              )}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded shrink-0 ml-3">
            <X size={18} />
          </button>
        </div>


        {/* Why the list is empty. Without this the refusal is indistinguishable
            from "nobody has access to this folder" — the same blank pane, and
            the wrong reading is the one that sticks once the toast fades. */}
        {loadError && (
          <div className="mx-4 md:mx-6 mt-4 flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5">
            <ShieldAlert size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm themed-text">{loadError}</p>
          </div>
        )}

        {/* Step 9b: top-level mode switch — "People & teams" vs "Outsiders".
            Sits above the right pane only (not over the Permission column):
            rendered at the top of the people right-pane below, and passed to
            OutsiderTab via topSlot. */}
        {/* PHONE: the scope control, rendered ONCE for every view.
            It used to be rendered by each view's own branch — three call
            sites at three different nesting depths (Outsiders puts it inside
            a padded scrolling wrapper, the other two inside their right
            column). At rest each looked right; switching moved the control
            VERTICALLY under the reader's finger, which is the worst place for
            a control to move: you tap a tab and the thing you tapped is
            somewhere else. Rendering it above the branches makes its position
            a property of the dialog rather than of whichever view happens to
            be showing. Desktop keeps the per-branch strip, which has to line
            up with the pane beside the sidebar. */}
        {!isDesktop && (
          <div className="px-4 pt-3 pb-3 shrink-0">
            <ViewModeTabs viewMode={viewMode} onChange={setViewMode} hidePeople={hidePeopleTeams} />
          </div>
        )}

        {viewMode === 'outsider' && (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden md:overflow-y-auto px-0 md:px-6 py-0 md:py-4">
            <OutsiderTab
              folderId={folderId}
              folderName={folderName}
              folderPath={folderPath}
              myPermission={myPermission}
              isFolder={isFolder}
              personal={hidePeopleTeams}
              topSlot={<ViewModeSwitch viewMode={viewMode} onChange={setViewMode} hidePeople={hidePeopleTeams} thumbRef={viewTabThumb} />}
            />
          </div>
        )}

        {viewMode === 'share-links' && (
          <div className="flex flex-col md:flex-row flex-1 min-h-0 mx-0 md:mx-6 mt-3 md:mt-4 mb-0 md:mb-6">
            {/* Sidebar — parent tabs + indented sub-filter tabs.
                Selecting a sub-filter snaps the parent into context AND
                narrows the right pane; the parent button itself acts as
                the "show everything in this view" affordance. */}
            <nav className="w-48 shrink-0 hidden md:flex flex-col gap-0.5 py-2 pr-2 mr-2">
              {/* Links section. Flat list of icon-bearing pills under a
                  section heading, matching the People-view sidebar.
                  Selecting any item flips the active sub-tab so the
                  right pane renders the right slice. */}
              <div className="hidden md:block text-[10px] uppercase tracking-wider themed-text font-medium py-2 mb-1 ml-3">
                Links
              </div>
              <SidebarTab
                label="All Links"
                tooltip="All share links for this file, including external reshares."
                count={shareLinkPrincipals.rows.length + shareLinkPrincipals.inboundReshares.length + shareLinkPrincipals.viaShortcut.length}
                active={shareLinksSubTab === 'links' && linksFilter === 'all'}
                onClick={() => { setShareLinksSubTab('links'); setLinksFilter('all') }}
              />
              {/* Ordered closed → open, matching the share dialog. Labels and
                  tooltips come from the shared audience tables so this sidebar
                  cannot describe a bucket differently from the chip on the rows
                  it filters to. */}
              <SidebarTab
                Icon={Home}
                label={AUDIENCE_LABEL.private}
                tooltip={AUDIENCE_HINT.private}
                count={audienceCounts.private}
                active={shareLinksSubTab === 'links' && linksFilter === 'private'}
                onClick={() => { setShareLinksSubTab('links'); setLinksFilter('private') }}
              />
              <SidebarTab
                Icon={Lock}
                label={AUDIENCE_LABEL.restricted}
                tooltip={AUDIENCE_HINT.restricted}
                count={audienceCounts.restricted}
                active={shareLinksSubTab === 'links' && linksFilter === 'restricted'}
                onClick={() => { setShareLinksSubTab('links'); setLinksFilter('restricted') }}
              />
              <SidebarTab
                Icon={Globe}
                label={AUDIENCE_LABEL.public}
                tooltip={AUDIENCE_HINT.public}
                count={audienceCounts.public}
                active={shareLinksSubTab === 'links' && linksFilter === 'public'}
                onClick={() => { setShareLinksSubTab('links'); setLinksFilter('public') }}
              />
              <div className="hidden md:block text-[10px] uppercase tracking-wider themed-text font-medium py-2 mb-1 ml-3 mt-3">
                Recipients
              </div>
              <SidebarTab
                label="All Recipients"
                tooltip="Everyone who can open this file through a link."
                count={recipientAggregates.length}
                active={shareLinksSubTab === 'recipients' && recipientsFilter === 'all'}
                onClick={() => { setShareLinksSubTab('recipients'); setRecipientsFilter('all') }}
              />
              <SidebarTab
                Icon={SquarePen}
                label="Edit"
                tooltip="People who can edit."
                count={recipientsBucketCounts.edit}
                active={shareLinksSubTab === 'recipients' && recipientsFilter === 'edit'}
                onClick={() => { setShareLinksSubTab('recipients'); setRecipientsFilter('edit') }}
              />
              <SidebarTab
                Icon={PDAppIcon}
                label="App"
                tooltip="People who can open the file as an app."
                count={recipientsBucketCounts.app}
                active={shareLinksSubTab === 'recipients' && recipientsFilter === 'app'}
                onClick={() => { setShareLinksSubTab('recipients'); setRecipientsFilter('app') }}
              />
              <SidebarTab
                Icon={Eye}
                label="View (Live Edit)"
                liveDot
                tooltip="People who see your edits as they happen."
                count={recipientsBucketCounts['view-live']}
                active={shareLinksSubTab === 'recipients' && recipientsFilter === 'view-live'}
                onClick={() => { setShareLinksSubTab('recipients'); setRecipientsFilter('view-live') }}
              />
              <SidebarTab
                Icon={Eye}
                label="View (Last Save)"
                tooltip="People who see the last saved version."
                count={recipientsBucketCounts['view-save']}
                active={shareLinksSubTab === 'recipients' && recipientsFilter === 'view-save'}
                onClick={() => { setShareLinksSubTab('recipients'); setRecipientsFilter('view-save') }}
              />
              <SidebarTab
                Icon={Ban}
                label="Blocked"
                tooltip="People who can't open the link."
                count={recipientsBucketCounts.blocked}
                active={shareLinksSubTab === 'recipients' && recipientsFilter === 'blocked'}
                onClick={() => { setShareLinksSubTab('recipients'); setRecipientsFilter('blocked') }}
              />
            </nav>
            {/* Right column — view switch (above the box) + bordered pane. */}
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
              <ViewModeSwitch viewMode={viewMode} onChange={setViewMode} hidePeople={hidePeopleTeams} thumbRef={viewTabThumb} />
              {/* PHONE: which LIST, and nothing else. A segmented pair rather
                  than more chips, because these two are a choice between
                  alternatives — the shape should say "one of these", which a
                  row of pills does not. */}
              {!isDesktop && (
                /* Track darker, active segment lighter — the same rule
                   `ViewModeTabs` states and follows. Built the other way round
                   (a tertiary track with a secondary pill) the selected
                   segment matched the DIALOG's own background and read as a
                   hole punched in the strip, one row below a control doing the
                   opposite. */
                <div className="mx-4 mb-3 flex rounded-full themed-bg-primary p-1">
                  {([['links', 'Links'], ['recipients', 'Recipients']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={shareLinksSubTab === key}
                      onClick={() => setShareLinksSubTab(key)}
                      className={`flex-1 h-9 rounded-full text-sm transition-colors ${
                        shareLinksSubTab === key ? 'themed-bg-tertiary themed-text font-medium' : 'themed-text-muted'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {/* `px-4` on phone, matching the People pane and OutsiderTab.
                  At `p-0` the rounded LinkRow cards sat flush against both
                  screen edges with no gutter — the only view in this dialog
                  whose cards were cut off at the viewport. */}
              <div className={`flex-1 flex flex-col min-h-0 overflow-y-auto px-4 py-3 md:p-6 ${paneChrome}`}>
                <ShareLinkPrincipalsPane
                  fileId={folderId}
                  subTab={shareLinksSubTab}
                  filtered={shareLinksSubTab === 'links' ? linksFilter !== 'all' : recipientsFilter !== 'all'}
                  rows={visibleRows}
                  aggregates={visibleAggregates}
                  inboundReshares={visibleInboundReshares}
                  viaShortcut={linksFilter === 'all' ? shareLinkPrincipals.viaShortcut : []}
                  viaShortcutIncomplete={linksFilter === 'all' && shareLinkPrincipals.viaShortcutIncomplete}
                  onRevokeVia={handleViaShortcutRevoke}
                  loading={shareLinkPrincipals.loading}
                  error={shareLinkPrincipals.error}
                  isFolder={isFolder}
                  editingId={shareLinkEditingId}
                  busyId={shareLinkBusyId}
                  flashId={shareLinkFlashId}
                  onStartEdit={setShareLinkEditingId}
                  onCancelEdit={() => setShareLinkEditingId(null)}
                  onCopy={handleShareLinkCopy}
                  onShowQr={setQrModalUrl}
                  onRevoke={handleShareLinkRevoke}
                  onRevokeInbound={handleInboundReshareRevoke}
                  onInboundPatched={shareLinkPrincipals.patchInboundReshare}
                  onLinkPatched={handleShareLinkPatched}
                  onFocusLink={focusLinkInAllLinks}
                />
              </div>
            </div>
          </div>
        )}

        {(viewMode === 'people' || viewMode === 'miniapp') && (
        <div className="flex flex-col md:flex-row flex-1 min-h-0 mx-0 md:mx-6 mt-3 md:mt-4 mb-0 md:mb-6">
          {/* Sidebar — permission tabs.
              The nav-level dragLeave is a safety net for fast cursor
              movement: per-tab dragLeave can be missed by the browser when
              the cursor exits the sidebar quickly, leaving dragOverTab
              stuck. Checking `relatedTarget` (where the cursor went)
              against `currentTarget` (the nav itself) lets us clear the
              state precisely when the cursor leaves the whole sidebar
              while ignoring transitions between two tabs inside it. */}
          <nav
            className="w-48 shrink-0 hidden md:flex flex-col gap-0.5 py-2 pr-2 mr-2"
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setDragOverTab(null)
              }
            }}
          >
            {/* Sidebar label — sits as a centered chip with a lighter
                background so it reads as the column heading rather
                than a clickable tab. Matches the right-pane section
                headers in size + weight. */}
            <div className="hidden md:block text-[10px] uppercase tracking-wider themed-text font-medium py-2 mb-1 ml-3">
              Permission
            </div>
            {sidebarTabs.map((p) => {
              const count = countForTab(p)
              const isActive = activeTab === p
              // 'all' is a synthetic tab: not a drop target, no cap warning,
              // no flash. Uses UsersRound as icon; per-tab semantics apply
              // only to actual permission tiers.
              const isAllTab = p === 'all'
              const dropAllowed = !isAllTab && isDropTabAllowed(p)
              const dragOverThis = !isAllTab && !!draggingTag && dragOverTab === p
              const flashThis = !isAllTab && flashingTabs.has(p)
              const blockedTip = !isAllTab && !dropAllowed && draggingTag
                ? `"${labelFor(p, shareCap, isReceivedShare)}" can't be set on a file directly`
                : null
              // Received cross-tenant .shared caps internal grants at the
              // source level (min(src_cap, placement)). Flag every tier
              // ABOVE the source cap (server returns `shareCap`) with an
              // amber warning — granting at a higher tier is permitted
              // but ineffective. Fallback when `shareCap` is absent
              // (older server, network blip): warn on the top tier only,
              // matching pre-Phase-4 behavior. 'all' is exempt — it
              // doesn't represent a grant tier.
              const capIdx = shareCap ? TAB_ORDER.indexOf(shareCap) : -1
              const pIdx = isAllTab ? -1 : TAB_ORDER.indexOf(p)
              // TAB_ORDER is most-permissive first (admin=0); above-cap
              // means lower index than the cap's.
              const aboveCap = isAllTab ? false : (capIdx >= 0 ? pIdx < capIdx : p === 'admin')
              const sharedCapWarn = isReceivedShare && aboveCap
              const PermIcon = isAllTab
                ? UsersRound
                : (sharedCapWarn ? AlertTriangle : PERM_ICONS[p])
              const capTip = sharedCapWarn
                ? 'Shared from another organization — members get only the access your org was granted on this folder, no matter the level set here.'
                : null
              return (
                <Tooltip
                  key={p}
                  content={blockedTip ?? capTip}
                  always
                  followCursor={!!blockedTip}
                  className="block"
                >
                  <button
                    onClick={() => setActiveTab(p)}
                    onDragOver={isAllTab ? undefined : onTabDragOver(p)}
                    onDragLeave={isAllTab ? undefined : onTabDragLeave(p)}
                    onDrop={isAllTab ? undefined : onTabDrop(p)}
                    className={`
                      ${isAllTab ? 'w-full' : 'w-[calc(100%-0.5rem)] ml-2'} text-left px-3 py-2 text-sm rounded-full
                      transition-colors flex items-center gap-2 justify-between
                      ${dragOverThis && dropAllowed
                        ? 'bg-brand-500/25 themed-text font-medium'
                        : isActive
                          ? 'themed-bg-primary themed-text font-medium'
                          : 'themed-text-muted hover:themed-bg-primary'}
                      ${draggingTag && !dropAllowed ? 'opacity-40 cursor-not-allowed' : ''}
                      ${flashThis ? 'animate-restricted-tab-flash' : ''}
                    `}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {/* 'all' is a synthetic, non-tier tab — the icon
                          doesn't carry permission semantics here, so we
                          drop it to keep the row visually distinct from
                          the actual tier rows below. */}
                      {!isAllTab && (
                        <PermIcon size={14} className={`shrink-0 ${sharedCapWarn ? 'text-amber-400' : ''}`} />
                      )}
                      <span className="truncate">{labelFor(p, shareCap, isReceivedShare)}</span>
                    </span>
                    {count > 0 && (
                      // min-w + inline-flex centers single-digit counts in
                      // the pill so "1" reads as a circle and "12" as an
                      // oval rather than the small badge collapsing
                      // around its content. tabular-nums keeps width
                      // stable between renders that flip between
                      // single-digit and double-digit counts. justify-
                      // between on the parent floats the badge to the
                      // right edge so every row's count lines up.
                      <span className={`text-[11px] font-medium tabular-nums min-w-[20px] inline-flex items-center justify-center px-1.5 h-5 rounded-full shrink-0 ${isActive ? 'themed-bg-tertiary themed-text' : 'themed-bg-tertiary themed-text-muted'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                </Tooltip>
              )
            })}

            {/* Options section — folder-level settings that aren't
                permissions. Header matches the "Permission" label above
                exactly (same classes) so the column reads as two
                sibling sections rather than tabs + a footer. */}
            {isFolder && (
              <>
                <div className="hidden md:block text-[10px] uppercase tracking-wider themed-text font-medium py-2 mb-1 ml-3 mt-3">
                  Option
                </div>
                <Tooltip
                  content="When on, access from the folders above stops here, except for people who manage the folder above. Everyone else gets only what is granted on this folder or inside it."
                  always
                  followCursor
                  className="block"
                >
                  <button
                    type="button"
                    role="switch"
                    aria-checked={restricted}
                    aria-disabled={restrictedLocked}
                    disabled={restrictedLocked}
                    onClick={handleToggleRestricted}
                    className={`w-full text-left px-3 py-2 text-sm rounded-full themed-text-muted hover:themed-bg-primary transition-colors flex items-center justify-between ${
                      restrictedLocked ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    <span>Restricted</span>
                    <span
                      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                        restricted ? 'bg-amber-400' : 'themed-bg-tertiary'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                          restricted ? 'left-[18px]' : 'left-0.5'
                        }`}
                      >
                        {restrictedStatus?.busy && (
                          <Loader2 aria-hidden className="absolute inset-0 m-auto w-3 h-3 animate-spin text-neutral-500" />
                        )}
                      </span>
                    </span>
                  </button>
                </Tooltip>
                {/* Always rendered (fixed height): the status appearing never shifts the sidebar. */}
                <div className="h-4 px-3 flex items-center text-[11px] themed-text-muted" aria-live="polite">
                  {restrictedStatus && (
                    <span role="status" data-testid="access-list-pending" className="flex items-center gap-1 min-w-0">
                      {!restrictedStatus.busy && <AlertTriangle size={11} className="shrink-0 text-amber-400" />}
                      <span className="truncate" title={restrictedStatus.text}>{restrictedStatus.text}</span>
                    </span>
                  )}
                </div>
              </>
            )}

            {/* Trash drop target — the ONE drop-to-remove for every tab,
                pinned to the bottom of the sidebar under Restricted. It used
                to live inside the right pane, which meant it moved with the
                content, was missing entirely on All Permissions, and competed
                with the tier tabs as a drop target. One fixed spot instead.
                Mid-drag only, and only for explicit (non-inherited) tags —
                an inherited grant has no row here to delete. */}
            {draggingTag && !draggingTag.inherited && (() => {
              // Dropping an explicit grant deletes that row — but when the
              // principal ALSO inherits one, deleting it does not take their
              // access away, it hands them back the inherited level. Calling
              // that "remove" reads as "cut this person off", which is the
              // opposite of what happens, so the target says what the drop
              // will actually leave behind. Same source as the 3-dot menu's
              // "Inherit (X)" row, so the two can never disagree.
              const fallback = inheritedPermissionFor(draggingTag, inheritedRaw)
              return (
                <div
                  onDragOver={onTrashDragOver}
                  onDragLeave={onTrashDragLeave}
                  onDrop={onTrashDrop}
                  data-testid="access-trash-zone"
                  data-drop-intent={fallback ? 'inherit' : 'remove'}
                  className={`
                    mt-auto mx-1 mb-1 flex flex-col items-center justify-center gap-1.5 h-36 rounded-xl px-2 text-center
                    border-2 border-dashed
                    transition-[border-color,background-color,color,transform] duration-150
                    ${fallback
                      ? (trashHover
                        ? 'border-amber-400 bg-amber-400/20 text-amber-200 scale-[1.02]'
                        : 'border-amber-400/40 bg-amber-400/10 text-amber-300')
                      : (trashHover
                        ? 'border-red-500 bg-red-500/20 text-red-300 scale-[1.02]'
                        : 'border-red-500/40 bg-red-500/10 text-red-400')}
                  `}
                >
                  {fallback ? <Undo2 size={20} /> : <Trash2 size={22} />}
                  <span className="text-xs font-medium leading-tight">
                    {fallback ? 'Back to inherited' : 'Drop to remove'}
                  </span>
                  {fallback && (
                    <span className="text-[10px] opacity-70 leading-tight">{PERM_LABELS[fallback]}</span>
                  )}
                </div>
              )
            })()}
          </nav>

          {/* Right column — view switch (above the box) + the bordered pane. */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <ViewModeSwitch viewMode={viewMode} onChange={setViewMode} hidePeople={hidePeopleTeams} thumbRef={viewTabThumb} />
            {/* PHONE: the tier strip.
                Deliberately UNLIKE the scope control directly above it. That
                pair is what "two navigations, very weird" was about: a pill
                and a row of chips sat flush in ONE row separated by a
                hairline, so the eye read them as peers of which one behaved
                differently. Two rows now, and two different shapes — the scope
                is a filled segmented control of icons, this is a plain
                scrolling strip of text chips.

                The chips carry their COUNTS, which is most of the value: "how
                many people can edit this" is answerable without opening
                anything.

                A right-edge fade: the original strip clipped mid-word at the
                viewport edge with nothing saying more existed, which is what
                made it feel broken rather than merely full. The mask says
                "keep going" and the last chip runs under it instead of being
                guillotined.

                NO negative margin. `-mr-4` assumed a `px-4` parent, but on a
                phone the column above is full-bleed (`mx-0 md:mx-6`), so it
                pushed the strip 16px PAST the dialog — where the outer
                `overflow-hidden` clipped a third of the 48px gradient and part
                of the trailing spacer, muting the very affordance it exists to
                provide. */}
            {!isDesktop && (
              <div className="relative mb-2 shrink-0">
                <div ref={tierStripRef} className="flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {sidebarTabs.map(p => {
                    const isActive = activeTab === p
                    const count = countForTab(p)
                    return (
                      <button
                        key={p}
                        type="button"
                        aria-pressed={isActive}
                        // Scrolled into view by the effect keyed on `activeTab`,
                        // NOT from an inline ref. React re-attaches an inline ref
                        // on every commit, so this ran on every unrelated
                        // re-render — a realtime `org:access_changed` bump, the
                        // Restricted toggle, opening a row menu — yanking the
                        // strip back to the active chip while the reader was
                        // scrolled off looking at another tier's count.
                        data-tier={p}
                        onClick={() => setActiveTab(p)}
                        className={`shrink-0 whitespace-nowrap rounded-full px-3 h-8 text-[13px] flex items-center gap-1.5 transition-colors ${
                          isActive ? 'themed-bg-tertiary themed-text font-medium' : 'themed-text-muted'
                        }`}
                      >
                        <span>{p === 'all' ? 'All' : labelFor(p, shareCap, isReceivedShare)}</span>
                        <span className={`text-[11px] tabular-nums ${isActive ? 'themed-text-muted' : 'opacity-60'}`}>{count}</span>
                      </button>
                    )
                  })}
                  {/* Trailing spacer so the last chip can clear the fade. */}
                  <span className="shrink-0 w-6" aria-hidden />
                </div>
                {/* Non-interactive: it must never eat a tap meant for a chip. */}
                <div
                  className="pointer-events-none absolute inset-y-0 right-0 w-12"
                  style={{ background: 'linear-gradient(to left, var(--bg-secondary) 35%, transparent)' }}
                  aria-hidden
                />
              </div>
            )}
            {/* PHONE: Restricted, which lives in the (desktop-only) sidebar under
                its own "Option" heading. It is a SETTING, not navigation, so it
                gets a switch row rather than a pill beside the scope control —
                the whole complaint about the previous layout was two unlike
                things wearing one shape. It sits ABOVE the list because it
                changes what the list shows (under Restricted, only Manage &
                Edit inherits), so cause precedes effect, and because a
                security-relevant toggle should not be something you scroll to
                find. */}
            {!isDesktop && isFolder && (
              <button
                type="button"
                role="switch"
                aria-checked={restricted}
                aria-disabled={restrictedLocked}
                disabled={restrictedLocked}
                onClick={handleToggleRestricted}
                // FLAT, not a filled card. Given the scope control's own pill
                // treatment it became a second rounded bar of the same width
                // directly beneath it — which is the "two navigations" reading
                // this change exists to remove, rebuilt out of one control and
                // one setting. A bordered row is unmistakably a settings row.
                className={`flex w-full items-center gap-3 border-y themed-border px-4 py-2.5 text-left ${
                  restrictedLocked ? 'opacity-60' : ''
                }`}
              >
                <ShieldAlert size={16} className={`shrink-0 ${restricted ? 'text-amber-400' : 'themed-text-muted'}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm themed-text">Restricted</span>
                  {/* One line either way (truncated): the status replaces the hint in place, no shift. */}
                  {restrictedStatus ? (
                    <span role="status" aria-live="polite" data-testid="access-list-pending" className="flex items-center gap-1 text-[11px] leading-snug themed-text-muted min-w-0">
                      {!restrictedStatus.busy && <AlertTriangle size={11} className="shrink-0 text-amber-400" />}
                      <span className="truncate">{restrictedStatus.text}</span>
                    </span>
                  ) : (
                    <span className="block truncate text-[11px] leading-snug themed-text-muted">
                      Only Manage &amp; Edit inherits from parent folders
                    </span>
                  )}
                </span>
                <span
                  className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                    restricted ? 'bg-amber-400' : 'themed-bg-tertiary'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                      restricted ? 'left-[18px]' : 'left-0.5'
                    }`}
                  >
                    {restrictedStatus?.busy && (
                      <Loader2 aria-hidden className="absolute inset-0 m-auto w-3 h-3 animate-spin text-neutral-500" />
                    )}
                  </span>
                </span>
              </button>
            )}
          {/* Right pane — description (top) + tag list.
              `inert` (not `pointer-events-none`) at the read rung of
              `manage_folder_permission`: it blocks the KEYBOARD too, so a
              tab-through cannot reach a grant's menu, and it covers every
              affordance in the pane at once — rows, drag-to-move, the
              per-grant menus — which enumerating `disabled` props would keep
              missing as rows gain controls. The server enforces the gate
              regardless; this stops offering an action that would 403.

              `true`, NOT `''`. React 19 treats an empty string on a boolean
              attribute as FALSE and omits the attribute entirely (it warns:
              "Received an empty string for a boolean attribute `inert`"), so
              the cast that used to be here silently disabled the whole
              mechanism — every row, menu and drag stayed live for a read-rung
              caller, who then collected a 403 toast per attempt. `ShareLinksDialog`
              has the correct form for the same purpose. */}
          <div
            inert={accessMapReadOnly ? true : undefined}
            aria-busy={listPending ? true : undefined}
            data-testid="access-pane"
            className={`flex-1 flex flex-col min-h-0 overflow-hidden ${paneChrome}`}
          >
            {/* Permission description — sits at the top of the pane so
                the active tab's meaning reads before the user scans
                the grant list below. When the active tab is above the
                source-share ceiling, replace the per-tier blurb with a
                generic note: granting at that level is allowed but
                members are clamped to the cap. */}
            {(() => {
              const capIdx = shareCap ? TAB_ORDER.indexOf(shareCap) : -1
              const activeIdx = activeTab === 'all' ? -1 : TAB_ORDER.indexOf(activeTab)
              // Every tier above the source ceiling (admin included)
              // gets the generic "fine to grant, but members are
              // clamped" line — the cap applies regardless of tier.
              const activeAboveCap = isReceivedShare && capIdx >= 0 && activeIdx >= 0 && activeIdx < capIdx
              const ActiveIcon = activeTab === 'all' ? null : PERM_ICONS[activeTab]
              const capLabel = shareCap ? labelFor(shareCap, shareCap, isReceivedShare) : ''
              // Manage & Edit above the source cap is a different story
              // from the other tiers: even though their access to the
              // CONTENT is clamped to the cap, the grantee still gets
              // real authority over THIS organization's access list
              // (add/remove members, change tiers within the share).
              // Generic "ineffective above cap" copy understates that.
              const desc = activeAboveCap
                ? activeTab === 'admin'
                  ? `Lets them manage your organization's access to this shared folder — add or remove members, change permissions here. They have ${capLabel} permission as well until the source organization caps the permission.`
                  : `Granting at this level is fine, but the source organization capped this folder at ${capLabel} — members effectively get ${capLabel} regardless of what's set here.`
                : activeTab === 'all'
                  ? ALL_DESCRIPTION
                  : activeTab === 'admin'
                    ? adminDescriptionFor(shareCap, isReceivedShare)
                    : PERM_DESCRIPTIONS[activeTab]
              return (
                /* PHONE: no band, UNLESS it is carrying the cross-tenant cap
                   warning.
                   The band normally restates the active tier's name directly
                   beneath a chip that already says it, and the tier is carried
                   by the list's own sticky section headers — so a third band
                   was one restatement too many and it went.
                   But it is also the ONLY surface for `activeAboveCap`: that
                   the source organization capped this folder, so a grant above
                   the cap will be clamped. The other surface for the same fact
                   — the sidebar's amber warning — is `hidden md:flex`, and
                   `labelFor` renames only the ADMIN tier, so no chip carries
                   it either. Hidden unconditionally, a phone admin of a
                   received `.shared` folder could grant Read & Write with
                   nothing on screen saying it would not take effect.
                   So the band is suppressed for the RESTATEMENT and kept for
                   the WARNING. */
                (!isDesktop && !activeAboveCap) ? null : (
                <div className={`px-4 md:px-5 py-2.5 md:py-3 border-b themed-border-secondary ${
                  isDesktop ? 'themed-bg-primary' : 'bg-amber-500/10'
                }`}>
                  <p className="text-xs themed-text-muted flex items-start gap-2">
                    {/* On a phone this band only ever renders as the WARNING,
                        so it is marked as one — amber, and led by the alert
                        glyph rather than the tier icon. */}
                    {!isDesktop
                      ? <ShieldAlert size={14} className="shrink-0 mt-[1px] text-amber-500" />
                      : ActiveIcon && <ActiveIcon size={14} className="shrink-0 mt-[1px] themed-text" />}
                    <span>
                      <span className="font-medium themed-text">{labelFor(activeTab, shareCap, isReceivedShare)}</span>
                      <span> — {desc}</span>
                    </span>
                  </p>
                </div>
                )
              )
            })()}
            <div className={`flex-1 overflow-y-auto px-4 md:px-5 pt-0 pb-4 md:py-4 flex flex-col transition-opacity ${listPending && !listPending.previewed ? 'opacity-50' : ''}`}>
              {loading ? (
                <div className="py-6 text-center themed-text-muted text-sm">Loading...</div>
              ) : activeTab === 'all' ? (
                <AllPermissionsView
                  tabs={tabs}
                  tags={viewTags}
                  viaDescendant={viewVia}
                  allMembers={allMembers}
                  allTeams={allTeams}
                  shareCap={shareCap}
                  isReceivedShare={isReceivedShare}
                  isFolder={isFolder}
                  draggingTag={draggingTag}
                  savingId={savingId}
                  sharingByTag={sharingByTag}
                  inheritedRaw={inheritedRaw}
                  onTagDragStart={onTagDragStart}
                  onTagDragEnd={onTagDragEnd}
                  onAdd={handleAdd}
                  onRemove={handleRemove}
                  onSetSharing={handleSetSharing}
                  isAppView={isAppView}
                  onAddApp={setAppPickerFor}
                />
              ) : (
                <>
                  {/* User-list drop zone wraps the explicit-pills row and
                      grows (flex-1) to fill the space down to the bottom
                      group, so the whole upper pane becomes a visible drop
                      target mid-drag (mirror of the trash zone, but at the
                      top). Pills sit at the top of the zone; the empty
                      space below them is part of the drop target.
                      The dashed border + tinted bg toggle in lockstep with
                      `draggingTag`; the consistent p-3 -m-3 prevents layout
                      shift when the border appears/disappears. Purple
                      matches the active-tab drop ring on the sidebar. */}
                  <div
                    onDragOver={onUserListDragOver}
                    onDragLeave={onUserListDragLeave}
                    onDrop={onUserListDrop}
                    className={`
                      flex-1 rounded-md p-3 -m-3 border-2 border-dashed
                      ${draggingTag && isDropTabAllowed(activeTab)
                        ? userListHover
                          ? 'border-brand-500 bg-brand-500/9'
                          : 'border-brand-500/40 bg-brand-500/6'
                        : 'border-transparent'}
                    `}
                  >
                    {/* Explicit grants render as a 2-column grid of
                        fully-rounded pill cards (`h-14 rounded-full`).
                        Each card carries an avatar (member initial /
                        type-icon), the primary label, the existing
                        count/email subline, an app-share subline (per
                        v2 miniapp design — member/team/role only), and
                        the remove X. More info (team member preview,
                        full email + role, drag hint) lives in the
                        cursor-following Tooltip so the card stays
                        compact and uniform. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {visibleTags.explicit.map((t) => (
                        <Tag
                          key={tagKey(t)}
                          tag={t}
                          saving={savingId === tagKey(t)}
                          flash={hasLoadedOnce && recentlyAdded.has(tagKey(t))}
                          allMembers={allMembers}
                          allTeams={allTeams}
                          onRemove={handleRemove}
                          onDragStart={onTagDragStart}
                          onDragEnd={onTagDragEnd}
                          isDragging={!!draggingTag}
                          sharingAllowed={sharingByTag.get(tagKey(t)) ?? true}
                          sharingDropdownOpen={sharingDropdownFor === tagKey(t)}
                          // All three principal types are toggleable:
                          // member → memberships.allow_sharing,
                          // team → teams.allow_sharing,
                          // role → tenant_settings.role_allow_sharing.
                          onToggleSharingDropdown={() => setSharingDropdownFor(prev =>
                            prev === tagKey(t) ? null : tagKey(t),
                          )}
                          onSetSharing={(allowed) => handleSetSharing(t, allowed)}
                          // Same 3-dot menu as All Permissions. These pills used
                          // to carry a bare X, so removal was the only thing you
                          // could do from a tier tab — changing a level meant
                          // going back to All, or dragging.
                          renderActions={() => (
                            <CardActionsMenu
                              tag={t}
                              tabs={tabs}
                              shareCap={shareCap}
                              isReceivedShare={isReceivedShare}
                              sharingAllowed={sharingByTag.get(tagKey(t)) ?? true}
                              inheritedPermission={inheritedPermissionFor(t, inheritedRaw)}
                              open={tagMenuFor === tagKey(t)}
                              onOpenChange={(o) => setTagMenuFor(o ? tagKey(t) : null)}
                              onChangePermission={async (p2) => {
                                setTagMenuFor(null)
                                if (!t.inherited && t.permission === p2) return
                                await handleAdd(t.type, t.targetId, t.label, p2)
                              }}
                              onUseInheritance={async () => {
                                setTagMenuFor(null)
                                if (!t.inherited) await handleRemove(t)
                              }}
                              onSetSharing={async (allowed) => { await handleSetSharing(t, allowed) }}
                            />
                          )}
                        />
                      ))}
                      {!(activeTab === 'hidden' && !isFolder) && (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              if (isAppView) { setAppPickerFor(activeTab); return }
                              setPickerAnchor(e.currentTarget)
                              setPickerOpen(o => !o)
                            }}
                            className="w-full h-14 flex items-center justify-center gap-1.5 rounded-full border border-dashed themed-border-secondary themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors text-xs"
                          >
                            <Plus size={14} className="shrink-0" />
                            <span>{isAppView ? 'Add PDApp' : 'Add'}</span>
                          </button>
                          {pickerOpen && !isAppView && (
                            <AddPicker
                              activePermission={activeTab}
                              tierLabel={labelFor(activeTab, shareCap, isReceivedShare)}
                              anchorEl={pickerAnchor}
                              tags={tags}
                              allTeams={allTeams}
                              allMembers={allMembers}
                              onPick={handleAdd}
                              onClose={() => setPickerOpen(false)}
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {visibleTags.explicit.length === 0 && visibleTags.inherited.length === 0 && (
                      <p className="text-sm themed-text-muted mt-3">{emptyHintFor(activeTab, isFolder)}</p>
                    )}
                  </div>

                  {/* Bottom group: trash drop target (mid-drag only) sits
                      above the inherited section, hallway viewers below it.
                      mt-auto on the wrapper pins the whole bottom group to
                      the bottom of the pane; a flex column inside stacks
                      trash → inherited → hallway.

                      Hallway viewers are bucketed by their permission on
                      the descendant — a member with `readwrite` on a child
                      shows under the Read & Write tab, etc. This answers
                      "who has effectively this level somewhere below this
                      folder?" per tab. */}
                  {(() => {
                    const hallwayOnTab = viewVia.filter(v => v.permission === activeTab)
                    return visibleTags.inherited.length > 0 || hallwayOnTab.length > 0
                  })() && (
                    <div className="mt-auto pt-6 flex flex-col gap-3">
                      {/* Inherited — same card chrome as the explicit
                          grid; the section heading carries the "this
                          is inherited" context. Drag-to-tab still
                          works (becomes an explicit grant at the new
                          level). */}
                      {visibleTags.inherited.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider themed-text-muted mb-2">
                            {visibleTags.inherited[0]?.inheritedFromPath
                              ? `Inherited from ${visibleTags.inherited[0].inheritedFromPath}`
                              : 'Inherited from parent'}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 opacity-80">
                            {visibleTags.inherited.map((t) => (
                              <Tag
                                key={tagKey(t)}
                                tag={t}
                                saving={false}
                                flash={false}
                                allMembers={allMembers}
                                allTeams={allTeams}
                                onDragStart={onTagDragStart}
                                onDragEnd={onTagDragEnd}
                                isDragging={!!draggingTag}
                                // Inherited pills get the same sharing-
                                // toggle UX as explicit pills. The
                                // capability is per-principal (member,
                                // team, or role — backed by
                                // memberships / teams /
                                // tenant_settings.role_allow_sharing
                                // respectively), not per-grant. The
                                // "inherited" label applies to the
                                // folder grant, not to this capability.
                                sharingAllowed={sharingByTag.get(tagKey(t)) ?? true}
                                sharingDropdownOpen={sharingDropdownFor === tagKey(t)}
                                onToggleSharingDropdown={() => setSharingDropdownFor(prev =>
                                  prev === tagKey(t) ? null : tagKey(t),
                                )}
                                onSetSharing={(allowed) => handleSetSharing(t, allowed)}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Visible via descendant grant — read-only.
                          Filtered to entries whose descendant permission
                          matches the active tab, so each tab answers "who
                          has this level somewhere below?" Each row names
                          the descendant path via the tooltip. */}
                      {viewVia.some(v => v.permission === activeTab) && (
                        <div className="opacity-50">
                          <p className="text-[10px] uppercase tracking-wider themed-text-muted mb-2">
                            Visible via descendant grant
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {viewVia
                              .filter(v => v.permission === activeTab)
                              .map((v) => {
                                // Reuse the pill shape from Tag, dropping the
                                // draggable + removable affordances since
                                // hallway entries are read-only. CardAvatar
                                // accepts the same {type, targetId} shape.
                                const fakeTag: AccessTag = {
                                  type: v.type,
                                  targetId: v.targetId,
                                  label: v.label,
                                  permission: v.permission,
                                  inherited: false,
                                }
                                // Build the hallway-card subline using the same rules as
                                // explicit/inherited cards (Tag composes its own subline,
                                // but hallway uses inline JSX so we re-derive the same
                                // logic here): "<role> · <email>" for members where both
                                // are known, "<N> members[ · <K> sub-teams]" for teams.
                                const hallwaySubParts: string[] = []
                                if (v.type === 'member') {
                                  const m = allMembers.find(x => x.email === v.targetId)
                                  const name = m?.name?.trim()
                                  if (name && name !== v.targetId) hallwaySubParts.push(v.targetId)
                                } else if (v.type === 'team') {
                                  const team = allTeams.find(t => t.id === v.targetId)
                                  if (team?.memberCount !== undefined) {
                                    hallwaySubParts.push(`${team.memberCount} ${team.memberCount === 1 ? 'member' : 'members'}`)
                                  }
                                  const subs = allTeams.filter(t => t.parentId === v.targetId).length
                                  if (subs > 0) hallwaySubParts.push(`${subs} ${subs === 1 ? 'sub-team' : 'sub-teams'}`)
                                } else if (v.type === 'role') {
                                  // Actual members only. `allMembers` now also
                                  // carries pending invitees (grant targets),
                                  // and counting them here would inflate "N
                                  // emails" with people who hold no membership
                                  // row yet — the role grant reaches them only
                                  // once they accept.
                                  const count = actualMembers(allMembers).filter(m => m.role === v.targetId).length
                                  hallwaySubParts.push(`${count} ${count === 1 ? 'email' : 'emails'}`)
                                }
                                const hallwaySubline = hallwaySubParts.length > 0 ? hallwaySubParts.join(' · ') : null
                                // Mirror Tag's sharing subline so the hallway pill
                                // stays the same shape (avoids a height-mismatch in
                                // the grid when explicit + hallway sit on the same
                                // tab). Hallway pills are read-only — no chevron,
                                // no dropdown.
                                return (
                                  <HallwayCard
                                    key={`${v.type}:${v.targetId}`}
                                    tag={fakeTag}
                                    allMembers={allMembers}
                                    descendantPath={v.descendantPath}
                                    redacted={!!v.redacted}
                                    subline={hallwaySubline}
                                    showSharing
                                  />
                                )
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          </div>
        </div>
        )}

        {appPickerFor && (
          <AppAddPicker
            excludeAppIds={new Set(tags.filter(t => t.type === 'app').map(t => t.targetId))}
            onPick={(appId, name, icon) => { void handleAdd('app', appId, name, appPickerFor, icon); setAppPickerFor(null) }}
            onClose={() => setAppPickerFor(null)}
          />
        )}

        {/* QR modal — centered over the dialog card; opened by a link row's QR icon. */}
        <QrOverlay url={qrModalUrl} onClose={() => setQrModalUrl(null)} />
      </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Modal to pick a `.miniapp` folder to grant access to this file/folder.
 *  A flat, filterable list of the tenant's apps — picking a row IS the grant,
 *  there is no confirm step. (It used to embed the shared drive browser and
 *  enable a confirm once the open folder happened to be an app.) */
function AppAddPicker({ excludeAppIds, onPick, onClose }: {
  excludeAppIds: Set<string>
  onPick: (appId: string, name: string, icon: { spec: MiniappIconSpec | null; rev: string | null }) => void
  onClose: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [filter, setFilter] = useState('')
  /**
   * Entry animation. Double rAF so the closed frame (transparent, slightly
   * small) is PAINTED before the open one, otherwise the browser coalesces
   * both into one paint and the transition never plays — the same trick, and
   * the same reason, as the sheets.
   *
   * Fade + a small scale rather than a slide: this is a centred dialog, and a
   * dialog that travels reads as a sheet that landed in the wrong place.
   */
  const [shown, setShown] = useState(false)
  useEffect(() => {
    let r2 = 0
    const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setShown(true)) })
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2) }
  }, [])

  /**
   * Escape closes THIS picker, not the dialog underneath it.
   *
   * Capture phase + stopPropagation, because WhoHasAccessDialog listens for
   * the same key: without this, one Escape tore down the whole Who-has-access
   * dialog along with the picker, losing the reader's place after they had
   * already been navigating it. Identical to the fix AddPicker carries.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  // The tenant's apps, from the same query the drive's PDApps page reads —
  // reused for the icon metadata it already carries and the cache key it
  // already shares, not for any safety property. WHAT MAKES THIS SAFE IS THE
  // WRITE, not the list: `POST /api/folders/:id/permissions` requires Manage
  // resolved on the folder being granted (`canEditFolderAccess` — never a role;
  // a folder inside a received `.shared` is gated by its mount scope instead),
  // and an APP grant is judged by `judgeAppGrantWriteGated`: a widen needs the
  // person to cover the app's new access, and someone who can EDIT the app (the
  // Modification Safeguard / app reach) before and after the write (D23 (b)) —
  // the same rule the app's own settings apply. Nothing about the picker's
  // contents is load-bearing.
  //
  // DELIBERATELY GLOBAL — this is the one place the list differs from the
  // PDApps page, which filters `hiddenWithoutAdminView` (apps the viewer
  // reaches only via their admin role) unless "Show as admin" is on. That page
  // answers "what am I working on"; this dialog answers "who can reach this
  // folder", which is an admin question, and hiding apps here would leave a
  // folder admin unable to grant one with no explanation as to why it is
  // missing. Do not "align" the two — they are not the same question.
  const projects = useProjects(true)
  const apps = useMemo(() => {
    const rows = (projects.data ?? []).filter(p => isMiniappFolder(p.name))
    const q = filter.trim().toLowerCase()
    return (q ? rows.filter(p => appDisplayName(p.name).toLowerCase().includes(q)) : rows)
      .sort((a, b) => appDisplayName(a.name).localeCompare(appDisplayName(b.name)))
  }, [projects.data, filter])

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ease-out motion-reduce:transition-none ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {/* `w-[520px]` was a DESKTOP width on every screen: at 390px the card was
          130px wider than the phone, so it overflowed the viewport with no
          gutter and the "centred" flex parent had nothing left to centre it
          in. Now it fills the width it is given, minus the p-4 gutter on the
          backdrop, and stops growing at the old 520px on a large screen.
          Height in `svh` for the same reason the sheets use it — `dvh` would
          resize the card as the browser chrome hides. */}
      <div
        className={`${themeClass} relative themed-bg-secondary themed-border border rounded-2xl md:rounded-lg
          w-full max-w-[520px] h-[75svh] md:h-[520px] max-h-[85svh]
          flex flex-col shadow-2xl origin-center
          transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${
          shown ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 h-11 border-b themed-border shrink-0">
          <span className="text-sm font-medium themed-text">Add a PDApp</span>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text"><X size={16} /></button>
        </div>

        <div className="px-4 py-2.5 border-b themed-border shrink-0">
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter PDApps…"
            autoFocus
            className="w-full text-sm themed-input rounded px-2.5 py-1.5 outline-none"
          />
        </div>

        {/* A LIST, not a drive browser. Picking an app meant navigating to a
            `.pdapp` folder and knowing that a folder was what you were looking
            for — the picker asked the reader to hold the storage layout in
            their head to answer a question about apps. */}
        {/* `flex flex-col` so the EmptyState below can actually centre itself:
            its `flex-1` and `justify-center` are inert in a block container,
            which left the icon and copy top-aligned above a tall blank area —
            unlike the Links and Outsiders empty states it is meant to match,
            whose panes are flex columns. */}
        <div className="flex-1 overflow-y-auto p-2 flex flex-col">
          {projects.isLoading ? (
            <div className="p-6 text-center text-sm themed-text-muted">Loading…</div>
          ) : apps.length === 0 ? (
            /* Same treatment as the Links and Outsiders views, so the three
               empty states in this dialog read as one product. The two cases
               need different copy for the same reason as those: a filter that
               matched nothing is a dead end the reader can back out of, and an
               empty drive is a state they have to leave to change. */
            filter.trim()
              ? (
                <EmptyState
                  compact
                  icon={PDAppIcon}
                  title="No match"
                  body={`No PDApp in this drive is called “${filter.trim()}”.`}
                />
              )
              : (
                <EmptyState
                  compact
                  icon={PDAppIcon}
                  title="No PDApps yet"
                  body="PDApps built in this drive can be granted access to a folder. Create one from the PDApps page first."
                />
              )
          ) : apps.map(app => {
            const name = appDisplayName(app.name)
            const already = excludeAppIds.has(app.folderId)
            return (
              <button
                key={app.folderId}
                disabled={already}
                onClick={() => onPick(app.folderId, name, { spec: parseMiniappIcon(app.metadataJson), rev: parseMiniappIconRev(app.metadataJson) })}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${
                  already ? 'opacity-45 cursor-default' : 'hover:themed-bg-tertiary cursor-pointer'
                }`}
              >
                <MiniappIcon
                  spec={parseMiniappIcon(app.metadataJson)}
                  name={name}
                  size={28}
                  refId={app.folderId}
                  rev={parseMiniappIconRev(app.metadataJson)}
                  className="shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm themed-text truncate" title={name}>{name}</span>
                  {/* The path disambiguates two apps sharing a name; it is the
                      caller's own drive, so it discloses nothing they cannot
                      already browse. */}
                  <span className="block text-[11px] themed-text-muted truncate" title={app.path}>{app.path}</span>
                </span>
                {already && <span className="text-[11px] themed-text-muted shrink-0">Already added</span>}
              </button>
            )
          })}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 h-12 border-t themed-border shrink-0">
          <button onClick={onClose} className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors">Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/** Audit + actions view for the synthetic "All Permissions" tab. Renders
 *  a section per permission tier (in TAB_ORDER) with the explicit +
 *  inherited + hallway cards grouped underneath. Each section is a drop
 *  zone — dragging a card from another section drops it at this tier.
 *  Cards are also draggable to the sidebar permission tabs (reuses the
 *  parent dialog's onTagDragStart/onTabDrop wiring). A 3-dot menu on
 *  each card opens inline permission + sharing controls, replacing the
 *  per-tab X button (delete becomes a menu item). */
function AllPermissionsView({
  tabs,
  tags,
  viaDescendant,
  allMembers,
  allTeams,
  shareCap,
  isReceivedShare,
  isFolder,
  draggingTag,
  savingId,
  sharingByTag,
  inheritedRaw,
  onTagDragStart,
  onTagDragEnd,
  onAdd,
  onRemove,
  onSetSharing,
  isAppView,
  onAddApp,
}: {
  tabs: Permission[]
  tags: AccessTag[]
  viaDescendant: Array<{ type: TagType; targetId: string; label: string; permission: Permission; descendantPath: string; redacted?: boolean }>
  allMembers: MemberInfo[]
  allTeams: TeamInfo[]
  shareCap: Permission | null
  isReceivedShare: boolean
  isFolder: boolean
  draggingTag: AccessTag | null
  savingId: string | null
  sharingByTag: Map<string, boolean>
  /** Raw `inherited` map from the permissions API (keyed by role name,
   *  `group:<teamId>`, or `user:<email>`). Lets the menu show the
   *  would-be-inherited tier even when an explicit grant shadows it. */
  inheritedRaw: Record<string, string>
  onTagDragStart: (tag: AccessTag) => (e: React.DragEvent) => void
  onTagDragEnd: () => void
  onAdd: (type: TagType, targetId: string, label: string, permission: Permission) => Promise<void>
  onRemove: (tag: AccessTag) => Promise<void>
  onSetSharing: (tag: AccessTag, allowed: boolean) => Promise<void>
  /** Which principal this view is listing. The single-tier tabs already branch
   *  on it; this view did not know its own tab at all, so its Add opened the
   *  people picker on the PDApp side and a pick landed under People & teams. */
  isAppView: boolean
  /** Opens the app picker at a tier. Separate from `onAdd` because choosing an
   *  app is a different act — browsing to a `.pdapp` folder, not picking from
   *  a member/team/role list. */
  onAddApp: (permission: Permission) => void
}) {
  const [dropTarget, setDropTarget] = useState<Permission | null>(null)
  const [menuFor, setMenuFor] = useState<string | null>(null)
  /** Which section's "+ Add" picker is open (this view can add at any tier,
   *  so the picker is per-section rather than one for the whole pane). */
  const isDesktop = useIsDesktop()
  const [pickerFor, setPickerFor] = useState<Permission | null>(null)
  /** The "+ Add" button the open picker belongs to. Captured from the click
   *  rather than held in a ref: the All-permissions view renders one Add per
   *  section inside a `.map()`, where a per-item ref needs a component split. */
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null)

  const TYPE_ORDER: Record<TagType, number> = { role: 0, team: 1, member: 2, app: 3 }
  const sorter = (a: AccessTag, b: AccessTag) => {
    if (TYPE_ORDER[a.type] !== TYPE_ORDER[b.type]) return TYPE_ORDER[a.type] - TYPE_ORDER[b.type]
    return a.label.localeCompare(b.label)
  }
  const sections = tabs.map(p => {
    const onTab = tags.filter(t => t.permission === p)
    const explicit = onTab.filter(t => !t.inherited).sort(sorter)
    // The server sends the inherited list AFTER the Restricted cut (#787 §2/§3,
    // `inheritedByPrincipal`); shown as-is — no client-side copy of the cut.
    const inherited = onTab.filter(t => t.inherited).sort(sorter)
    const hallway = viaDescendant.filter(v => v.permission === p)
    return { p, explicit, inherited, hallway }
  })
  // Always render all sections (even empty) so they can act as drop
  // zones; empty sections still receive a card dropped onto them.

  // Drop-target eligibility. `admin` (Manage & Edit) is grantable on files now, but
  // "No access" (hidden) stays folder-only — a file inherits it from its parent.
  const isDropAllowedAt = (p: Permission): boolean => !(p === 'hidden' && !isFolder)

  const handleSectionDragOver = (p: Permission) => (e: React.DragEvent) => {
    if (!draggingTag) return
    if (!isDropAllowedAt(p)) {
      e.dataTransfer.dropEffect = 'none'
      return
    }
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dropTarget !== p) setDropTarget(p)
  }
  const handleSectionDragLeave = (p: Permission) => (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null) && dropTarget === p) {
      setDropTarget(null)
    }
  }
  const handleSectionDrop = (p: Permission) => async (e: React.DragEvent) => {
    e.preventDefault()
    setDropTarget(null)
    const dt = draggingTag
    if (!dt) return
    if (!isDropAllowedAt(p)) return
    // Same level + same explicit target = no-op (matches sidebar tab semantics).
    if (!dt.inherited && dt.permission === p) return
    await onAdd(dt.type, dt.targetId, dt.label, p)
  }

  return (
    <div className="flex flex-col gap-6">
      {sections.map(({ p, explicit, inherited, hallway }) => {
        const Icon = PERM_ICONS[p]
        const total = explicit.length + inherited.length + hallway.length
        const dragHere = dropTarget === p && !!draggingTag
        const dropAllowed = isDropAllowedAt(p)
        return (
          <section
            key={p}
            onDragOver={handleSectionDragOver(p)}
            onDragLeave={handleSectionDragLeave(p)}
            onDrop={handleSectionDrop(p)}
            className={`rounded-md p-2 pt-1 pb-3 -m-2 border-2 border-dashed transition-colors ${
              draggingTag
                ? dragHere && dropAllowed
                  ? 'border-brand-500 bg-brand-500/9'
                  : dropAllowed
                    ? 'border-brand-500/30'
                    : 'border-transparent opacity-50'
                : 'border-transparent'
            }`}
          >
            {/* PHONE: the tier header STICKS. It is the only thing naming the
                tier now that the tab strip is gone, so it has to say so where
                it applies rather than once at the top of a list you scroll
                away from. `themed-bg-secondary` matches the dialog, which is
                what shows through on a phone — the pane has no fill of its own
                there (see `paneChrome`). */}
            <div className={`flex items-center gap-1.5 mb-2 ${
              isDesktop ? '' : 'sticky top-0 z-10 themed-bg-secondary -mx-4 px-4 pt-3 pb-2 mb-1'
            }`}>
              <Icon size={12} className="themed-text" />
              <span className="text-[11px] uppercase tracking-wider themed-text font-medium">
                {labelFor(p, shareCap, isReceivedShare)}
              </span>
              <span className="text-[10px] themed-text-muted ml-1">{total}</span>
              <span className="flex-1" />
              {/* Add AT THIS TIER — the All view can grant directly now; it
                  used to be read-only ("switch to a specific tier to add").
                  Suppressed for No-access on a FILE, same rule as the tier
                  tab: hidden is folder-only (it works by overlaying an empty
                  folder — no file equivalent). */}
              {!(p === 'hidden' && !isFolder) && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      if (isAppView) { onAddApp(p); return }
                      setPickerAnchor(e.currentTarget)
                      setPickerFor(cur => (cur === p ? null : p))
                    }}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
                    data-testid={`all-add-${p}`}
                  >
                    <Plus size={12} className="shrink-0" />{isAppView ? 'Add PDApp' : 'Add'}
                  </button>
                  {!isAppView && pickerFor === p && (
                    <AddPicker
                      activePermission={p}
                      tierLabel={labelFor(p, shareCap, isReceivedShare)}
                      anchorEl={pickerAnchor}
                      tags={tags}
                      allTeams={allTeams}
                      allMembers={allMembers}
                      onPick={async (type, targetId, label, permission) => {
                        setPickerFor(null)
                        await onAdd(type, targetId, label, permission)
                      }}
                      onClose={() => setPickerFor(null)}
                    />
                  )}
                </div>
              )}
            </div>
            {(explicit.length > 0 || inherited.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[...explicit, ...inherited].map(t => {
                  const key = tagKey(t)
                  // Per-tag inherited level — drives the "(inherit)" row of the
                  // 3-dot menu, and the drag target's wording. Shared helper so
                  // the two surfaces cannot drift apart.
                  const inheritedPermissionForTag = inheritedPermissionFor(t, inheritedRaw)
                  return (
                    <div key={key} className={t.inherited ? 'opacity-80' : ''}>
                      <Tag
                        tag={t}
                        saving={savingId === key}
                        flash={false}
                        allMembers={allMembers}
                        allTeams={allTeams}
                        onDragStart={onTagDragStart}
                        onDragEnd={onTagDragEnd}
                        isDragging={!!draggingTag}
                        sharingAllowed={sharingByTag.get(key) ?? true}
                        renderActions={() => (
                          <CardActionsMenu
                            tag={t}
                            tabs={tabs}
                            shareCap={shareCap}
                            isReceivedShare={isReceivedShare}
                            sharingAllowed={sharingByTag.get(key) ?? true}
                            inheritedPermission={inheritedPermissionForTag}
                            open={menuFor === key}
                            onOpenChange={(o) => setMenuFor(o ? key : null)}
                            onChangePermission={async (p2) => {
                              setMenuFor(null)
                              if (!t.inherited && t.permission === p2) return
                              await onAdd(t.type, t.targetId, t.label, p2)
                            }}
                            onUseInheritance={async () => {
                              setMenuFor(null)
                              // Already inherited → menu close is enough.
                              // Explicit → revert by removing the explicit row.
                              if (!t.inherited) await onRemove(t)
                            }}
                            onSetSharing={async (allowed) => {
                              await onSetSharing(t, allowed)
                            }}
                          />
                        )}
                      />
                    </div>
                  )
                })}
              </div>
            )}
            {hallway.length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] uppercase tracking-wider themed-text-muted mb-1">
                  Visible via descendant grant
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 opacity-50">
                  {hallway.map(v => {
                    const fakeTag: AccessTag = {
                      type: v.type, targetId: v.targetId, label: v.label,
                      permission: v.permission, inherited: false,
                    }
                    return (
                      <HallwayCard
                        key={`${v.type}:${v.targetId}`}
                        tag={fakeTag}
                        allMembers={allMembers}
                        descendantPath={v.descendantPath}
                        redacted={!!v.redacted}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

/** Per-card right-side actions for the All Permissions audit view.
 *  Replaces the X button: inherited cards show an "INHERITED" pill +
 *  3-dot menu, explicit cards show the 3-dot menu only. Menu surfaces
 *  the permission picker (the currently-effective tier is checked,
 *  suffixed "(inherited)" when the grant is inherited at that level —
 *  clicking it again makes it explicit) and the Allow/No sharing
 *  toggle. There is no Remove — "demote" a grant by picking a lower
 *  tier instead. */
function CardActionsMenu({
  tag,
  tabs,
  shareCap,
  isReceivedShare,
  sharingAllowed,
  inheritedPermission,
  open,
  onOpenChange,
  onChangePermission,
  onUseInheritance,
  onSetSharing,
}: {
  tag: AccessTag
  tabs: Permission[]
  shareCap: Permission | null
  isReceivedShare: boolean
  sharingAllowed: boolean
  /** Tier this principal would inherit if their explicit grant were
   *  removed. Renders as the first menu row labelled "(inherit)". When
   *  null, the row is hidden — the principal has no inheritance path. */
  inheritedPermission: Permission | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onChangePermission: (p: Permission) => Promise<void>
  /** Click handler for the "(inherit)" row. Resolves the grant to the
   *  inherited tier — no-op for already-inherited tags; removes the
   *  explicit row for explicit ones. */
  onUseInheritance: () => Promise<void>
  onSetSharing: (allowed: boolean) => Promise<void>
}) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  /** The popover itself, separate from the wrapper `menuRef` sits on.
   *  Both used to share one ref, and since React attaches child refs before
   *  parent ones the wrapper won — which measures as zero-height, because the
   *  popover inside it is `position: fixed` and contributes no layout. The
   *  click-outside check is happy with either (fixed still means DOM child),
   *  but measuring needs the real box. */
  const popoverRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  // Portaled to document.body, so we re-apply the dialog's theme class
  // (CSS vars for themed-bg-secondary / themed-text / themed-border are
  // scoped to .theme-* — without this the menu would render with
  // unresolved variables, i.e. transparent on top of the page).
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return
      if (menuRef.current?.contains(e.target as Node)) return
      onOpenChange(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onOpenChange])

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const MARGIN = 8   // keep this much clear of the viewport edges
    const GAP = 4      // breathing room between trigger and menu

    // Measure the real menu rather than assuming a size. The portal renders in
    // the same commit as this layout effect, so the node is already there — and
    // a layout effect runs before paint, so the corrected position is the only
    // one drawn. The fallbacks cover a first run where it somehow is not.
    const menuEl = popoverRef.current
    const width = menuEl?.offsetWidth || 168
    const height = menuEl?.offsetHeight || 0

    // Right edge aligns to the trigger, then clamped so a pill near either
    // side of the dialog cannot push the menu off-screen.
    const left = Math.min(
      Math.max(MARGIN, rect.right - width),
      Math.max(MARGIN, window.innerWidth - width - MARGIN),
    )

    // Below by default. A pill low in the list has no room there — the menu
    // used to run off the bottom with its lower half unreachable — so flip it
    // above the trigger, and if it fits neither way, sit it against the bottom
    // margin instead of overflowing.
    let top = rect.bottom + GAP
    if (height > 0 && top + height > window.innerHeight - MARGIN) {
      const above = rect.top - GAP - height
      top = above >= MARGIN ? above : Math.max(MARGIN, window.innerHeight - MARGIN - height)
    }

    setPos({ top, left })
  }, [open])

  // Effective inherited label, e.g. "Manage & Edit" — the inherit row
  // shows this in parens, matching MemberAccessDialog's "Inherit (X)".
  const inheritedLabel = inheritedPermission
    ? labelFor(inheritedPermission, shareCap, isReceivedShare)
    : null

  return (
    <>
      {tag.inherited && (
        <span
          className="text-[9px] uppercase tracking-wider themed-text-muted px-1.5 py-0.5 rounded-full bg-current/10 shrink-0"
          title="Inherited from parent — change the permission to make it explicit here."
        >
          Inherited
        </span>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onOpenChange(!open)
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.preventDefault()}
        className="p-0.5 rounded-full opacity-50 hover:opacity-100 hover:bg-current/10 transition-opacity cursor-pointer shrink-0"
        aria-label={`Options for ${tag.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={14} />
      </button>
      {open && createPortal(
        // Reuses the AccessSettingsPopover from SharePermissionPicker —
        // same surface, same item rows, same Check-icon prefix, same
        // selected-state color. We pass an `inherit` option for the
        // MemberAccessDialog "Inherit (X)" row; no `confirmAction`
        // because changes apply immediately (vs the share dialog's
        // staged-then-Confirm flow).
        <div
          ref={menuRef}
          role="menu"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <AccessSettingsPopover
            themeClass={themeClass}
            pos={pos}
            dropdownRef={popoverRef}
            permissionOptions={tabs}
            permissionLabels={Object.fromEntries(tabs.map(p => [p, labelFor(p, shareCap, isReceivedShare)]))}
            selectedPermission={tag.inherited ? null : tag.permission}
            onSelectPermission={(opt) => onChangePermission(opt as Permission)}
            inherit={inheritedLabel ? {
              label: `Inherit (${inheritedLabel})`,
              selected: tag.inherited,
              onClick: onUseInheritance,
            } : undefined}
            sharingAllowed={sharingAllowed}
            onSetSharing={onSetSharing}
          />
        </div>,
        document.body
      )}
    </>
  )
}

function emptyHintFor(perm: Permission, isFolder: boolean): string {
  if (perm === 'hidden' && !isFolder) {
    return 'Files inherit "No access" from their parent folder. Set on the folder to apply here.'
  }
  return 'No one has this permission level.'
}

function tagKey(t: AccessTag): string {
  return `${t.type}:${t.targetId}:${t.inherited ? 'i' : 'e'}`
}

/** Walk up from `el` to the nearest scrollable ancestor. The sharing
 *  dropdown is `absolute`-positioned inside the dialog's `overflow-y-auto`
 *  content area; the scroll container's bottom edge is the real clip
 *  boundary, so a pill near the bottom (e.g. the "Inherited from parent"
 *  section, pinned with `mt-auto`) needs the dropdown to flip upward
 *  rather than open downward into the clipped region. Returns null when
 *  nothing scrollable is found (falls back to the viewport). */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null
  while (node) {
    const oy = getComputedStyle(node).overflowY
    if (oy === 'auto' || oy === 'scroll') return node
    node = node.parentElement
  }
  return null
}


/** Strip the `role:` / `team:` prefix from the label — the row icon now
 *  carries the type signal, so the prefix would just be visual noise. */
function displayName(tag: AccessTag): string {
  if (tag.type === 'member') return tag.label
  return tag.label.replace(/^(role|team):/, '')
}

// `avatarBg`, `initialFromEmail`, `nameFromEmail` come from
// @papan/share-links/ui — same palette + hash so a recipient's avatar
// is recognizable across both the WhoHasAccess audit view and the
// share-links manager.

/** Two-character initial for a member avatar — "JD" for "John Doe",
 *  "AS" for "alice.smith", "AL" for "alice@…". Prefers the display name;
 *  falls back to the email's local-part so the avatar still reads as a
 *  pair of letters even when no name is on file. */
function memberInitial(targetId: string, allMembers: MemberInfo[]): string {
  const m = allMembers.find(x => x.email === targetId)
  const raw = m?.name?.trim() || targetId.split('@')[0]
  const parts = raw.split(/[\s_.-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
  }
  const single = parts[0] || raw
  return (single.slice(0, 2) || '?').toUpperCase()
}

/**
 * The principal avatar used across the access surfaces — member initials on
 * the hashed palette, amber shield for a role, the app's own icon for an app,
 * blue group for a team. Exported so the share dialogs render the SAME
 * identity chip a principal has here (ShareInternallyDialog, ShareDialog).
 */
export function CardAvatar({ tag, allMembers, size = 40 }: { tag: AccessTag; allMembers: MemberInfo[]; size?: number }) {
  // `size` lets the compact share dialogs reuse the same chip smaller.
  const box = { width: size, height: size }
  const glyph = Math.round(size * 0.35)
  if (tag.type === 'member') {
    return (
      <div style={box} className={`rounded-full ${avatarBg(tag.targetId)} flex items-center justify-center text-white text-xs font-semibold shrink-0 select-none`}>
        {memberInitial(tag.targetId, allMembers)}
      </div>
    )
  }
  if (tag.type === 'role') {
    return (
      <div style={box} className="rounded-full bg-amber-500 flex items-center justify-center shrink-0">
        <Shield size={glyph} className="text-white" />
      </div>
    )
  }
  if (tag.type === 'app') {
    // The app's OWN icon — the one renderer every surface uses (`MiniappIcon`), so the
    // card matches the drive tile and the add-picker row. With no stored spec it draws
    // the same name-derived default they do.
    return (
      <MiniappIcon
        spec={tag.appIcon ?? null}
        name={tag.label}
        size={size}
        refId={tag.targetId}
        rev={tag.appIconRev ?? null}
        className="shrink-0"
      />
    )
  }
  return (
    <div style={box} className="rounded-full bg-blue-500 flex items-center justify-center shrink-0">
      <UsersRound size={glyph} className="text-white" />
    </div>
  )
}

/** Primary card label. Stripped of "role:" / "team:" prefix (the avatar
 *  carries the type signal); falls back to display name for members. */
/**
 * The people who actually hold a membership row.
 *
 * `allMembers` carries pending invitees too (they are valid grant targets), so
 * anything COUNTING or LISTING members must filter them out: a role grant does
 * not reach an invitee until they accept, and showing them inflates every
 * "N emails" figure with people the grant does not yet apply to.
 *
 * One helper rather than three inline predicates — the first version of this
 * fix guarded a single call site and left two others counting invitees.
 */
function actualMembers(allMembers: MemberInfo[]): MemberInfo[] {
  return allMembers.filter(m => m.status !== 'invited')
}

/**
 * Is this tag a user grant whose target has not accepted their org invite?
 *
 * `allMembers` carries pending invitees (they are valid grant targets), so the
 * status travels with the list the row is already resolved against — no extra
 * request, and it cannot disagree with the picker that created the grant.
 */
function isPendingInvitee(tag: AccessTag, allMembers: MemberInfo[]): boolean {
  if (tag.type !== 'member') return false
  return allMembers.find(x => x.email === tag.targetId)?.status === 'invited'
}

function primaryLabel(tag: AccessTag, allMembers: MemberInfo[]): string {
  if (tag.type === 'member') {
    const m = allMembers.find(x => x.email === tag.targetId)
    return m?.name?.trim() || tag.targetId
  }
  if (tag.type === 'app') return tag.label
  return displayName(tag)
}

/** Strip the trailing ` <email>` suffix the server adds to member-preview
 *  entries when a display name is available — leaves just the name (or the
 *  bare email when no name was on file). Keeps the inline list short. */
function compactMemberName(s: string): string {
  return s.replace(/\s*<[^>]+>$/, '')
}

/** Build the per-type tooltip body. Minimal by design:
 *    - role:   no tooltip (the card label is sufficient)
 *    - member: display name (when distinct) + email
 *    - team:   team name + up to 3 member names inline with "+N more"
 *  No drag-instruction text, no sub-team list, no roster expansion.
 *  Server already caps `memberPreview` at 5; we slice further to 3 so the
 *  tooltip stays one tight line of names. */
function tooltipFor(
  tag: AccessTag,
  allMembers: MemberInfo[],
  allTeams: TeamInfo[],
): React.ReactNode {
  // Sharing row, mirrored from the card's third subline so hover
  // exposes the same fact when the card text is truncated. The tooltip
  // doesn't know the live toggle state (it would need plumbing), so it
  // always shows the affirmative form — the live state is visible on
  // the card itself.
  const appShareRow = (
    <div className="opacity-80 text-[10px] flex items-center gap-1 pt-0.5">
      <Share2 size={9} className="shrink-0 opacity-70" />
      <span>Allow sharing</span>
    </div>
  )

  if (tag.type === 'team') {
    const team = allTeams.find(t => t.id === tag.targetId)
    if (!team) return null
    const memberCount = team.memberCount ?? 0
    const subTeamCount = allTeams.filter(t => t.parentId === tag.targetId).length
    const preview = (team.memberPreview ?? []).slice(0, 3).map(compactMemberName)
    const overflow = Math.max(0, memberCount - preview.length)
    // Counts line restates the card's truncatable subtext in full —
    // long names + sub-team labels can ellipsis on a 178px card, so
    // the tooltip is the only place the user can see the complete
    // counts at a glance.
    const countParts: string[] = []
    if (memberCount > 0) {
      countParts.push(`${memberCount} ${memberCount === 1 ? 'member' : 'members'}`)
    }
    if (subTeamCount > 0) {
      countParts.push(`${subTeamCount} ${subTeamCount === 1 ? 'sub-team' : 'sub-teams'}`)
    }
    return (
      <div className="space-y-0.5">
        <div className="font-medium">{team.name}</div>
        {countParts.length > 0 && (
          <div className="opacity-80 text-[10px]">{countParts.join(' · ')}</div>
        )}
        {preview.length > 0 && (
          <div className="opacity-80 text-[10px]">
            {preview.join(', ')}{overflow > 0 ? `, +${overflow} more` : ''}
          </div>
        )}
        {appShareRow}
      </div>
    )
  }
  if (tag.type === 'member') {
    const m = allMembers.find(x => x.email === tag.targetId)
    const name = m?.name?.trim()
    const hasDistinctName = !!name && name !== tag.targetId
    return (
      <div className="space-y-0.5">
        {hasDistinctName && <div className="font-medium">{name}</div>}
        <div className={hasDistinctName ? 'opacity-80 text-[10px]' : 'font-medium'}>
          {tag.targetId}
        </div>
        {appShareRow}
      </div>
    )
  }
  if (tag.type === 'role') {
    // Mirror the team tooltip shape: label + count + preview list.
    // "emails" is the per-card subtext term, kept consistent here.
    const roleName = tag.targetId
    const label = ROLE_LABELS[roleName] ?? roleName
    const membersWithRole = actualMembers(allMembers).filter(m => m.role === roleName)
    const count = membersWithRole.length
    const preview = membersWithRole.slice(0, 3).map(m => m.name?.trim() || m.email)
    const overflow = Math.max(0, count - preview.length)
    return (
      <div className="space-y-0.5">
        <div className="font-medium">{label}</div>
        <div className="opacity-80 text-[10px]">
          {count} {count === 1 ? 'email' : 'emails'}
        </div>
        {preview.length > 0 && (
          <div className="opacity-80 text-[10px]">
            {preview.join(', ')}{overflow > 0 ? `, +${overflow} more` : ''}
          </div>
        )}
        {appShareRow}
      </div>
    )
  }
  if (tag.type === 'app') {
    // The card truncates a long app name and its folder subline; hover shows both
    // in full (the full drive path when known, else the disambiguated label).
    const where = tag.appFullPath ?? tag.appPath
    return (
      <div className="space-y-0.5">
        <div className="font-medium">{tag.label}</div>
        {where && <div className="opacity-80 text-[10px]">{where}</div>}
        {appShareRow}
      </div>
    )
  }
  return null
}

/** Hover content for a "visible via descendant" card: who it is, in full (the
 *  card truncates the name), then where the grant that makes them visible sits. */
function hallwayTooltip(tag: AccessTag, allMembers: MemberInfo[], descendantPath: string): React.ReactNode {
  const m = tag.type === 'member' ? allMembers.find(x => x.email === tag.targetId) : undefined
  const name = m?.name?.trim()
  return (
    <div className="space-y-0.5">
      <div className="font-medium">{tag.redacted ? primaryLabel(tag, allMembers) : (name || primaryLabel(tag, allMembers))}</div>
      {name && name !== tag.targetId && <div className="opacity-80 text-[10px]">{tag.targetId}</div>}
      <div className="opacity-80 text-[10px]">via {descendantPath}</div>
    </div>
  )
}

/** A "visible via descendant" card: read-only; hover (desktop) or tap (phone) shows the full
 *  name and where the grant that makes them visible sits. */
function HallwayCard({ tag, allMembers, descendantPath, redacted, subline = null, showSharing = false }: {
  tag: AccessTag
  allMembers: MemberInfo[]
  descendantPath: string
  redacted: boolean
  subline?: string | null
  showSharing?: boolean
}) {
  const isDesktop = useIsDesktop()
  return (
    <Tooltip
      content={hallwayTooltip(tag, allMembers, descendantPath)}
      always
      followCursor={isDesktop}
      tap={!isDesktop}
      className="block"
    >
      <div
        data-testid="access-card-hallway"
        className={`flex items-center gap-2 h-14 rounded-full px-2 themed-bg-tertiary ${isDesktop ? 'cursor-help' : ''} select-none`}
      >
        <CardAvatar tag={tag} allMembers={allMembers} />
        <div className="flex-1 min-w-0">
          <div className={`text-xs truncate leading-tight ${redacted ? 'themed-text-muted italic' : 'themed-text'}`}>
            {primaryLabel(tag, allMembers)}
          </div>
          {subline && !redacted && (
            <div className="text-[10px] themed-text-muted truncate leading-tight">{subline}</div>
          )}
          {showSharing && !redacted && (
            <div className="text-[10px] themed-text whitespace-nowrap leading-tight flex items-center gap-1">
              <Share2 size={9} className="shrink-0 opacity-70" />
              <span>Allow sharing</span>
            </div>
          )}
        </div>
      </div>
    </Tooltip>
  )
}

function Tag({
  tag,
  saving,
  flash,
  allMembers,
  allTeams,
  onRemove,
  onDragStart,
  onDragEnd,
  sharingAllowed = true,
  sharingDropdownOpen = false,
  onToggleSharingDropdown,
  onSetSharing,
  isDragging = false,
  renderActions,
}: {
  tag: AccessTag
  saving: boolean
  flash: boolean
  allMembers: MemberInfo[]
  allTeams: TeamInfo[]
  onRemove?: (tag: AccessTag) => void
  onDragStart?: (tag: AccessTag) => (e: React.DragEvent) => void
  onDragEnd?: () => void
  /** Current per-pill sharing state. Default true ("Allow sharing"). */
  sharingAllowed?: boolean
  /** True when this pill's sharing-toggle dropdown is the open one. */
  sharingDropdownOpen?: boolean
  /** Open/close the dropdown for THIS pill. Undefined → subline is
   *  read-only (used for inherited tags where toggling is ambiguous). */
  onToggleSharingDropdown?: () => void
  /** Set the new sharing state and close the dropdown. */
  onSetSharing?: (allowed: boolean) => void
  /** True while any pill in the dialog is being dragged. Suppresses
   *  tooltips so they don't follow the cursor through the drop zones. */
  isDragging?: boolean
  /** Replaces the default X-button affordance on the right of the card.
   *  Used by the All Permissions audit view to surface a 3-dot menu
   *  (with inline permission/sharing/remove controls) + an inherited
   *  badge in place of the X. */
  renderActions?: () => React.ReactNode
}) {
  const isDesktop = useIsDesktop()
  const flashClass = flash ? 'ring-2 ring-brand-400 transition-shadow duration-700 animate-tag-pop' : ''
  const removable = !tag.inherited && onRemove
  const team = tag.type === 'team' ? allTeams.find(t => t.id === tag.targetId) : null
  const teamMemberCount = team?.memberCount
  const teamSubCount = tag.type === 'team'
    ? allTeams.filter(t => t.parentId === tag.targetId).length
    : 0
  /** Subline composer per type:
   *  - member: just the email (when a distinct display name is on the
   *    primary line; otherwise the primary IS the email, no subline)
   *  - team:   "<N> members[ · <K> sub-teams]"
   *  - role:   "<N> emails" — count of org members with that role.
   *    Cheap: allMembers is already loaded, this is an O(n) filter on
   *    an in-memory array, no extra fetch.
   */
  const sublineParts: string[] = []
  if (tag.type === 'member') {
    const m = allMembers.find(x => x.email === tag.targetId)
    const name = m?.name?.trim()
    if (name && name !== tag.targetId) sublineParts.push(tag.targetId)
  } else if (tag.type === 'team') {
    if (teamMemberCount !== undefined) {
      sublineParts.push(`${teamMemberCount} ${teamMemberCount === 1 ? 'member' : 'members'}`)
    }
    if (teamSubCount > 0) {
      sublineParts.push(`${teamSubCount} ${teamSubCount === 1 ? 'sub-team' : 'sub-teams'}`)
    }
  } else if (tag.type === 'role') {
    const count = actualMembers(allMembers).filter(m => m.role === tag.targetId).length
    sublineParts.push(`${count} ${count === 1 ? 'email' : 'emails'}`)
  } else if (tag.type === 'app') {
    if (tag.appPath) sublineParts.push(tag.appPath)
  }
  const subline = sublineParts.length > 0 ? sublineParts.join(' · ') : null

  /**
   * Sharing subline (h-14 card, third row). Surfaces the principal's
   * sharing state per the v2 miniapp design (MINI_APP_SECURITY.md).
   * Scaffold-only local state for now; wires to a real
   * `allow_sharing` flag when the backend ships.
   */
  const sharingLabel = sharingAllowed ? 'Allow sharing' : 'No sharing'
  // The row is interactive only when the parent passes both handlers.
  // Inherited tags get a read-only label (no chevron, no click).
  const sharingInteractive = !!onToggleSharingDropdown && !!onSetSharing

  // Flip the dropdown upward when the trigger sits too close to the
  // bottom of the dialog's scroll area — otherwise a downward-opening
  // menu is clipped (the "Inherited from parent" section is pinned to
  // the bottom, so its pills hit this every time). Measured in a
  // layout effect (pre-paint) so the menu never flickers down-then-up.
  const sharingTriggerRef = useRef<HTMLButtonElement>(null)
  const [sharingDropUp, setSharingDropUp] = useState(false)
  useLayoutEffect(() => {
    if (!sharingDropdownOpen || !sharingTriggerRef.current) return
    const triggerRect = sharingTriggerRef.current.getBoundingClientRect()
    const scrollParent = findScrollParent(sharingTriggerRef.current)
    const boundaryBottom = scrollParent
      ? scrollParent.getBoundingClientRect().bottom
      : window.innerHeight
    // Menu is ~44px tall (2 rows of py-0.5 text-[10px] + py-0.5 wrapper);
    // 56px leaves a small margin below it.
    const CLEARANCE = 56
    setSharingDropUp(boundaryBottom - triggerRect.bottom < CLEARANCE)
  }, [sharingDropdownOpen])

  return (
    <Tooltip
      // Suppress the tooltip when:
      //  - The sharing dropdown is open — the user is interacting with
      //    the menu, not learning about the card, and a cursor-following
      //    tooltip overlapping the menu reads as visual noise.
      //  - Any pill in the dialog is being dragged — followCursor would
      //    have the tooltip race across the dialog with the drag image,
      //    fighting the sidebar-tab drop ring and the trash zone for
      //    attention.
      // On a phone (no hover) a tap on the card shows it and the next tap
      // anywhere hides it; taps on the card's own controls stay theirs.
      content={(sharingDropdownOpen || isDragging) ? null : tooltipFor(tag, allMembers, allTeams)}
      always
      followCursor={isDesktop}
      tap={!isDesktop}
      className="block"
    >
    <div
      ref={(el) => { if (el && flash) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) }}
      draggable={isDesktop && !!onDragStart}
      onDragStart={onDragStart ? onDragStart(tag) : undefined}
      onDragEnd={onDragEnd}
      data-testid={`access-card-${tag.type}`}
      data-target-id={tag.targetId}
      // Fully-rounded pill card. `relative` anchors the absolutely-
      // positioned drag handle. Fixed h-14 fits a 3-line card: primary
      // label (text-xs) + existing subline (text-[10px]) + app-share
      // subline (text-[10px]), all leading-tight.
      className={`group relative flex items-center gap-2 h-14 rounded-full pl-2.5 pr-2 themed-bg-tertiary ${isDesktop ? 'hover:themed-bg-secondary' : ''} transition-colors ${flashClass} ${saving ? 'opacity-50' : ''} ${isDesktop ? 'cursor-grab active:cursor-grabbing' : ''} select-none`}
    >
      {/* Drag handle floats over the left padding so it doesn't eat any
          flex-row width. The whole card is draggable anyway — the icon
          is a low-prominence affordance hint. */}
      {/* Drag handle — DESKTOP ONLY. HTML5 drag-and-drop does not fire on touch
          at all, so on a phone this advertised an interaction that could never
          happen. The `···` menu already carries the permission picker, which is
          the same operation by another route, so nothing is lost by removing
          the affordance where it cannot work. */}
      {isDesktop && (
        <GripVertical size={8} className="absolute left-0.5 top-1/2 -translate-y-1/2 themed-text-muted opacity-30 group-hover:opacity-70 transition-opacity pointer-events-none" />
      )}
      <CardAvatar tag={tag} allMembers={allMembers} />
      {/* Text column owns the sharing dropdown's positioning context
          so `top-full` on the menu sits flush under the third row,
          not 10+px below the card (card-level `relative` would anchor
          to the card bottom, which is ~10px below the subline due to
          the card's vertical centering). */}
      <div className="flex-1 min-w-0 relative">
        {/* `truncate` moved to the label SPAN: this element is now a flex row,
            and ellipsis does not apply to a flex container's children. The span
            also needs `min-w-0` — a flex item defaults to min-width:auto and
            would push a long email past the card edge on a narrow screen rather
            than truncating.
            The text STYLING travels with the text for the same reason. Leaving
            `themed-text-muted italic` on the row alone still rendered correctly
            (both inherit), but it moved them off the element that CONTAINS the
            label — and "the redacted label is muted and italic" is asserted
            against that element in `team-picker-and-redacted.spec.ts`. Styling a
            wrapper while the assertion reads the child is how a refactor breaks
            a guard without changing a pixel. */}
        <div className={`text-xs leading-tight flex items-center gap-1.5 ${tag.redacted ? '' : 'themed-text'}`}>
          <span className={`truncate min-w-0 ${tag.redacted ? 'themed-text-muted italic' : ''}`}>{primaryLabel(tag, allMembers)}</span>
          {/* A grant staged on someone who has NOT accepted their invite yet.
              Without this the row is a bare email — indistinguishable from a
              member who simply has no display name — so an admin auditing this
              list cannot tell "hasn't joined yet" from "joined, no name set".
              The picker already labels them; this is the same fact on the row
              the picker produces. */}
          {isPendingInvitee(tag, allMembers) && (
            <span className="shrink-0 text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300">Invited</span>
          )}
        </div>
        {subline && !tag.redacted && (
          <div className="text-[10px] themed-text-muted truncate leading-tight">
            {subline}
          </div>
        )}
        {!tag.redacted && (
          // Sharing row. Share2 icon distinguishes this from the
          // count/email subline above so the two read as separate facts
          // at a glance, not a wrapped continuation. Interactive when
          // the parent wired handlers (explicit grants); read-only label
          // otherwise (inherited tags, which can't toggle a state owned
          // at the ancestor level without first becoming explicit).
          sharingInteractive ? (
            <button
              ref={sharingTriggerRef}
              type="button"
              onClick={(e) => {
                // Stop drag affordance from interpreting the click as a
                // pickup; also stop propagation up the card so this
                // doesn't trip the user-list drop zone parent.
                e.stopPropagation()
                onToggleSharingDropdown!()
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
              aria-haspopup="menu"
              aria-expanded={sharingDropdownOpen}
              // whitespace-nowrap (not truncate): the label is short +
              // fixed, no truncation is ever needed, and `truncate` adds
              // overflow:hidden which would clip the descender of 'g'
              // under leading-tight at 10px.
              className="text-[10px] themed-text whitespace-nowrap leading-tight flex items-center gap-1 cursor-pointer w-fit"
            >
              {sharingAllowed
                ? <Share2 size={9} className="shrink-0 opacity-70" />
                : <Ban size={9} className="shrink-0 opacity-70" />}
              <span>{sharingLabel}</span>
              <ChevronDown
                size={10}
                className={`shrink-0 opacity-60 transition-transform ${sharingDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>
          ) : (
            <div className="text-[10px] themed-text whitespace-nowrap leading-tight flex items-center gap-1">
              {sharingAllowed
                ? <Share2 size={9} className="shrink-0 opacity-70" />
                : <Ban size={9} className="shrink-0 opacity-70" />}
              <span>{sharingLabel}</span>
            </div>
          )
        )}
        {sharingInteractive && sharingDropdownOpen && (
          // Anchored to the text column. Opens downward (top-full) by
          // default; flips upward (bottom-full) when the trigger is too
          // close to the scroll area's bottom edge — see sharingDropUp.
          // No left offset needed — the column's left edge already
          // aligns with the subline text.
          <div
            role="menu"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className={`absolute left-0 z-30 w-28 rounded-md shadow-xl border themed-border themed-bg-secondary py-0.5 ${
              sharingDropUp ? 'bottom-full mb-0.5' : 'top-full mt-0.5'
            }`}
          >
            <button
              role="menuitemradio"
              aria-checked={sharingAllowed}
              onClick={() => onSetSharing!(true)}
              className="w-full px-2 py-0.5 text-[10px] text-left flex items-center gap-1 themed-text hover:themed-bg-tertiary cursor-pointer"
            >
              <Check size={9} className={`shrink-0 ${sharingAllowed ? 'opacity-100' : 'opacity-0'}`} />
              <span>Allow sharing</span>
            </button>
            <button
              role="menuitemradio"
              aria-checked={!sharingAllowed}
              onClick={() => onSetSharing!(false)}
              className="w-full px-2 py-0.5 text-[10px] text-left flex items-center gap-1 themed-text hover:themed-bg-tertiary cursor-pointer"
            >
              <Check size={9} className={`shrink-0 ${!sharingAllowed ? 'opacity-100' : 'opacity-0'}`} />
              <span>No sharing</span>
            </button>
          </div>
        )}
      </div>
      {renderActions ? renderActions() : (removable && (
        <button
          onClick={() => onRemove(tag)}
          disabled={saving}
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => e.preventDefault()}
          className="p-0.5 rounded-full opacity-50 hover:opacity-100 hover:bg-current/10 transition-opacity disabled:opacity-30 cursor-pointer shrink-0"
          aria-label={`Remove ${tag.label}`}
        >
          <X size={12} />
        </button>
      ))}
    </div>
    </Tooltip>
  )
}

// ─── Phase 23: Share-link principals pane ───────────────────────────────
//
// Decision 9: each active share-link is a first-class principal whose
// grant is resolved by `resolveSharePrincipalPermission` (App dot-prefix
// rule + view cap baked in). This pane lists every link whose grant
// covers the current file — direct AND cascaded from an ancestor folder
// (Q4: down only, never up). Read-only for now; revoke / edit happen in
// the dedicated ShareLinksDialog.

// Decision 10 / Phase 37: Share Links tab gets a dual-pivot redesign.
//   All Links — card per share-link with recipient list inside
//   All Recipients — card per recipient (email or "Anyone with the link")
//                    with link breakdowns inside
// Both views render from a single /api/share-links/principals fetch.

interface ShareLinkPrincipalRow extends ShareLinkWithUrl {
  /** Resolver's effective max permission for the "anyone with the link"
   *  case. Used for the badge in the (now-unused) compact card. */
  permission: string
  /** Which ancestor the link is rooted at — drives the "inherited" hint
   *  in the audit view when grantRootId !== fileId. */
  grantRootId: string
  /** Name of the grant-root folder, for the "Inherited from folder X"
   *  label. Null when the root row is gone (race) — label falls back. */
  grantRootName?: string | null
  /** `false`: an inherited link this folder is cut from (its creator's sharing setting, a
   *  Restricted folder, grants frozen at mint) — visitors see the folder empty. Absent = not
   *  judged (the folder's own link, an app link) or reached. */
  reachesHere?: false
}

type ShareLinksSubTab = 'links' | 'recipients'

/** A row in the All Recipients view — one recipient (email or wildcard)
 *  with their per-link grants aggregated. */
interface RecipientAggregate {
  /** Either an email or '*'. */
  key: string
  contributions: Array<{
    linkId: string
    token: string
    /** Token with the tenant slug prefix stripped (App-kind only). */
    displayToken: string
    kind: 'app' | 'quick_view'
    /** 'block' entries are kept so the Blocked sidebar filter has a
     *  recipient list — block is a meaningful per-recipient state on a
     *  link that otherwise grants public/wildcard access. */
    permission: 'view' | 'edit' | 'block'
    /** From the link's per-recipient grant. Drives the View (Live Edit)
     *  vs View (Last Save) filter buckets. App contributions are never live
     *  (#1080 — an app link shows the saved files); the filter buckets them
     *  under "App" separately. */
    liveMode: boolean
    /** True when the source link has the '*' recipient (Public). Drives
     *  the purple lock suffix on the inline badge. */
    hasWildcard: boolean
    expiresAt: string | null
  }>
}

/** Aggregate principal rows into per-recipient cards. `recipientGrants`
 *  is the 0083 `{ permission, liveMode }` map. Block grants are kept
 *  alongside view/edit ones so the Blocked filter has a recipient list
 *  to show — a block on a Public link is a real, visible state. */
function aggregateByRecipient(rows: ShareLinkPrincipalRow[]): RecipientAggregate[] {
  const byKey = new Map<string, RecipientAggregate>()
  for (const row of rows) {
    const hasWildcard = '*' in row.recipientGrants
    for (const [key, grant] of Object.entries(row.recipientGrants)) {
      let entry = byKey.get(key)
      if (!entry) {
        entry = { key, contributions: [] }
        byKey.set(key, entry)
      }
      entry.contributions.push({
        linkId: row.id,
        token: row.token,
        displayToken: row.displayToken ?? row.token,
        kind: row.kind as 'app' | 'quick_view',
        permission: grant.permission,
        liveMode: grant.liveMode,
        hasWildcard,
        expiresAt: row.expiresAt,
      })
    }
  }
  return Array.from(byKey.values())
}

/** Distinct badges (Edit / View / App) for the multi-badge card top. */
function distinctBadges(contribs: RecipientAggregate['contributions']): Array<'Edit' | 'View' | 'App' | 'Blocked'> {
  const out = new Set<'Edit' | 'View' | 'App' | 'Blocked'>()
  for (const c of contribs) {
    // Block takes precedence over kind — a blocked App grant is still
    // a block. Otherwise the BadgePill for a recipient with only block
    // grants would falsely advertise "View" or "App".
    if (c.permission === 'block') out.add('Blocked')
    else if (c.kind === 'app') out.add('App')
    else if (c.permission === 'edit') out.add('Edit')
    else out.add('View')
  }
  // Stable order: Edit → View → App → Blocked (least-to-most-negative).
  return (['Edit', 'View', 'App', 'Blocked'] as const).filter(b => out.has(b))
}

/**
 * Fetch share-link principals covering `fileId`. `enabled=false` skips
 * the network round trip so the dialog only loads it when the user
 * actually switches to the Share-links view. The hook is shared by the
 * dialog body (counts on the sidebar) and the right pane (renders the
 * list) so both work off the same data without two fetches.
 */
/**
 * Parent-level sidebar pill in the Share-links view. Mirrors the
 * People-mode permission tabs (icon + label + count badge, full-width
 * rounded-full pill).
 */
function SidebarTab({
  Icon,
  label,
  count,
  active,
  onClick,
  liveDot = false,
  tooltip,
}: {
  /** Omit to render the icon-less "synthetic / show-everything" variant,
   *  matching the People-view "All permissions" tab. */
  Icon?: GlyphIcon
  label: string
  count: number
  active: boolean
  onClick: () => void
  /** When true, overlays the small emerald "live" dot on the top-right
   *  corner of the icon — mirrors the PermissionPicker's view-live glyph. */
  liveDot?: boolean
  /** Hover-only tooltip explaining what this filter shows. Wraps the
   *  button in a portal'd Tooltip; pass `null`/omit to disable. */
  tooltip?: ReactNode
}) {
  const button = (
    <button
      onClick={onClick}
      className={`${Icon ? 'w-auto md:w-[calc(100%-0.5rem)] md:ml-2' : 'w-auto md:w-full'} shrink-0 md:shrink text-left px-3 py-2 text-sm rounded-full transition-colors flex items-center gap-2 justify-between ${
        active
          ? 'themed-bg-primary themed-text font-medium'
          : 'themed-text-muted hover:themed-bg-primary'
      }`}
    >
      <span className="flex items-center gap-2 min-w-0">
        {Icon && (
          liveDot ? (
            <span className="relative inline-flex items-center justify-center shrink-0" style={{ width: 14, height: 14 }}>
              <Icon size={14} />
              <span
                className="absolute -top-0.5 -right-0.5 rounded-full bg-emerald-400"
                style={{ width: 5, height: 5 }}
                aria-hidden="true"
              />
            </span>
          ) : (
            <Icon size={14} className="shrink-0" />
          )
        )}
        <span className="truncate">{label}</span>
      </span>
      {count > 0 && (
        <span className={`text-[11px] font-medium tabular-nums min-w-[20px] inline-flex items-center justify-center px-1.5 h-5 rounded-full shrink-0 themed-bg-tertiary ${active ? 'themed-text' : 'themed-text-muted'}`}>
          {count}
        </span>
      )}
    </button>
  )
  if (!tooltip) return button
  return (
    <Tooltip content={tooltip} always followCursor className="block">
      {button}
    </Tooltip>
  )
}

function useShareLinkPrincipals(fileId: string, enabled: boolean) {
  const [rows, setRows] = useState<ShareLinkPrincipalRow[]>([])
  const [inboundReshares, setInboundReshares] = useState<InboundReshareLink[]>([])
  // Links that reach this folder THROUGH A SHORTCUT (owner ruling: listed on this tab).
  const [viaShortcut, setViaShortcut] = useState<ViaShortcutLink[]>([])
  const [viaShortcutIncomplete, setViaShortcutIncomplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    setError(null)
    request<{ principals: ShareLinkPrincipalRow[]; inboundReshares?: InboundReshareLink[]; viaShortcut?: ViaShortcutLink[]; viaShortcutIncomplete?: boolean }>(
      `/api/share-links/principals?fileId=${encodeURIComponent(fileId)}`,
    )
      .then((res) => {
        if (cancelled) return
        setRows(res.principals)
        setInboundReshares(res.inboundReshares ?? [])
        setViaShortcut(res.viaShortcut ?? [])
        setViaShortcutIncomplete(res.viaShortcutIncomplete === true)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const text = errorText(e, 'Failed to load share-link principals')   // #1316: a dropped answer shows nothing
        if (text !== null) setError(text)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fileId, enabled])

  // Local mutators so the dialog can update the audit cache after the
  // user edits or revokes a row from inside the LinkRow / EditPanel,
  // without a second principals fetch. The principals query is
  // cascade-aware (inheritance) but a single row update preserves the
  // existing `permission` + `grantRootId`, which don't change on
  // settings / recipient edits.
  const updateRow = useCallback((id: string, patch: Partial<ShareLinkPrincipalRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }, [])
  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id))
  }, [])
  const removeInboundReshare = useCallback((id: string) => {
    setInboundReshares((prev) => prev.filter((r) => r.id !== id))
  }, [])
  const removeViaShortcut = useCallback((id: string) => {
    setViaShortcut((prev) => prev.filter((r) => r.id !== id))
  }, [])
  // An external reshare was edited — merge the patched link fields, keeping the
  // `reshareFrom` attribution.
  const patchInboundReshare = useCallback((updated: ShareLinkWithUrl) => {
    setInboundReshares((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))
  }, [])

  return { rows, inboundReshares, viaShortcut, viaShortcutIncomplete, loading, error, updateRow, removeRow, removeInboundReshare, removeViaShortcut, patchInboundReshare }
}

function ShareLinkPrincipalsPane({
  fileId,
  subTab,
  filtered,
  rows,
  aggregates,
  inboundReshares,
  viaShortcut,
  viaShortcutIncomplete,
  onRevokeVia,
  loading,
  error,
  isFolder,
  editingId,
  busyId,
  flashId,
  onStartEdit,
  onCancelEdit,
  onCopy,
  onShowQr,
  onRevoke,
  onRevokeInbound,
  onInboundPatched,
  onLinkPatched,
  onFocusLink,
}: {
  fileId: string
  subTab: ShareLinksSubTab
  /** Whether an audience/category filter is NARROWING what was passed in.
   *  Drives the empty-state copy, which otherwise blames a filter the reader
   *  may not have — on a phone the filters are pinned to 'all' and there is no
   *  control on screen to explain. */
  filtered: boolean
  /** Already filtered by `linksFilter` upstream — the pane just renders. */
  rows: ShareLinkPrincipalRow[]
  /** Already filtered by `recipientsFilter` upstream. */
  aggregates: RecipientAggregate[]
  /** EXTERNAL reshare links on this content (outsiders re-shared it by link).
   *  Owner-only; shown above the owner's own links in the All Links view. */
  inboundReshares: InboundReshareLink[]
  /** Links that reach this folder THROUGH A SHORTCUT (no audience — shown unfiltered only). */
  viaShortcut: ViaShortcutLink[]
  /** The server's bounded search couldn't check every such link. */
  viaShortcutIncomplete: boolean
  onRevokeVia: (l: ViaShortcutLink) => void
  loading: boolean
  error: string | null
  isFolder: boolean
  editingId: string | null
  busyId: string | null
  /** Row id currently being scroll-to + flash-highlighted. Cleared by
   *  the parent after the animation. */
  flashId: string | null
  onStartEdit: (id: string) => void
  onCancelEdit: () => void
  onCopy: (url: string) => void
  /** Open the shared QR modal (centered over the dialog) for this link's URL. */
  onShowQr: (url: string) => void
  onRevoke: (row: ShareLinkPrincipalRow) => void
  onRevokeInbound: (r: InboundReshareLink) => void
  onInboundPatched: (updated: ShareLinkWithUrl) => void
  onLinkPatched: (updated: ShareLinkWithUrl) => void
  /** Used by All Recipients badges to send the user back to All Links
   *  with the targeted row flashed. */
  onFocusLink: (linkId: string) => void
}) {
  if (loading) return <div className="text-sm themed-text-muted">Loading…</div>
  if (error) return <div className="text-sm text-red-400">{error}</div>
  if (subTab === 'links') {
    if (rows.length === 0 && inboundReshares.length === 0 && viaShortcut.length === 0 && !viaShortcutIncomplete) {
      return filtered
        ? (
          <EmptyState
            icon={Share2}
            title="Nothing in this audience"
            body={`No share link for this ${isFolder ? 'folder' : 'file'} is set to this audience. Choose another to see the rest.`}
          />
        )
        : (
          <EmptyState
            icon={Share2}
            title="No share links yet"
            body={`Links you create for this ${isFolder ? 'folder' : 'file'} appear here, along with who they were sent to.`}
          />
        )
    }
    return (
      <div className="space-y-2">
        {rows.length > 0 && (
          <AllLinksView
            rows={rows}
            fileId={fileId}
            isFolder={isFolder}
            editingId={editingId}
            busyId={busyId}
            flashId={flashId}
            onStartEdit={onStartEdit}
            onCancelEdit={onCancelEdit}
            onCopy={onCopy}
            onShowQr={onShowQr}
            onRevoke={onRevoke}
            onLinkPatched={onLinkPatched}
          />
        )}
        {/* External reshares render as ordinary cards in the same list, marked
            only by the outsider avatar glyph. */}
        <InboundResharesSection
          reshares={inboundReshares}
          isFolder={isFolder}
          editingId={editingId}
          busyId={busyId}
          onStartEdit={onStartEdit}
          onCancelEdit={onCancelEdit}
          onCopy={onCopy}
          onShowQr={onShowQr}
          onRevoke={onRevokeInbound}
          onLinkPatched={onInboundPatched}
        />
        <ViaShortcutSection links={viaShortcut} incomplete={viaShortcutIncomplete} busyId={busyId} onRevoke={onRevokeVia} />
      </div>
    )
  }
  if (aggregates.length === 0) {
    return filtered
      ? (
        <EmptyState
          icon={Mail}
          title="Nobody in this group"
          body={`No recipient of this ${isFolder ? 'folder' : 'file'} falls into this group. Choose another to see the rest.`}
        />
      )
      : (
        <EmptyState
          icon={Mail}
          title="No recipients yet"
          body={`Anyone who opens a share link for this ${isFolder ? 'folder' : 'file'} is listed here, with what each link gave them.`}
        />
      )
  }
  return <AllRecipientsView aggregates={aggregates} onSelectLink={onFocusLink} />
}

/** All Links sub-tab: reuses the ShareLinksDialog `LinkRow` in
 *  read-only mode so the audit view matches the share-management UI
 *  visually. The inherited-from-ancestor hint is tagged as a small chip
 *  above each affected row since LinkRow itself doesn't surface
 *  cascade scope. */
function AllLinksView({
  rows,
  fileId,
  isFolder,
  editingId,
  busyId,
  flashId,
  onStartEdit,
  onCancelEdit,
  onCopy,
  onShowQr,
  onRevoke,
  onLinkPatched,
}: {
  rows: ShareLinkPrincipalRow[]
  fileId: string
  isFolder: boolean
  editingId: string | null
  busyId: string | null
  flashId: string | null
  onStartEdit: (id: string) => void
  onCancelEdit: () => void
  onCopy: (url: string) => void
  onShowQr: (url: string) => void
  onRevoke: (row: ShareLinkPrincipalRow) => void
  onLinkPatched: (updated: ShareLinkWithUrl) => void
}) {
  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const inherited = row.grantRootId !== fileId
        const linkRow = (
            <LinkRow
              link={row}
              isFolder={isFolder}
              canGrantEdit
              isEditing={editingId === row.id}
              isBusy={busyId === row.id}
              isHighlighted={flashId === row.id}
              onStartEdit={() => onStartEdit(row.id)}
              onCancelEdit={onCancelEdit}
              onClose={onCancelEdit}
              onCopy={() => onCopy(row.url)}
              onShowQr={onShowQr}
              onRevoke={() => onRevoke(row)}
              onLinkPatched={onLinkPatched}
            />
        )
        if (!inherited) return <div key={row.id}>{linkRow}</div>
        // Inherited from an ancestor folder: label naming the source folder,
        // with a rounded L tree-connector dropping from it into the card.
        return (
          <div key={row.id}>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide themed-text-muted pl-1 min-w-0">
              <span className="shrink-0">Inherited from</span>
              <Folder size={11} className="shrink-0" />
              <span className="truncate normal-case text-[11px] font-medium">
                {row.grantRootName ?? 'ancestor folder'}
              </span>
              {row.reachesHere === false && (
                <span
                  className="shrink-0 normal-case text-[11px] text-amber-500"
                  title="Visitors of this link see this folder empty — it's cut from the link by its creator's sharing settings, a Restricted folder, or the access the link was made with."
                >
                  · doesn't reach here
                </span>
              )}
            </div>
            <div className="flex items-stretch mt-0.5">
              <div className="relative w-4 shrink-0" aria-hidden>
                <div className="absolute left-[7px] right-0 -top-0.5 bottom-1/2 border-l-2 border-b-2 rounded-bl-lg themed-border" />
              </div>
              <div className="flex-1 min-w-0">{linkRow}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** All Recipients sub-tab: card per recipient with the links granting them access. */
function AllRecipientsView({
  aggregates,
  onSelectLink,
}: {
  aggregates: RecipientAggregate[]
  onSelectLink: (linkId: string) => void
}) {
  const emails = aggregates.filter(a => a.key !== '*').sort((a, b) => a.key.localeCompare(b.key))
  const wildcard = aggregates.find(a => a.key === '*')

  return (
    <div className="space-y-4">
      {emails.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider themed-text-muted font-semibold">Emails</div>
          {emails.map(agg => <RecipientCard key={agg.key} agg={agg} onSelectLink={onSelectLink} />)}
        </div>
      )}
      {wildcard && (
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider themed-text-muted font-semibold">Anyone with the link</div>
          <RecipientCard agg={wildcard} onSelectLink={onSelectLink} />
        </div>
      )}
    </div>
  )
}

function RecipientCard({
  agg,
  onSelectLink,
}: {
  agg: RecipientAggregate
  onSelectLink: (linkId: string) => void
}) {
  const isWildcard = agg.key === '*'
  const badges = distinctBadges(agg.contributions)
  // Anyone (wildcard) uses the Globe sidebar icon. Email recipients
  // get the colored-initial avatar + name + email stack used in the
  // share-links recipient list, so the audit view's "person card"
  // matches what users see when managing the link directly.
  // Card chrome mirrors LinkRow: same themed-bg-tertiary surface +
  // oversized rotated watermark icon (Globe for the wildcard, AtSign
  // for email recipients) faded to ~6% opacity so the row reads as the
  // same kind of object.
  const WatermarkIcon = isWildcard ? Globe : Mail
  return (
    <div className="border themed-border rounded-md themed-bg-tertiary relative overflow-hidden">
      <WatermarkIcon
        size={100}
        aria-hidden="true"
        className="absolute top-8 -right-4 themed-text opacity-[0.06] pointer-events-none rotate-[12deg] select-none"
      />
      <div className="relative flex items-center gap-3 px-3 py-2 text-sm themed-text">
        {isWildcard ? (
          <>
            <div className="w-9 h-9 rounded-full themed-bg-secondary flex items-center justify-center shrink-0">
              <Globe size={16} className="themed-text-muted" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="themed-text font-medium truncate">Anyone with the link</div>
              <div className="themed-text-muted text-[11px] truncate">No identity check — anyone with the URL can open it.</div>
            </div>
          </>
        ) : (
          <>
            <div className={`w-9 h-9 rounded-full ${avatarBg(agg.key)} flex items-center justify-center text-white text-[11px] font-semibold shrink-0 select-none`}>
              {initialFromEmail(agg.key)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="themed-text font-medium truncate" title={nameFromEmail(agg.key)}>{nameFromEmail(agg.key)}</div>
              <div className="themed-text-muted text-[11px] truncate" title={agg.key}>{agg.key}</div>
            </div>
          </>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {badges.map(b => <BadgePill key={b} kind={b} />)}
        </div>
      </div>
      <div className="px-3 py-2">
        <InlineLinkBadges contributions={agg.contributions} onSelect={onSelectLink} />
      </div>
    </div>
  )
}

/**
 * Inline strip of LinkRow-style chip badges with overflow handling.
 * One row by default; once chips overflow the container, the last
 * visible slot is replaced by a "…N more" button. Clicking the button
 * expands the strip into a wrapped grid; clicking a chip fires
 * `onSelect` (which the parent uses to switch back to All Links and
 * flash the targeted row).
 *
 * Implementation: a hidden phantom row at the same width measures each
 * chip's offsetWidth. We add them up (with gap + reserved space for the
 * "more" button) and stop when the running width would overflow.
 */
function InlineLinkBadges({
  contributions,
  onSelect,
}: {
  contributions: RecipientAggregate['contributions']
  onSelect: (linkId: string) => void
}) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const phantomRef = useRef<HTMLDivElement>(null)
  const moreRef = useRef<HTMLSpanElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [visibleCount, setVisibleCount] = useState(contributions.length)

  useLayoutEffect(() => {
    if (expanded) return
    const wrapper = wrapperRef.current
    const phantom = phantomRef.current
    if (!wrapper || !phantom) return
    const measure = () => {
      const cw = wrapper.clientWidth
      const chips = Array.from(phantom.querySelectorAll<HTMLElement>('[data-link-chip]'))
      const moreW = moreRef.current?.offsetWidth ?? 0
      const gap = 4
      let used = 0
      let fit = 0
      for (let i = 0; i < chips.length; i++) {
        const w = chips[i].offsetWidth + (i > 0 ? gap : 0)
        const willHaveMore = i + 1 < chips.length
        const reserve = willHaveMore ? moreW + gap : 0
        if (used + w + reserve > cw) break
        used += w
        fit++
      }
      setVisibleCount(fit > 0 ? fit : (chips.length > 0 ? 1 : 0))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(wrapper)
    return () => ro.disconnect()
  }, [contributions, expanded])

  const visible = expanded ? contributions : contributions.slice(0, visibleCount)
  const hidden = contributions.length - visible.length

  return (
    <div className="relative">
      {/* Phantom row used only for width measurement. Off the right
          edge so it never affects scroll/layout; chips wrapped in
          shrink-0 spans so they report their NATURAL width (without
          shrink-0 they'd compress like the visible row and report the
          same widths, defeating the measurement). */}
      <div
        ref={phantomRef}
        aria-hidden
        className="absolute left-full top-0 invisible inline-flex gap-1 pointer-events-none whitespace-nowrap"
      >
        {contributions.map(c => (
          <span key={`phantom-${c.linkId}`} className="shrink-0">
            <LinkBadgeChip contribution={c} />
          </span>
        ))}
        <span ref={moreRef} className="text-[11px] themed-text-muted px-2 py-0.5 inline-flex items-center shrink-0">
          …{contributions.length} more
        </span>
      </div>
      <div
        ref={wrapperRef}
        className={`flex gap-1 ${expanded ? 'flex-wrap' : 'flex-nowrap overflow-hidden'}`}
      >
        {visible.map(c => (
          <button
            key={c.linkId}
            type="button"
            onClick={() => onSelect(c.linkId)}
            className="inline-flex shrink-0 rounded cursor-pointer transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60"
            title={c.token}
          >
            <LinkBadgeChip contribution={c} />
          </button>
        ))}
        {!expanded && hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-[11px] themed-text-muted hover:themed-text hover:themed-bg-tertiary px-2 py-0.5 inline-flex items-center shrink-0 rounded cursor-pointer transition-colors"
          >
            …{hidden} more
          </button>
        )}
        {expanded && contributions.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[11px] themed-text-muted hover:themed-text hover:themed-bg-tertiary px-2 py-0.5 inline-flex items-center shrink-0 rounded cursor-pointer transition-colors"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Compact mirror of the LinkRow header chip: prefix label (App/View/Edit) +
 * token segment + purple lock when restricted. Pure presentation — the
 * parent button handles the click.
 */
function LinkBadgeChip({ contribution: c }: { contribution: RecipientAggregate['contributions'][number] }) {
  // Mirror LinkRow's chip split: Block = Ban (precedence — a blocked
  // recipient is denied regardless of kind), App = the PDApp mark, Edit = SquarePen,
  // View(Live) = Eye + green dot, View(Saved) = Eye. Keeps the
  // recipient-card mini-chips visually aligned with the full-row chip.
  let label: React.ReactNode
  let cls: string
  if (c.permission === 'block') {
    label = (<><Ban size={9} /><span>Blocked</span></>)
    cls = 'bg-red-500/30 text-red-200'
  } else if (c.kind === 'app') {
    label = (<><PDAppIcon size={9} /><span>App</span></>)
    cls = 'bg-amber-500/30 text-amber-200'
  } else if (c.permission === 'edit') {
    label = (<><SquarePen size={9} /><span>Edit</span></>)
    cls = 'bg-emerald-500/30 text-emerald-200'
  } else if (c.liveMode) {
    label = (
      <>
        <span className="relative inline-flex items-center justify-center" style={{ width: 9, height: 9 }}>
          <Eye size={9} />
          <span
            className="absolute -top-0.5 -right-0.5 rounded-full bg-emerald-400"
            style={{ width: 4, height: 4 }}
            aria-hidden="true"
          />
        </span>
        <span>View</span>
      </>
    )
    cls = 'bg-blue-500/30 text-blue-200'
  } else {
    label = (<><Eye size={9} /><span>View</span></>)
    cls = 'bg-blue-500/30 text-blue-200'
  }
  return (
    <code
      data-link-chip
      className="text-[11px] leading-none font-mono themed-text rounded inline-flex items-center overflow-hidden h-5"
    >
      <span className={`inline-flex items-center gap-1 h-full px-1.5 font-sans font-medium ${cls}`}>
        {label}
      </span>
      <span className="px-1.5 h-full themed-bg-secondary inline-flex items-center gap-1">
        <span>{c.displayToken}</span>
      </span>
      {/* SEMANTIC — DO NOT BRAND. The badge below is the "Restricted" status,
          not the product accent; it must keep reading as a status regardless of
          what a brand's primary colour is. */}
      {!c.hasWildcard && (
        <span className="inline-flex items-center h-full px-1 bg-purple-500/30 text-purple-200">
          <Lock size={9} aria-label="Restricted" />
        </span>
      )}
    </code>
  )
}

function BadgePill({ kind }: { kind: 'Edit' | 'View' | 'App' | 'Blocked' }) {
  const cls = kind === 'Edit'
    ? 'bg-emerald-500/20 text-emerald-200'
    : kind === 'App'
      ? 'bg-amber-500/20 text-amber-200'
      : kind === 'Blocked'
        ? 'bg-red-500/20 text-red-200'
        : 'bg-blue-500/20 text-blue-200'
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cls}`}>
      {kind}
    </span>
  )
}

